import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};
import { format, addDays } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { bookAppointment, bookWitthNewPatient, rescheduleAppointment, cancelAppointment, updateAppointment, saveAppointmentUpdate, getDoctorAvailableSlots, sendRescheduleRecommendation, getAllAppointments } from '../../redux/appointmentSlice';
import { getSingleCustomPrice, clearSelectedCustomPrice } from '../../redux/customPriceSlice';
import { getPatientById, getAllPatientsList, lookupPatientByEmail, getPatienttsNameList, lookupPatientHousehold, addHouseholdMember } from '../../redux/patientSlice';
import PaymentMethodSelect from '../payment/PaymentMethodSelect';
import CompletedPaymentCard from '../payment/CompletedPaymentCard';
import { checkEmailExists, checkMobileExists, sendOtp, verifyOtp } from '../../redux/authSlice';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { lookupMocLink, classifyMocLookup } from '../../redux/mocLinkSlice';
import MocLinkModal from '../moc/MocLinkModal';
import HouseholdPicker from '../household/HouseholdPicker';
import { patientPolicyFor, isConnectedTenant, policyErrorMessage, policyErrorField, isAnyOneContact, missingAnyContact, otpVerificationPending } from '../../utils/loginPolicy';

import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { X, ArrowLeft, ArrowRight, CalendarCheck, CalendarX, Video, User, Mail, Users, Calendar, Check, ShieldCheck } from 'lucide-react';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';
import GenderSelect from '../common/GenderSelect';
import CountryTimeZoneSelect from '../common/CountryTimeZoneSelect';
import { docPatCreationCountryList } from '../../redux/masterSlice';
import { isFeatureEnabled, FEATURE_KEYS } from '../../redux/featureSlice';
import { hasControl, patientLimitReached, patientLimitMessage, SECURED_VIDEO_MSG } from '../../redux/subscriptionSlice';
import { getSocket } from '../../socket';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { getDoctorPastBoundary } from '../../utils/pastBoundary';
import usePermissions from '../../hooks/usePermissions';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import { currencySymbol, formatPrice } from '../../utils/currency';
import { hospitalPatientCode } from '../../utils/patientCode';
import SearchableSelect from '../common/SearchableSelect';
import SpecialtyIcon from '../common/SpecialtyIcon';
import { useMasterLocaleTree, resolveMasterLabel } from '../../hooks/useMasterLabel';
import { formatTime as formatClockTime } from '../../utils/clock';
import ProgrammeBookingQuestion, { programmeChoiceProblem, programmeChoiceBody } from '../programmes/ProgrammeBookingQuestion';

// CONNECTED tenants (owner, 2 Oct 2026): the desk owes no code — the patient
// verifies at their first sign-in — and "Send OTP" stays only as an OPTION for
// a patient who is with you now. Drawn as a quiet outline chip labelled
// "Verify (optional)" so it no longer reads as a demand (owner, 20 Oct 2026).
// An isolated hospital whose policy does demand the code keeps the solid button.
const OPTIONAL_OTP_CLASS = 'ml-2 shrink-0 text-xs px-2 py-1 rounded-lg border border-[#4ABDBD]/60 bg-white text-[#2D8A8A] font-medium hover:bg-[#4ABDBD]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

const AppointmentModal = ({
  isModalOpen,
  setIsModalOpen,
  selectedSlot,
  setSelectedSlot,
  selectedAppointment,
  setSelectedAppointment,
  isRescheduling,
  setIsRescheduling,
  selectedDoctor,
  isModalOpenAppointment,
  setisModalOpenAppointment,
  setAppoId,
  dateRangeStart,
  dateRangeEnd,
  initialSpeciality,
  initialAppointmentType,
  initialAddressId,
  onAppointmentBooked,
  onReschedulingSlotsRefetch
}) => {
  const { t, i18n } = useTranslation('appointmentmodal');
  const dispatch = useDispatch();

  // Live "meeting completed" flag: selectedAppointment is a PROP here, so a
  // redux refetch wouldn't update it — flip a local flag from the socket event
  // instead, and the Connect Meet button hides instantly.
  const [meetingDoneLive, setMeetingDoneLive] = useState(false);
  // P39: set when the server answers the create-room request with 409 — the
  // visit can never have a room (not a video visit, or no longer live) — so the
  // button stops inviting a press into the same refusal.
  const [meetingRoomRefused, setMeetingRoomRefused] = useState(false);
  useEffect(() => { setMeetingDoneLive(false); setMeetingRoomRefused(false); }, [selectedAppointment?.appointmentId]);
  useEffect(() => {
    const id = selectedAppointment?.appointmentId;
    if (!id) return undefined;
    let alive = true;
    let retryTimer = null;
    let attached = null;
    const onCompleted = (p) => { if (p && p.appointmentId === id) setMeetingDoneLive(true); };
    const attach = () => {
      if (!alive) return;
      const socket = getSocket && getSocket();
      if (!socket) { retryTimer = setTimeout(attach, 1500); return; }
      attached = socket;
      socket.on('meeting:completed', onCompleted);
    };
    attach();
    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (attached) attached.off('meeting:completed', onCompleted);
    };
  }, [selectedAppointment?.appointmentId]);
  const { patientNameList: rawPatientNameList, allPatientsList, isLoading: patientsLoading, selectedPatient } = useSelector((state) => state.patient);
  const patientNameList = Array.isArray(rawPatientNameList) ? rawPatientNameList : [];

  const { isLoading: rawAppointmentLoading, isBookNewPatientLoading, isSaveAppointmentLoading, controlDuration, earlyOpenRestriction, availableDoctorSlots } = useSelector((state) => state.appointment);
  const appointmentLoading = rawAppointmentLoading || isSaveAppointmentLoading;
  const { selectedCustomPrice } = useSelector((state) => state.customPrice);
  const { emailCheckLoading, mobileCheckLoading, sendOtpLoading, verifyOtpLoading } = useSelector((state) => state.auth);
  const { countryTimezoneList } = useSelector((state) => state.master);
  // Admin Settings → Doctor → All Patient Mode controls whether the
  // "All Patients" radio is offered alongside "My Patients" and "New Patient".
  const allPatientModeEnabled = useSelector((state) => isFeatureEnabled(state, FEATURE_KEYS.doctorAllPatientMode));
  const userRole = (localStorage.getItem('Role') || '').toLowerCase();
  const patientLoading = patientsLoading; // Keep for backward compatibility
  const axiosInstance = useAxiosAuth();

  // What this person may do to the visit (P23), mirroring the backend's gates in
  // api/routes/appointmentRoutes.js. A staff login (Role "user") needs the
  // per-action permission: cancel = appointments.cancel, moving it =
  // appointments.reschedule, booking = appointments.create; the /update/:id
  // writes (No Show, Edit) and minting a Meet room accept ANY of the three.
  // Doctors and admins hold no sub-role and pass, as they do on the server.
  // Cosmetic only — the API refuses regardless.
  const { can } = usePermissions();
  const isStaffLogin = userRole === 'user';
  const canCreateVisit = !isStaffLogin || can('appointments.create');
  const canRescheduleVisit = !isStaffLogin || can('appointments.reschedule');
  const canCancelVisit = !isStaffLogin || can('appointments.cancel');
  const canWriteVisit = canCreateVisit || canRescheduleVisit || canCancelVisit;
  // P39: exactly the meeting-links route's gate — doctor, admin, or staff holding
  // an appointment write permission (appointmentWriteRoles + appointmentWritePermission).
  const canCreateMeetingRoom = userRole === 'doctor' || userRole === 'admin' || (isStaffLogin && canWriteVisit);

  // P26: the server's refusal of a save (a slot taken in the meantime —
  // SLOT_TAKEN, DOCTOR_BUSY …) as a sentence inside the modal, next to the form
  // that was refused. Always a string: an error OBJECT rendered as a child is
  // what used to take a booking screen down.
  const [saveRefusal, setSaveRefusal] = useState('');
  useEffect(() => { setSaveRefusal(''); }, [selectedSlot?.start, selectedAppointment?.appointmentId, isModalOpen]);

  // A visio appointment can exist with no meeting room: if Mitra One Care Meet
  // was unreachable at booking time the server stored no links and told the
  // caller `meetingLinkPending`, which nothing used to read — so the booking
  // looked normal and on the day there was simply no button and no reason.
  // The desk can now mint the room after the fact.
  const [meetingLinkRetrying, setMeetingLinkRetrying] = useState(false);
  const retryMeetingLinks = useCallback(async () => {
    const appointmentId = selectedAppointment?.appointmentId;
    if (!appointmentId || meetingLinkRetrying) return;
    setMeetingLinkRetrying(true);
    try {
      const { data } = await axiosInstance.post(`/appointment/${appointmentId}/meeting-links`);
      if (data?.doctorMeetingLink) {
        // Patch in place: the caller reloads the list on close, and re-fetching
        // the whole appointment here would discard whatever is half-typed.
        setSelectedAppointment((prev) => (prev ? { ...prev, ...data } : prev));
        toast.success(t('appointmentmodal.meeting_link_ready', 'Video room is ready.'));
      } else {
        toast.info(t('appointmentmodal.meeting_link_already', 'This appointment already has a video room.'));
      }
    } catch (err) {
      // 503 means Meet is still down and retrying later is the right advice;
      // 409 means the appointment can never have a room (not video, or no
      // longer live), so stop offering it.
      const code = err?.response?.data?.code;
      if (err?.response?.status === 409) setMeetingRoomRefused(true);
      toast.warn(code === 'MEET_UNAVAILABLE'
        ? t('appointmentmodal.meeting_link_unavailable', 'The video service is not responding. Try again in a moment.')
        : extractErrorMessage(err?.response?.data, t('appointmentmodal.meeting_link_failed', 'Could not create the video room.'), i18n.language));
    } finally {
      setMeetingLinkRetrying(false);
    }
  }, [axiosInstance, selectedAppointment?.appointmentId, meetingLinkRetrying, setSelectedAppointment, t, i18n.language]);

  // Subscription gates for the SELECTED doctor (planControls + active patient
  // count come from getDoctorById). Fail-open when not loaded so we never wrongly
  // block: undefined planControls => allow; empty/real controls => enforce.
  const docControls = selectedDoctor?.planControls;
  const docPatientCount = selectedDoctor?.activePatientCount ?? 0;
  const patientLimitHit = patientLimitReached(docControls, docPatientCount);
  const patientLimitTip = patientLimitMessage(docControls);
  // Owner (16 Sep 2026): the HOSPITAL's video_consultation switch gates the
  // toggle too — the server refuses with FEATURE_DISABLED otherwise, and used
  // to say so only at the final button (booking-rules audit §4.2).
  const { has: tenantHasFeature } = useTenantFeatures();
  const videoAllowed = (docControls == null ? true : hasControl(docControls, 'secured_video'))
    && tenantHasFeature('video_consultation');

  const [isPatientOpen, setIsPatientOpen] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientSearchError, setPatientSearchError] = useState('');
  const [othersSearch, setOthersSearch] = useState('');
  const debouncedOthersSearch = useDebounce(othersSearch, 400);
  const [newPatientErrors, setNewPatientErrors] = useState({
    name: '',
    email: '',
    gender: '',
    dob: ''
  });
  const [charLimitReached, setCharLimitReached] = useState({});
  const [emailStatus, setEmailStatus] = useState(null); // null: not checked, true: available, false: exists
  const [emailExistsRole, setEmailExistsRole] = useState(null); // role of the existing account when emailStatus === false (e.g. 'doctor', 'admin')
  const emailCheckTimeoutRef = useRef(null);
  const lastCheckedEmailRef = useRef('');
  const isEmailCheckInProgressRef = useRef(false);
  const [phoneStatus, setPhoneStatus] = useState(null); // null: not checked, true: available, false: exists
  const phoneCheckTimeoutRef = useRef(null);
  const lastCheckedPhoneRef = useRef('');
  const isPhoneCheckInProgressRef = useRef(false);
  // Currency tokens for the debounced checks: each run bails out after every
  // await when a newer run has started or the field moved on, so a slow,
  // superseded answer can never set the status, open the MOC popup or lock
  // foundPatientId for a value that is no longer in the field.
  const emailRunRef = useRef(0);
  const phoneRunRef = useRef(0);
  const [isPatientFound, setIsPatientFound] = useState(false);
  const [foundPatientId, setFoundPatientId] = useState(null);
  // MOC LINKING (New patient mode): the duplicate check is tenant-aware. An
  // email/phone can already belong to a record HERE, to a Mitra One Care
  // account elsewhere (link — never a second account), or be free.
  // A record that already exists here is NOT a question for the desk: it is
  // applied silently (prefill + lock + book against that patientId) and only
  // confirmed with a green line — `inTenantApplied` says which identifier
  // matched. `emailBlock`/`phoneBlock` say WHY a field is blocked and
  // `mocPopup` is the popup that resolves those (components/moc/MocLinkModal.jsx).
  const tenantId = localStorage.getItem('TenantId') || '';
  // THIS HOSPITAL'S PATIENT RULE (/tenants/public → patientPolicy): which
  // identifiers are mandatory in the New-patient form and which get an OTP
  // step. Only case C (a brand-new person, no match anywhere) is
  // policy-checked — an existing or MOC-linked patient (cases A/B) skips it
  // entirely. CONNECTED (owner, 2 Oct 2026): at least one contact, no code
  // owed by the desk — the patient verifies at their first sign-in; the
  // optional Send OTP stays. ISOLATED: the hospital's login policy, unchanged.
  const [publicTenants, setPublicTenants] = useState([]);
  useEffect(() => {
    if (!isModalOpen) return;
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance.get('/tenants/public');
        if (alive) setPublicTenants(res.data?.tenants || []);
      } catch {
        if (alive) setPublicTenants([]);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);
  // Until the list answers, the connection mode useTenantFeatures cached for
  // this tenant says which world the form is in.
  const cachedConnected = (() => {
    try { return localStorage.getItem('tenantConnectionMode') === 'connected'; } catch { return false; }
  })();
  const connected = isConnectedTenant(publicTenants, tenantId, cachedConnected);
  const loginPolicy = patientPolicyFor(publicTenants, tenantId, cachedConnected);
  const [emailBlock, setEmailBlock] = useState(null); // 'doctor'|'account'|'moc'|'pending'
  const [phoneBlock, setPhoneBlock] = useState(null);
  const [inTenantApplied, setInTenantApplied] = useState(null); // 'email'|'phone' — an existing record was used automatically
  // …and whether that record is ALREADY THIS DOCTOR'S PATIENT (lookup's
  // inTenant.linkedToDoctor). A record existing at the hospital is not the same
  // as it being yours: a doctor typing the email of someone a colleague treats
  // used to be told "already registered with you" and then found nobody in
  // My Patients. true → say it is theirs; false/null → say a record exists here.
  const [inTenantOwned, setInTenantOwned] = useState(null);
  const [mocPopup, setMocPopup] = useState(null); // { source: 'email'|'phone', lookup }
  // ONE CONTACT, SEVERAL PATIENTS (owner, 17 Sep 2026). Anitha's e-mail is
  // also her three children's login — they are secondary rows on her
  // household. Case A above used to reuse THE one row the lookup handed back,
  // so a visit for the son was silently booked for the mother. When the
  // contact turns out to be a household, the form asks instead:
  //   `household`     — { contact, primary, members, canAddMember, source }
  //   `householdPick` — the row the desk chose; booking then goes down the
  //                     ORDINARY existing-patient path (patientId, not bookNew).
  // No household (404 NO_MATCH, or a single row with nothing to add) → the
  // old silent hand-off, unchanged.
  const [household, setHousehold] = useState(null);
  const [householdPick, setHouseholdPick] = useState(null);
  const [householdAdding, setHouseholdAdding] = useState(false);
  const [householdAddError, setHouseholdAddError] = useState('');
  // The green line under a matched identifier. Says "yours" ONLY when the
  // server confirmed the pairing; otherwise it states the fact it actually
  // knows — this hospital already has a patient number for them — and what
  // booking will do about it.
  const inTenantMessage = () => (inTenantOwned
    ? t('appointmentmodal.already_your_patient', 'This is already your patient — using their record for this booking.')
    : t('appointmentmodal.already_registered_here', 'This person already has a patient number here — using it. Booking adds them to your patients.'));
  const [identifierBusy, setIdentifierBusy] = useState({ email: false, phone: false });
  const [recheckTick, setRecheckTick] = useState(0);
  const lastLookupRef = useRef({ email: null, phone: null });
  const suppressPopupRef = useRef(false);
  const emailBusy = emailCheckLoading || identifierBusy.email;
  const phoneBusy = mobileCheckLoading || identifierBusy.phone;
  const [isOpenAppointmentLoading, setIsOpenAppointmentLoading] = useState(false);

  // --- Email OTP state (new patient only) ---
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpVerified, setEmailOtpVerified] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState('');
  const [emailOtpTimer, setEmailOtpTimer] = useState(0);
  const emailOtpTimerRef = useRef(null);
  const emailOtpVerifyingRef = useRef(false);

  // --- Phone OTP state (new patient only) ---
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
  const [phoneOtpError, setPhoneOtpError] = useState('');
  const [phoneOtpTimer, setPhoneOtpTimer] = useState(0);
  const phoneOtpTimerRef = useRef(null);
  const phoneOtpVerifyingRef = useRef(false);

  // OTP ownership proofs (verify-otp -> proof.token). The token — not a
  // client-side boolean — is the only thing the booking endpoint trusts.
  const [emailProof, setEmailProof] = useState(null);
  const [phoneProof, setPhoneProof] = useState(null);

  // View mode state — when clicking a booked appointment, start in view mode
  const [isViewMode, setIsViewMode] = useState(false);

  // Email regex for validation
  const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    // Use UTC getters for birthDate to avoid timezone shift on DOB
    let age = today.getFullYear() - birthDate.getUTCFullYear();
    const monthDiff = today.getMonth() - birthDate.getUTCMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
      age--;
    }
    return age;
  };

  // Validate new patient fields
  const validateNewPatientFields = () => {
    const errors = {
      name: '',
      surname: '',
      email: '',
      gender: '',
      dob: '',
      phone: ''
    };

    // Name validation
    const trimmedName = newPatientData.name.trim();
    if (!trimmedName) {
      errors.name = t('appointmentmodal.name_required');
    } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmedName)) {
      errors.name = t('appointmentmodal.name_invalid_chars');
    } else if (trimmedName.length < 2) {
      errors.name = t('appointmentmodal.name_min_length');
    } else if (trimmedName.length > 20) {
      errors.name = t('appointmentmodal.name_max_length');
    } else if (/^\s+$/.test(newPatientData.name)) {
      errors.name = t('appointmentmodal.name_only_spaces');
    }

    // Surname validation
    const trimmedSurname = newPatientData.surname?.trim();
    if (!trimmedSurname) {
      errors.surname = t('appointmentmodal.surname_required');
    } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmedSurname)) {
      errors.surname = t('appointmentmodal.surname_invalid_chars');
    } else if (trimmedSurname.length < 2) {
      errors.surname = t('appointmentmodal.surname_min_length');
    } else if (trimmedSurname.length > 20) {
      errors.surname = t('appointmentmodal.surname_max_length');
    }

    // Email validation — mandatory per this hospital's login policy.
    const trimmedEmail = newPatientData.email.trim();
    if (!trimmedEmail) {
      if (loginPolicy.email.required) errors.email = t('appointmentmodal.email_required');
    } else {
      // Check total length (RFC 5321: max 254 characters)
      if (trimmedEmail.length > 254) {
        errors.email = t('appointmentmodal.email_too_long');
      } else {
        // Split email into local and domain parts
        const parts = trimmedEmail.split('@');
        if (parts.length !== 2) {
          errors.email = t('appointmentmodal.invalid_email_format');
        } else {
          const [localPart, domainPart] = parts;
          // Local part (before @) should be max 64 characters (RFC 5321)
          if (localPart.length > 64) {
            errors.email = t('appointmentmodal.email_local_too_long');
          } else if (localPart.length === 0) {
            errors.email = t('appointmentmodal.invalid_email_format');
          } else if (domainPart.length === 0 || !domainPart.includes('.')) {
            errors.email = t('appointmentmodal.invalid_email_format');
          } else if (domainPart.length > 253) {
            errors.email = t('appointmentmodal.email_domain_too_long');
          } else if (!emailRegex.test(trimmedEmail)) {
            errors.email = t('appointmentmodal.invalid_email_format');
          } else if (emailStatus === false) {
            errors.email = blockMessage('email');
          } else if (emailStatus === null && !emailBusy) {
            errors.email = t('appointmentmodal.verify_email_address');
          }
          // A missing OTP no longer blocks: handleSaveAppointment asks
          // "book as pending?" instead (the account starts pending).
        }
      }
    }

    // Gender validation
    if (!newPatientData.gender) {
      errors.gender = t('appointmentmodal.gender_required');
    } else {
      const validGenders = ['male', 'female', 'Not_Communicated', 'other'];
      if (!validGenders.includes(newPatientData.gender)) {
        errors.gender = t('appointmentmodal.select_valid_gender');
      }
    }

    // DOB validation
    if (!newPatientData.dob) {
      errors.dob = t('appointmentmodal.dob_required');
    } else {
      const dobDate = new Date(newPatientData.dob);
      const today = new Date();

      if (isNaN(dobDate.getTime())) {
        errors.dob = t('appointmentmodal.invalid_date_format');
      } else if (dobDate > today) {
        errors.dob = t('appointmentmodal.dob_future');
      } else if (dobDate.getFullYear() < 1900) {
        errors.dob = t('appointmentmodal.dob_after_1900');
      } else {
        const age = calculateAge(newPatientData.dob);
        if (age < 0) {
          errors.dob = t('appointmentmodal.invalid_dob');
        } else if (age > 150) {
          errors.dob = t('appointmentmodal.enter_valid_dob');
        }
      }
    }

    // Phone validation — mandatory per this hospital's login policy.
    const trimmedPhone = newPatientData.phone.trim();
    if (!trimmedPhone) {
      if (loginPolicy.mobile.required) errors.phone = t('appointmentmodal.mobile_required');
    } else if (!/^\d+$/.test(trimmedPhone)) {
      errors.phone = t('appointmentmodal.mobile_digits_only');
    } else if (trimmedPhone.length < (selectedCountry?.mobileMinLength || 9)) {
      const minLen = selectedCountry?.mobileMinLength || 9;
      errors.phone = t('appointmentmodal.mobile_min_digits', { count: minLen, defaultValue: `Mobile number must be at least ${minLen} digits` });
    } else if (trimmedPhone.length > (selectedCountry?.mobileMaxLength || 10)) {
      const maxLen = selectedCountry?.mobileMaxLength || 10;
      errors.phone = t('appointmentmodal.mobile_max_digits', { count: maxLen, defaultValue: `Mobile number must be at most ${maxLen} digits` });
    } else if (!isPatientFound) {
      // Only check existence for truly new patients (not pre-filled from email lookup)
      if (phoneStatus === false) {
        errors.phone = blockMessage('phone');
      } else if (phoneStatus === null && !phoneBusy) {
        errors.phone = t('appointmentmodal.verify_mobile_number');
      }
      // A missing OTP no longer blocks: see handleSaveAppointment.
    }

    // 'EITHER' MODE — ANY ONE CONTACT, VERIFIED (owner, 17 Sep 2026).
    // Neither field is starred, so neither per-field rule above can fire on a
    // blank; the hospital still needs ONE way to reach the patient, and that
    // is the only refusal the mode makes on presence. Shown on the email field
    // because it is the first of the two.
    if (missingAnyContact(loginPolicy, { hasEmail: !!trimmedEmail, hasPhone: !!trimmedPhone })) {
      errors.email = t('appointmentmodal.one_contact_required', 'An e-mail or a mobile number is required');
    }

    setNewPatientErrors(errors);
    return Object.values(errors).every(error => !error);
  };
  const [priceError, setPriceError] = useState('');
  const [communicationError, setCommunicationError] = useState('');
  const dropdownRef = useRef(null);

  // Parse duration string (e.g., "1:00" or "0:30") to minutes
  // Also handles if duration is already a number
  const parseDuration = (duration) => {
    if (!duration) return 60;
    if (typeof duration === 'number') return duration;
    if (typeof duration === 'string' && duration.includes(':')) {
      const [hours, minutes] = duration.split(':').map(Number);
      return hours * 60 + (minutes || 0);
    }
    // If it's a string number, parse it
    const parsed = parseInt(duration, 10);
    return isNaN(parsed) ? 60 : parsed;
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsPatientOpen(false);
        setPatientSearch('');
        setOthersSearch('');
        setPatientSearchError('');
      }
      if (dropdownRescheduleRef.current && !dropdownRescheduleRef.current.contains(e.target)) {
        setIsRescheduleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format minutes back to "HH:MM"
  const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}:${mins.toString().padStart(2, '0')}`;
  };

  // Format a slot as "MM/DD/YYYY, 2:30 PM" — DISPLAY only (the payload uses
  // formatTimeForPayload, 24-hour). One clock format (owner, 15 Oct 2026).
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    const day = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    return `${day}, ${formatClockTime(date)}`;
  };


  const handleSelectPatient = (pat) => {
    setPatient(pat.patientId);
    setIsPatientOpen(false);
    setPatientSearch('');
    setOthersSearch('');
    setPatientSearchError('');
  };

  // Format time to "HH:mm" for payload — uses browser local time (what user sees in UI)
  const formatTimeForPayload = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Format date to "YYYY-MM-DD" for payload — uses browser local time (what user sees in UI)
  const formatDateForPayload = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate duration in minutes from start and end times (HH:mm)
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 60;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    let diff = (endH * 60 + endM) - (startH * 60 + startM);
    // Appointment crosses midnight (e.g., 23:00 → 00:00) — treat end as next day
    if (diff < 0) diff += 24 * 60;
    return diff;
  };

  // Get the day of the week from selectedSlot
  const getDayOfWeek = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const docTz = selectedDoctor?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return date.toLocaleString('en-US', { weekday: 'long', timeZone: docTz });
  };
  const clickedDay = selectedSlot ? getDayOfWeek(selectedSlot.start) : '';

  // Find availability for the clicked day and get addressType
  const dayAvailability = selectedDoctor?.availability?.find(
    (avail) => avail.day === clickedDay
  );
  // Use initialAddressId if provided (from BookAppointmentModal), otherwise fall back to dayAvailability or selectedAppointment
  const addressTypeId = initialAddressId || dayAvailability?.addressType || selectedAppointment?.addressType || '';

  // Find the matching address using addressId === addressTypeId
  const selectedAddress = selectedDoctor?.address?.find(
    (addr) => addr.addressId === addressTypeId
  );
  const locations = selectedAddress ? [selectedAddress.addressType] : [t('appointmentmodal.no_address_available')];

  // State declarations
  const [patient, setPatient] = useState('');
  const masterTree = useMasterLocaleTree();
  const [location, setLocation] = useState(locations[0] || t('appointmentmodal.no_address_available'));
  const [speciality, setSpeciality] = useState('');
  const [appointmentType, setAppointmentType] = useState('');
  const [duration, setDuration] = useState('');
  const [price, setPrice] = useState('');
  const [payment, setPayment] = useState({ method: '', timing: 'postpaid' });
  const [mode, setMode] = useState('visio');
  const [communication, setCommunication] = useState('');
  const [notes, setNotes] = useState('');
  const [isRescheduleRecommendationMode, setIsRescheduleRecommendationMode] = useState(false);
  const [isManualRescheduleMode, setIsManualRescheduleMode] = useState(false);
  const [manualReschedulePayload, setManualReschedulePayload] = useState(null);
  const [isViewingMoreSlots, setIsViewingMoreSlots] = useState(false);
  const [selectedRecommendationSlots, setSelectedRecommendationSlots] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [openDay, setOpenDay] = useState(null);
  const [isCustomSelection, setIsCustomSelection] = useState(false);
  const [patientType, setPatientType] = useState('myPatient'); // 'myPatient' or 'new'
  // THE PROGRAMME QUESTION (doc/programme-visits.md §2): the answer to "What is
  // this appointment for?" when the chosen doctor runs an active programme for
  // this (existing) patient; null = nothing asked. `programmeRefusal` is the
  // server's PROGRAMME_* sentence for the last try.
  const [programmeChoice, setProgrammeChoice] = useState(null);
  const [programmeRefusal, setProgrammeRefusal] = useState('');
  const [programmeShowErrors, setProgrammeShowErrors] = useState(false);
  const [newPatientData, setNewPatientData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    dob: '',
    gender: '',
    countryId: '',
    timeZone: ''
  });
  const selectedCountry = countryTimezoneList?.find(c => c.countryId === newPatientData.countryId) || null;
  const originalDurationRef = useRef(null); // Store original duration from appointment type
  const originalPriceRef = useRef(null); // Store original price from appointment type or custom price API
  const isPriceManuallyChangedRef = useRef(false); // Track if doctor manually changed the price
  const lastFetchParamsRef = useRef(null);
  const originalAppointmentTypeIdRef = useRef(null); // Track original appointment type ID in edit mode
  const scrollableContentRef = useRef(null);
  const [isRescheduleDropdownOpen, setIsRescheduleDropdownOpen] = useState(false);
  const dropdownRescheduleRef = useRef(null);

  // Get the appropriate patient list based on patient type
  const getPatientList = () => {
    if (patientType === 'others') {
      return allPatientsList || [];
    }
    return patientNameList || [];
  };

  // Get the appropriate search term based on patient type
  const getSearchTerm = () => {
    if (patientType === 'others') {
      return othersSearch.toLowerCase();
    }
    return patientSearch.toLowerCase();
  };

  const filteredPatients = getPatientList().filter(p => {
    const searchTerm = getSearchTerm();
    const name = p.name || '';
    const patientId = p.patientId || '';
    // THIS HOSPITAL'S number (APO034) — the id the desk is handed. It lives
    // on the allocation now, so it is read through the helper that accepts
    // either key (utils/patientCode.js).
    const tenantCode = hospitalPatientCode(p) || '';
    const surname = p.surname || '';
    const fullName = `${name} ${surname}`.trim();
    return name.toLowerCase().includes(searchTerm) ||
      patientId.toLowerCase().includes(searchTerm) ||
      tenantCode.toLowerCase().includes(searchTerm) ||
      fullName.toLowerCase().includes(searchTerm);
  });

  // Helper function to scroll to bottom of modal
  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollableContentRef.current) {
        scrollableContentRef.current.scrollTop = scrollableContentRef.current.scrollHeight;
      }
    }, 0);
  };

  // Helper function to get the Monday of the current week
  const getCurrentWeekMonday = (date) => {
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.getFullYear(), date.getMonth(), diff);
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Fetch country-timezone list for new patient
  useEffect(() => {
    dispatch(docPatCreationCountryList());
  }, [dispatch]);

  // Check if the appointment is in the past
  const isPastAppointment = selectedAppointment?.localTime
    ? new Date(selectedAppointment.localTime) < new Date()
    : false;

  // True when the appointment sits inside a slot the doctor has chosen to
  // restrict via doctorSettings (business hour / leave / break). The Edit
  // button is hidden in that case so doctors can't modify a conflicted
  // booking without first lifting the matching restriction toggle.
  const isAppointmentInRestrictedSlot = useMemo(() => {
    if (!selectedAppointment || !selectedDoctor) return false;
    const settings = selectedDoctor.doctorSettings;
    if (!settings) return false;

    // Match the rest of the app: undefined/true → restricted, false → off.
    const businessHourRestriction = settings.businessHour !== false;
    const leaveRestriction = settings.leaveRestriction !== false;
    const breakRestriction = settings.breakRestriction !== false;
    if (!businessHourRestriction && !leaveRestriction && !breakRestriction) return false;

    const startSource = selectedAppointment.localTime || selectedAppointment.start;
    if (!startSource) return false;
    const apptStart = new Date(startSource);
    if (Number.isNaN(apptStart.getTime())) return false;

    const durationMin = Number(selectedAppointment.duration) || 30;
    const apptEnd = new Date(apptStart.getTime() + durationMin * 60 * 1000);

    const dateStr = `${apptStart.getFullYear()}-${String(apptStart.getMonth() + 1).padStart(2, '0')}-${String(apptStart.getDate()).padStart(2, '0')}`;
    const startMin = apptStart.getHours() * 60 + apptStart.getMinutes();
    const sameDay =
      apptEnd.getFullYear() === apptStart.getFullYear() &&
      apptEnd.getMonth() === apptStart.getMonth() &&
      apptEnd.getDate() === apptStart.getDate();
    const rawEndMin = sameDay ? apptEnd.getHours() * 60 + apptEnd.getMinutes() : 24 * 60;
    const endMin = rawEndMin <= startMin ? 24 * 60 : rawEndMin;

    const hhmmToMin = (s) => {
      if (!s) return null;
      const m = String(s).match(/^(\d{1,2}):(\d{2})/);
      return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
    };

    // Leave check — leave.date is "YYYY-MM-DD..." in selectedDoctor.leaveDates
    if (leaveRestriction && Array.isArray(selectedDoctor.leaveDates)) {
      for (const leave of selectedDoctor.leaveDates) {
        const leaveDateStr = leave?.date ? String(leave.date).slice(0, 10) : null;
        if (!leaveDateStr || leaveDateStr !== dateStr) continue;
        const ls = hhmmToMin(leave.startTime);
        const le = hhmmToMin(leave.endTime);
        if (ls == null || le == null) return true; // full-day leave
        if (startMin < le + 1 && endMin > ls) return true;
      }
    }

    // Business-hour / break check needs the day's availability entry.
    if ((businessHourRestriction || breakRestriction) && Array.isArray(selectedDoctor.availability)) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[apptStart.getDay()];
      const avail = selectedDoctor.availability.find((a) => a.day === dayName);

      if (businessHourRestriction) {
        if (!avail || !avail.isActive) return true;
        const aStart = hhmmToMin(avail.start);
        const aEnd = hhmmToMin(avail.end);
        if (aStart != null && aEnd != null) {
          if (startMin < aStart || endMin > aEnd) return true;
        }
      }

      if (breakRestriction && avail?.breaks?.length) {
        for (const br of avail.breaks) {
          const bs = hhmmToMin(br.breakStart);
          const be = hhmmToMin(br.breakEnd);
          if (bs != null && be != null && startMin < be && endMin > bs) return true;
        }
      }
    }

    return false;
  }, [selectedAppointment, selectedDoctor]);

  // Check if we're within 2 hours before the appointment time
  const isWithinTwoHoursBeforeAppointment = () => {
    // If early open restriction is disabled, always allow
    if (earlyOpenRestriction === false) return true;

    if (!selectedAppointment?.localTime) return false;

    const appointmentTime = new Date(selectedAppointment.localTime);
    const currentTime = new Date();
    // 30 Sep 2026: a doctor with no doctorSettings row gets `ctrlDuration: null`
    // from the diary, and `null * 60000` is 0 — Join opened only AT the start
    // time. The server's rule is the DoctorSettings model default: open 60
    // minutes before (Backend/models/doctorSetting.js `duration`, the same
    // fallback the patient web uses in MyAppointments.jsx#joinOpensAt).
    const leadMinutes = Number(controlDuration) > 0 ? Number(controlDuration) : 60;
    const twoHoursBeforeAppointment = new Date(appointmentTime.getTime() - (leadMinutes * 60 * 1000));

    return currentTime >= twoHoursBeforeAppointment;
  };

  // Get specialities based on selected address; preserve the appointment's
  // original specialty after a drag-drop to an address that doesn't offer it.
  const specialities = (() => {
    const list = selectedAddress?.specialty?.map((s) => s.name) || ['Ortho'];
    if (selectedAppointment?.SpecialtyId) {
      const originalSpecialtyName = selectedDoctor?.appointmentSpeciality?.find(
        (s) => s.specialtyId === selectedAppointment.SpecialtyId || s._id === selectedAppointment.SpecialtyId
      )?.name;
      if (originalSpecialtyName && !list.includes(originalSpecialtyName)) {
        return [...list, originalSpecialtyName];
      }
    }
    return list;
  })();

  // Get appointment types based on selected speciality
  const selectedSpecialityData = selectedDoctor?.appointmentSpeciality?.find(
    (s) => s.name === speciality
  );

  const appointmentTypes = selectedSpecialityData?.appointmentTypes?.map((appt) => ({
    type: appt.type,
    duration: parseDuration(appt.duration),
    price: parseInt(appt.price, 10),
    specialtyId: appt.specialtyId,
    virtual: appt.virtual,
    appointmentTypeId: appt.typeId,
  })) || [];

  // Check if selected appointment type allows virtual
  const selectedApptType = appointmentTypes.find((appt) => appt.type === appointmentType);
  const isVirtualAllowed = selectedApptType?.virtual || false;

  // Calculate current priceType based on the price source
  const currentPriceType = useMemo(() => {
    // Type 1: Price comes from appointmenttype (default, not from custom price API, not manually changed)
    // Type 2: Price comes from customprice API response (selectedCustomPrice exists and price matches it)
    // Type 3: Doctor manually edited the price (regardless of source)

    // If editing/rescheduling an existing appointment, use its pricetype if available
    // But if price was manually changed, it overrides to Type 3

    if (selectedAppointment?.pricetype && !isPriceManuallyChangedRef.current) {
      if (selectedAppointment.pricetype === 'Type 1' || selectedAppointment.pricetype === 'Type 2' || selectedAppointment.pricetype === 'Type 3') {
        return selectedAppointment.pricetype;
      }
    }

    // First check if price was manually changed - if so, it's always Type 3
    if (isPriceManuallyChangedRef.current) {
      return 'Type 3'; // Doctor manually edited the price
    }

    // Check if custom price exists from API
    if (selectedCustomPrice?.customPrice !== undefined && selectedCustomPrice?.customPrice !== null) {
      const customPriceValue = typeof selectedCustomPrice.customPrice === 'number'
        ? selectedCustomPrice.customPrice
        : parseFloat(selectedCustomPrice.customPrice) || 0;

      const currentPriceValue = typeof price === 'string'
        ? parseFloat(price) || 0
        : (typeof price === 'number' ? price : 0);

      // If current price matches custom price from API, it's Type 2
      if (currentPriceValue === customPriceValue) {
        return 'Type 2'; // Using custom price from API
      }
    }

    // No custom price and no manual change - using appointment type price
    return 'Type 1'; // Using appointment type price (default)
  }, [price, selectedCustomPrice, selectedAppointment?.pricetype]);

  // Fetch custom price when appointment type is selected (for both new and edit mode)
  useEffect(() => {
    if (patient && appointmentType && selectedApptType && selectedDoctor) {
      const doctorId = selectedDoctor.doctorId;
      const specialityId = selectedApptType.specialtyId;
      const appointmentTypeId = selectedApptType.appointmentTypeId;

      // Create a unique key for this combination of parameters
      const fetchKey = `${patient}-${doctorId}-${specialityId}-${appointmentTypeId}`;

      // Only call API if parameters have changed (avoid duplicate calls)
      if (lastFetchParamsRef.current !== fetchKey) {
        lastFetchParamsRef.current = fetchKey;

        // Call getSingleCustomPrice API with four IDs
        dispatch(getSingleCustomPrice({
          patientId: patient,
          doctorId: doctorId,
          specialityId: specialityId,
          appointmentTypeId: appointmentTypeId
        }));
      }
    }
  }, [patient, appointmentType, selectedApptType?.specialtyId, selectedApptType?.appointmentTypeId, selectedDoctor?.doctorId, dispatch]);

  // Update price based on custom price API response or default appointment type price (for both new and edit mode)
  const lastPriceUpdateRef = useRef({ customPrice: null, defaultPrice: null, appointmentTypeId: null });
  useEffect(() => {
    if (appointmentType && selectedApptType) {
      const appointmentTypeId = selectedApptType.appointmentTypeId || selectedApptType._id;

      // In edit mode, only update price if appointment type has changed from the original
      // For new appointments, always update when appointment type is selected
      // Also update if originalAppointmentTypeIdRef is null (specialty was changed, resetting the ref)
      const isAppointmentTypeChanged = selectedAppointment &&
        originalAppointmentTypeIdRef.current !== null &&
        originalAppointmentTypeIdRef.current !== appointmentTypeId;

      const isSpecialtyChanged = selectedAppointment && originalAppointmentTypeIdRef.current === null;

      const shouldUpdatePrice = !selectedAppointment || isAppointmentTypeChanged || isSpecialtyChanged;

      if (shouldUpdatePrice) {
        // If custom price exists from API, always use it (this takes priority over default)
        // Note: selectedCustomPrice is the data object directly (from action.payload.data)
        // The API response structure: { success: true, data: { customPrice: 20000, ... } }
        // Redux stores action.payload.data directly into selectedCustomPrice
        if (selectedCustomPrice?.customPrice !== undefined && selectedCustomPrice?.customPrice !== null) {
          // Only update if the custom price has actually changed
          if (lastPriceUpdateRef.current.customPrice !== selectedCustomPrice.customPrice ||
            lastPriceUpdateRef.current.appointmentTypeId !== appointmentTypeId) {
            const customPriceValue = selectedCustomPrice.customPrice;
            setPrice(customPriceValue);
            originalPriceRef.current = customPriceValue; // Store custom price as original
            isPriceManuallyChangedRef.current = false; // Reset manual change flag
            lastPriceUpdateRef.current = {
              customPrice: customPriceValue,
              defaultPrice: null,
              appointmentTypeId
            };
          }
        } else if (!price || price === originalPriceRef.current || lastPriceUpdateRef.current.appointmentTypeId !== appointmentTypeId) {
          // Only set default price if no custom price exists and price field is empty or matches original
          // Or if appointment type has changed (for edit mode)
          const defaultPrice = selectedApptType.price;
          // Only update if the default price has actually changed
          if (lastPriceUpdateRef.current.defaultPrice !== defaultPrice ||
            lastPriceUpdateRef.current.appointmentTypeId !== appointmentTypeId) {
            setPrice(defaultPrice);
            originalPriceRef.current = defaultPrice; // Store appointment type price as original
            isPriceManuallyChangedRef.current = false; // Reset manual change flag
            lastPriceUpdateRef.current = {
              customPrice: null,
              defaultPrice,
              appointmentTypeId
            };
          }
        }
      }
    }
  }, [selectedCustomPrice, appointmentType, selectedApptType, selectedAppointment, price]);

  // Update selectedSlot.end when custom duration changes in custom selection mode (for both new and edit mode)
  useEffect(() => {
    if (isCustomSelection && selectedSlot && selectedSlot.start && duration) {
      const durationInMinutes = typeof duration === 'number' ? duration : parseDuration(duration);
      if (durationInMinutes && durationInMinutes > 0) {
        const start = new Date(selectedSlot.start);
        const newEnd = new Date(start.getTime() + durationInMinutes * 60 * 1000);
        const newEndISO = newEnd.toISOString();
        // Only update if the end time has actually changed to avoid infinite loops
        if (selectedSlot.end !== newEndISO) {
          setSelectedSlot(prev => ({ ...prev, end: newEndISO }));
        }
      }
    }
  }, [duration, isCustomSelection, selectedSlot?.start]);

  // Set patient from selectedPatient (Redux) when modal opens for new appointment
  // Only auto-select patient when opened from BookAppointmentModal flow (has initialSpeciality/initialAppointmentType)
  // Calendar page (Appointment.jsx) should always let the user select patient manually
  useEffect(() => {
    if (isModalOpen && !selectedAppointment && selectedPatient?.patientId && !patient && (initialSpeciality || initialAppointmentType)) {
      setPatient(selectedPatient.patientId);
    }
  }, [isModalOpen, selectedAppointment, selectedPatient, patient, initialSpeciality, initialAppointmentType]);

  // Initialize speciality, appointmentType, duration, and location from BookAppointmentModal props
  useEffect(() => {
    if (isModalOpen && !selectedAppointment && selectedDoctor) {
      // Reset duration and ref when modal opens fresh (without initial values and no user selections)
      if (!initialSpeciality && !initialAppointmentType && !speciality && !appointmentType && duration) {
        setDuration('');
        originalDurationRef.current = null;
      }

      // Initialize speciality if provided and not already set
      if (initialSpeciality && !speciality) {
        setSpeciality(initialSpeciality);
      }

      // Initialize appointmentType if provided and not already set
      if (initialAppointmentType && !appointmentType) {
        setAppointmentType(initialAppointmentType);

        // Also set duration from appointment type
        if (initialSpeciality && selectedDoctor?.appointmentSpeciality) {
          const selectedSpecialityData = selectedDoctor.appointmentSpeciality.find(
            (s) => s.name === initialSpeciality
          );
          const apptType = selectedSpecialityData?.appointmentTypes?.find(
            (appt) => appt.type === initialAppointmentType
          );
          if (apptType) {
            const durationInMinutes = parseDuration(apptType.duration);
            setDuration(durationInMinutes);
            originalDurationRef.current = durationInMinutes;
          }
        }
      }

      // Initialize location if initialAddressId is provided
      if (initialAddressId && selectedDoctor?.address) {
        const addressFromId = selectedDoctor.address.find(
          (addr) => addr.addressId === initialAddressId
        );
        if (addressFromId && (location === t('appointmentmodal.no_address_available') || !location)) {
          setLocation(addressFromId.addressType);
        }
      }
    }
  }, [isModalOpen, selectedAppointment, initialSpeciality, initialAppointmentType, initialAddressId, selectedDoctor, speciality, appointmentType, location, duration]);

  // Fetch patients when "Others" is selected and search changes
  useEffect(() => {
    if (patientType === 'others' && isPatientOpen && !patientSearchError) {
      dispatch(getAllPatientsList({ search: debouncedOthersSearch }));
    }
  }, [dispatch, debouncedOthersSearch, patientType, isPatientOpen, patientSearchError]);

  // Debounced email check (only for new patient)
  useEffect(() => {
    if (patientType !== 'new') {
      setEmailStatus(null);
      return;
    }

    // Clear previous timeout
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
    }

    const email = newPatientData.email?.trim() || '';

    // If email is empty or invalid format, reset status
    if (!email || !emailRegex.test(email)) {
      setEmailStatus(null);
      lastCheckedEmailRef.current = '';
      return;
    }

    // If email hasn't changed or check is in progress, don't check again
    if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
      return;
    }

    // Set new timeout for debounced check (500ms delay)
    emailCheckTimeoutRef.current = setTimeout(async () => {
      const trimmedEmail = newPatientData.email.trim();

      // Double check email is still valid and hasn't changed during debounce
      if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
        return;
      }

      // Mark check as in progress and update last checked email BEFORE making the API call
      isEmailCheckInProgressRef.current = true;
      lastCheckedEmailRef.current = trimmedEmail;
      const runId = ++emailRunRef.current;
      // A newer run started, or the field was edited/cleared since this run
      // began (onChange, the X icon and handleMocPopupClose reset the ref).
      const isStale = () => runId !== emailRunRef.current || lastCheckedEmailRef.current !== trimmedEmail;

      setIdentifierBusy((b) => ({ ...b, email: true }));
      try {
        // 1) A doctor/staff login with this email at THIS hospital still
        //    blocks — the MOC lookup only knows patients. tenantId makes the
        //    answer per-hospital instead of platform-wide.
        const existAction = await dispatch(checkEmailExists({ email: trimmedEmail, tenantId, axiosInstance }));
        if (isStale()) return;
        const exists = checkEmailExists.fulfilled.match(existAction) ? existAction.payload : null;
        if (exists?.exists && exists.role && exists.role !== 'patient') {
          setIsPatientFound(false);
          setFoundPatientId(null);
          setEmailStatus(false);
          setEmailExistsRole(exists.role);
          setEmailBlock(exists.role === 'doctor' ? 'doctor' : 'account');
          return;
        }

        // 2) Tenant-aware lookup — registered HERE (case A: use that patient),
        //    a Mitra One Care account elsewhere (case B: link), a link request
        //    already pending, or free (case C: create as today).
        const lookupAction = await dispatch(lookupMocLink({ email: trimmedEmail, doctorId: selectedDoctor?.doctorId }));
        if (isStale()) return;
        if (lookupMocLink.fulfilled.match(lookupAction)) {
          const r = lookupAction.payload;
          const kind = classifyMocLookup(r);
          if (kind === 'inTenant') {
            // ONE CONTACT, SEVERAL PATIENTS. Before reusing "the" matching
            // row, ask whether this contact is a household — it is the whole
            // point of the picker that the desk gets to say WHICH member.
            const hh = await tryHousehold('email', { email: trimmedEmail });
            if (isStale()) return;
            if (hh) { showHousehold(hh); return; }
            // Case A — the desk already knows this person, so there is nothing
            // to ask: fetch the row unmasked, prefill + lock the form and book
            // against that patientId. A green line says why the form changed.
            let full = null;
            const fullAction = await dispatch(lookupPatientByEmail({ email: trimmedEmail, axiosInstance }));
            if (isStale()) return;
            if (lookupPatientByEmail.fulfilled.match(fullAction) && fullAction.payload?.found) full = fullAction.payload.data;
            applyFoundPatient(full?.patientId ? full : { ...r.inTenant, email: undefined, phone: undefined });
            setInTenantApplied('email');
            setInTenantOwned(r.inTenant?.linkedToDoctor === true);
          } else if (kind) {
            openMocPopup('email', r);
            setEmailStatus(false);
            setEmailExistsRole(null);
            setEmailBlock(kind);
          } else if (exists?.exists) {
            // A login exists here without a patient row — creating would
            // collide, so keep the plain "already exists".
            setIsPatientFound(false);
            setFoundPatientId(null);
            setEmailStatus(false);
            setEmailExistsRole(exists.role || null);
            setEmailBlock('account');
          } else {
            setIsPatientFound(false);
            setFoundPatientId(null);
            setEmailStatus(true);
            setEmailExistsRole(null);
            setEmailBlock(null);
          }
        } else {
          // Old backend without /moc-link/lookup: the in-tenant lookup-by-email
          // prefill + the per-tenant duplicate answer, as before.
          const fullAction = await dispatch(lookupPatientByEmail({ email: trimmedEmail, axiosInstance }));
          if (isStale()) return;
          if (lookupPatientByEmail.fulfilled.match(fullAction) && fullAction.payload?.found) {
            const hh = await tryHousehold('email', { email: trimmedEmail });
            if (isStale()) return;
            if (hh) { showHousehold(hh); return; }
            applyFoundPatient(fullAction.payload.data);
            return;
          }
          setIsPatientFound(false);
          setFoundPatientId(null);
          if (exists) {
            setEmailStatus(!exists.exists);
            setEmailExistsRole(exists.exists ? (exists.role || null) : null);
            setEmailBlock(exists.exists ? 'account' : null);
          } else {
            setEmailStatus(null);
            setEmailExistsRole(null);
            setEmailBlock(null);
          }
        }
      } catch (error) {
        console.error('Error checking email:', error);
        if (!isStale()) setEmailStatus(null);
      } finally {
        // Token only: a superseded run must not clear the newer run's
        // in-progress flag / spinner (the ref may legitimately be '' here).
        if (runId === emailRunRef.current) {
          isEmailCheckInProgressRef.current = false;
          setIdentifierBusy((b) => ({ ...b, email: false }));
        }
      }
    }, 500);

    // Cleanup function
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPatientData.email, patientType, dispatch, axiosInstance, recheckTick]);

  // Debounced phone check (only for new patient, skip if patient was found via email lookup)
  useEffect(() => {
    if (patientType !== 'new' || isPatientFound) {
      setPhoneStatus(null);
      lastCheckedPhoneRef.current = '';
      return;
    }

    if (phoneCheckTimeoutRef.current) {
      clearTimeout(phoneCheckTimeoutRef.current);
    }

    const phone = newPatientData.phone?.trim() || '';
    const minLen = selectedCountry?.mobileMinLength || 9;
    const maxLen = selectedCountry?.mobileMaxLength || 10;
    const isValidFormat = new RegExp(`^\\d{${minLen},${maxLen}}$`).test(phone);

    if (!phone || !isValidFormat) {
      setPhoneStatus(null);
      lastCheckedPhoneRef.current = '';
      return;
    }

    if (lastCheckedPhoneRef.current === phone || isPhoneCheckInProgressRef.current) {
      return;
    }

    phoneCheckTimeoutRef.current = setTimeout(async () => {
      const trimmedPhone = newPatientData.phone.trim();

      if (!trimmedPhone || lastCheckedPhoneRef.current === trimmedPhone || isPhoneCheckInProgressRef.current) {
        return;
      }

      isPhoneCheckInProgressRef.current = true;
      lastCheckedPhoneRef.current = trimmedPhone;
      const runId = ++phoneRunRef.current;
      // Same currency guard as the email check above.
      const isStale = () => runId !== phoneRunRef.current || lastCheckedPhoneRef.current !== trimmedPhone;

      setIdentifierBusy((b) => ({ ...b, phone: true }));
      try {
        // Tenant-aware lookup (see the email check above). The old
        // platform-wide mobileNoExist no longer blocks here.
        const lookupAction = await dispatch(lookupMocLink({ phone: trimmedPhone, doctorId: selectedDoctor?.doctorId }));
        if (isStale()) return;
        if (lookupMocLink.fulfilled.match(lookupAction)) {
          const r = lookupAction.payload;
          const kind = classifyMocLookup(r);
          if (kind === 'inTenant') {
            // Same household question as the email check above — one mobile
            // can be the login for a mother and her children alike.
            const hh = await tryHousehold('phone', { phone: trimmedPhone });
            if (isStale()) return;
            if (hh) { showHousehold(hh); return; }
            // Case A, matched by phone — same automatic hand-off as the email
            // check above; fetch the record unmasked for the prefill.
            let full = null;
            try {
              const res = await axiosInstance.get(`/patients/${r.inTenant.patientId}`);
              full = res.data?.patient || res.data || null;
            } catch {
              full = null;
            }
            if (isStale()) return;
            applyFoundPatient(full?.patientId ? full : { ...r.inTenant, email: undefined, phone: undefined });
            setInTenantApplied('phone');
            setInTenantOwned(r.inTenant?.linkedToDoctor === true);
          } else if (kind) {
            openMocPopup('phone', r);
            setPhoneStatus(false);
            setPhoneBlock(kind);
          } else {
            setPhoneStatus(true);
            setPhoneBlock(null);
          }
        } else {
          // Old backend without /moc-link/lookup: per-tenant answer only.
          const resultAction = await dispatch(checkMobileExists({ phone: trimmedPhone, tenantId, axiosInstance }));
          if (isStale()) return;
          if (checkMobileExists.fulfilled.match(resultAction)) {
            if (resultAction.payload.exists) {
              const hh = await tryHousehold('phone', { phone: trimmedPhone });
              if (isStale()) return;
              if (hh) { showHousehold(hh); return; }
            }
            setPhoneStatus(!resultAction.payload.exists);
            setPhoneBlock(resultAction.payload.exists ? 'account' : null);
          } else {
            setPhoneStatus(null);
            lastCheckedPhoneRef.current = '';
          }
        }
      } catch (err) {
        console.error('Error checking phone:', err);
        if (!isStale()) {
          setPhoneStatus(null);
          lastCheckedPhoneRef.current = '';
        }
      } finally {
        // Token only: a superseded run must not clear the newer run's
        // in-progress flag / spinner.
        if (runId === phoneRunRef.current) {
          isPhoneCheckInProgressRef.current = false;
          setIdentifierBusy((b) => ({ ...b, phone: false }));
        }
      }
    }, 500);

    return () => {
      if (phoneCheckTimeoutRef.current) {
        clearTimeout(phoneCheckTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPatientData.phone, patientType, isPatientFound, dispatch, axiosInstance, recheckTick]);

  // --- OTP helpers ---
  const resetOtpEmailState = useCallback(() => {
    setEmailOtpSent(false);
    setEmailOtpCode('');
    setEmailOtpVerified(false);
    setEmailOtpError('');
    setEmailOtpTimer(0);
    if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
    emailOtpVerifyingRef.current = false;
    setEmailProof(null);
  }, []);

  const resetOtpPhoneState = useCallback(() => {
    setPhoneOtpSent(false);
    setPhoneOtpCode('');
    setPhoneOtpVerified(false);
    setPhoneOtpError('');
    setPhoneOtpTimer(0);
    if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
    phoneOtpVerifyingRef.current = false;
    setPhoneProof(null);
  }, []);

  // ---- MOC linking (components/moc/MocLinkModal.jsx) ----
  const resetMocState = useCallback(() => {
    setEmailBlock(null);
    setPhoneBlock(null);
    setHousehold(null);
    setHouseholdPick(null);
    setHouseholdAdding(false);
    setHouseholdAddError('');
    setInTenantApplied(null); setInTenantOwned(null);
    setMocPopup(null);
    lastLookupRef.current = { email: null, phone: null };
    suppressPopupRef.current = false;
  }, []);
  const openMocPopup = (source, lookup) => {
    lastLookupRef.current[source] = lookup;
    // A quiet re-check after the popup changed something must not pop it
    // straight back open — the field's "View" button does that on demand.
    if (suppressPopupRef.current) { suppressPopupRef.current = false; return; }
    setMocPopup({ source, lookup });
  };
  const reopenMocPopup = (source) => {
    const lookup = lastLookupRef.current[source];
    if (lookup) setMocPopup({ source, lookup });
  };
  const blockMessage = (field) => {
    const kind = field === 'email' ? emailBlock : phoneBlock;
    if (field === 'email' && emailExistsRole === 'doctor') return t('appointmentmodal.email_used_by_doctor');
    switch (kind) {
      case 'moc': return t('appointmentmodal.has_moc_account', 'Has a Mitra One Care profile — add them to this hospital to continue');
      // OPEN PLATFORM: the record exists here but belongs to another doctor.
      // Not a duplicate and not an error — a consent step.
      case 'doctorConsent': return t('appointmentmodal.needs_doctor_consent', 'Ask this patient before adding them to this doctor');
      case 'pending': return t('appointmentmodal.link_pending', 'A link request is already pending');
      default: return field === 'email' ? t('appointmentmodal.email_already_exists') : t('appointmentmodal.phone_already_exists');
    }
  };
  // What the desk has typed so far. Kept with the consent request and used,
  // once the patient confirms, to FILL BLANKS on their person row — never to
  // overwrite what is there, and not at all once they have signed in
  // themselves (doc/one-patient-row.md §10a.4).
  const buildDraftPatient = () => {
    const d = {};
    Object.entries(newPatientData).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== '') d[k] = typeof v === 'string' ? v.trim() : v;
    });
    const lang = localStorage.getItem('i18nextLng');
    if (lang) d.lang = lang;
    return d;
  };
  // Switch the "New patient" form to an EXISTING patient of this hospital —
  // the same prefill + lock as the old lookup-by-email path; the booking then
  // goes through /appointment/book with patientId (isNewPatient: false).
  const applyFoundPatient = (p) => {
    if (!p?.patientId) return;
    setIsPatientFound(true);
    setFoundPatientId(p.patientId);
    setNewPatientData(prev => ({
      ...prev,
      name: p.name || '',
      surname: p.surname || '',
      email: p.email || prev.email,
      phone: p.phone || prev.phone,
      dob: p.dob ? String(p.dob).split('T')[0] : '',
      gender: p.gender || '',
      countryId: p.countryId || prev.countryId || '',
      timeZone: p.timeZone || prev.timeZone || ''
    }));
    setNewPatientErrors({ name: '', surname: '', email: '', gender: '', dob: '', phone: '' });
    setEmailStatus(true);
    setEmailExistsRole(null);
    setEmailBlock(null);
    setPhoneStatus(true);
    setPhoneBlock(null);
    resetOtpEmailState();
    resetOtpPhoneState();
    lastCheckedEmailRef.current = (p.email || newPatientData.email || '').trim();
    lastCheckedPhoneRef.current = (p.phone || newPatientData.phone || '').trim();
  };
  // Ask whether the matched contact is a whole household. Returns the payload
  // only when there is actually something to CHOOSE — a lone primary with no
  // room for a new member is the single record the old flow already handled,
  // and raising a one-row radio list for it would be a click that decides
  // nothing. 404 NO_MATCH and any other refusal answer null, i.e. "carry on as
  // before"; this endpoint must never be able to stop a booking.
  const tryHousehold = async (source, ident) => {
    let action;
    try {
      action = await dispatch(lookupPatientHousehold({ ...ident, axiosInstance }));
    } catch {
      return null;
    }
    if (!lookupPatientHousehold.fulfilled.match(action)) return null;
    const h = action.payload;
    const members = Array.isArray(h?.members) ? h.members.filter((m) => m?.patientId) : [];
    const rows = (h?.primary?.patientId ? 1 : 0) + members.length;
    if (!rows) return null;
    if (rows === 1 && !h.canAddMember) return null;
    return { ...h, members, source };
  };
  const resetHousehold = () => {
    setHousehold(null);
    setHouseholdPick(null);
    setHouseholdAddError('');
  };
  // Raise the picker. The identifier is NOT a red block — the contact is fine,
  // it just belongs to more than one person — so the field is left green and
  // the answer is owed at save time (handleSaveAppointment refuses without one).
  const showHousehold = (h) => {
    setHousehold(h);
    setHouseholdPick(null);
    setHouseholdAddError('');
    setIsPatientFound(false);
    setFoundPatientId(null);
    setInTenantApplied(null);
    setInTenantOwned(null);
    if (h.source === 'phone') { setPhoneStatus(true); setPhoneBlock(null); }
    else { setEmailStatus(true); setEmailExistsRole(null); setEmailBlock(null); }
  };
  // A row was chosen (or un-chosen, when the desk switched to "add a new
  // member"). Choosing hands the form to the ordinary existing-patient path.
  const pickHouseholdMember = (person) => {
    setHouseholdAddError('');
    if (!person?.patientId) {
      setHouseholdPick(null);
      setIsPatientFound(false);
      setFoundPatientId(null);
      return;
    }
    setHouseholdPick(person);
    applyFoundPatient(person);
  };
  // "Add as a new family member": no contact fields — the contact is the
  // primary's — and on 201 the new row becomes the pick, so the booking that
  // follows is the same ordinary /appointment/book call as any other.
  const submitHouseholdMember = async (values) => {
    const primaryId = household?.primary?.patientId;
    if (!primaryId) return;
    setHouseholdAddError('');
    setHouseholdAdding(true);
    try {
      const action = await dispatch(addHouseholdMember({ primaryPatientId: primaryId, ...values, axiosInstance }));
      if (addHouseholdMember.fulfilled.match(action)) {
        const created = action.payload?.patient || action.payload || {};
        const person = {
          ...values,
          ...created,
          patientId: created.patientId,
          accountType: 'secondary',
          relation: created.relation || values.relation,
        };
        if (!person.patientId) {
          setHouseholdAddError(t('appointmentmodal.household.add_failed', 'Could not add the family member. Please try again.'));
          return;
        }
        setHousehold((h) => (h ? { ...h, members: [...(h.members || []), person] } : h));
        pickHouseholdMember(person);
        toast.success(t('appointmentmodal.household.member_added', 'Family member added'));
      } else {
        // 404 / 403 / 422 / 409 — the server's own sentence when it sent one,
        // shown on the form like any other refusal.
        const payload = action.payload || {};
        setHouseholdAddError(
          (typeof payload.message === 'string' && payload.message)
          || t('appointmentmodal.household.add_failed', 'Could not add the family member. Please try again.')
        );
      }
    } finally {
      setHouseholdAdding(false);
    }
  };
  // Back out of the automatic case-A hand-off. The matched record filled BOTH
  // identifiers, so a mistyped one can only be corrected by clearing both and
  // starting the identifier step again.
  const clearInTenantMatch = () => {
    setInTenantApplied(null); setInTenantOwned(null);
    setHousehold(null);
    setHouseholdPick(null);
    setHouseholdAddError('');
    setIsPatientFound(false);
    setFoundPatientId(null);
    setEmailStatus(null);
    setEmailExistsRole(null);
    setEmailBlock(null);
    setPhoneStatus(null);
    setPhoneBlock(null);
    setNewPatientData(prev => ({ ...prev, email: '', phone: '', name: '', surname: '', dob: '', gender: '' }));
    setNewPatientErrors({ name: '', surname: '', email: '', gender: '', dob: '', phone: '' });
    lastCheckedEmailRef.current = '';
    lastCheckedPhoneRef.current = '';
    isEmailCheckInProgressRef.current = false;
    isPhoneCheckInProgressRef.current = false;
    resetOtpEmailState();
    resetOtpPhoneState();
  };
  const handleMocPopupClose = ({ changed } = {}) => {
    const source = mocPopup?.source;
    setMocPopup(null);
    if (changed) {
      // A request was cancelled/created inside the popup — re-check the field
      // quietly so its message reflects the new state.
      suppressPopupRef.current = true;
      if (source === 'phone') { setPhoneStatus(null); setPhoneBlock(null); lastCheckedPhoneRef.current = ''; }
      else { setEmailStatus(null); setEmailBlock(null); lastCheckedEmailRef.current = ''; }
      setRecheckTick((n) => n + 1);
    }
  };
  // 409 ALREADY_LINKED / EMAIL_IN_USE { patientId }: the record already exists
  // here — fetch it unmasked and continue exactly like case A.
  const handleUseExistingById = async ({ patientId }) => {
    setMocPopup(null);
    if (!patientId) return;
    try {
      const res = await axiosInstance.get(`/patients/${patientId}`);
      const full = res.data?.patient || res.data || null;
      applyFoundPatient(full?.patientId ? full : { patientId });
    } catch {
      applyFoundPatient({ patientId });
    }
  };
  // Case B, OTP confirmed — the server created/linked the row and returns the
  // patient: carry on booking with that patientId.
  const handleMocLinked = (patient) => {
    setMocPopup(null);
    if (patient?.patientId) applyFoundPatient(patient);
  };
  // Case B, Approval — async; booking only after the patient accepts.
  const handleMocApprovalSent = () => {
    setMocPopup(null);
    toast.info(t('appointmentmodal.moc_approval_sent', "Request sent to the patient's app — you'll be notified. Booking is possible after they accept."));
    handleCloseModal();
  };

  const startOtpTimer = (setTimer, timerRef) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTimer(60);
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendEmailOtp = async () => {
    setEmailOtpError('');
    try {
      const result = await dispatch(sendOtp({ email: newPatientData.email.trim(), axiosInstance })).unwrap();
      if (result?.success) {
        setEmailOtpSent(true);
        setEmailOtpCode('');
        setEmailOtpVerified(false);
        startOtpTimer(setEmailOtpTimer, emailOtpTimerRef);
      }
    } catch (err) {
      setEmailOtpError(typeof err === 'string' ? err : t('appointmentmodal.failed_to_send_otp'));
    }
  };

  const handleVerifyEmailOtp = useCallback(async (code) => {
    const otpVal = code ?? emailOtpCode;
    if (emailOtpVerifyingRef.current || otpVal.length !== 6) return;
    emailOtpVerifyingRef.current = true;
    setEmailOtpError('');
    try {
      const result = await dispatch(verifyOtp({ email: newPatientData.email.trim(), otpCode: otpVal, axiosInstance })).unwrap();
      if (result?.success) {
        setEmailOtpVerified(true);
        setEmailProof(result?.proof?.token || null);
        setNewPatientErrors(prev => ({ ...prev, email: '' }));
        if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
        setEmailOtpTimer(0);
      } else {
        setEmailOtpError(t('appointmentmodal.invalid_otp'));
      }
    } catch (err) {
      setEmailOtpError(typeof err === 'string' ? err : t('appointmentmodal.invalid_otp'));
    } finally {
      emailOtpVerifyingRef.current = false;
    }
  }, [emailOtpCode, dispatch, axiosInstance, newPatientData.email]);

  const handleSendPhoneOtp = async () => {
    setPhoneOtpError('');
    try {
      const result = await dispatch(sendOtp({ phone: newPatientData.phone.trim(), axiosInstance })).unwrap();
      if (result?.success) {
        setPhoneOtpSent(true);
        setPhoneOtpCode('');
        setPhoneOtpVerified(false);
        startOtpTimer(setPhoneOtpTimer, phoneOtpTimerRef);
      }
    } catch (err) {
      setPhoneOtpError(typeof err === 'string' ? err : t('appointmentmodal.failed_to_send_otp'));
    }
  };

  const handleVerifyPhoneOtp = useCallback(async (code) => {
    const otpVal = code ?? phoneOtpCode;
    if (phoneOtpVerifyingRef.current || otpVal.length !== 6) return;
    phoneOtpVerifyingRef.current = true;
    setPhoneOtpError('');
    try {
      const result = await dispatch(verifyOtp({ phone: newPatientData.phone.trim(), otpCode: otpVal, axiosInstance })).unwrap();
      if (result?.success) {
        setPhoneOtpVerified(true);
        setPhoneProof(result?.proof?.token || null);
        setNewPatientErrors(prev => ({ ...prev, phone: '' }));
        if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
        setPhoneOtpTimer(0);
      } else {
        setPhoneOtpError(t('appointmentmodal.invalid_otp'));
      }
    } catch (err) {
      setPhoneOtpError(typeof err === 'string' ? err : t('appointmentmodal.invalid_otp'));
    } finally {
      phoneOtpVerifyingRef.current = false;
    }
  }, [phoneOtpCode, dispatch, axiosInstance, newPatientData.phone]);

  // Cleanup OTP timers on unmount
  useEffect(() => {
    return () => {
      if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
      if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
    };
  }, []);

  // Populate states in edit/reschedule mode using IDs
  useEffect(() => {
    if (selectedAppointment) {
      // Start in view mode when opening a booked appointment (not rescheduling)
      if (!isRescheduling) {
        setIsViewMode(true);
      }
      setPatient(selectedAppointment.patientId || '');
      setPatientType('myPatient'); // Always use "My patient" in edit mode
      setNewPatientData({
        name: '',
        surname: '',
        email: '',
        phone: '',
        dob: '',
        gender: '',
        countryId: '',
        timeZone: ''
      });
      setLocation(selectedAddress?.addressType || selectedAppointment.addressTypeName || t('appointmentmodal.no_address_available'));

      // Use actual appointment values, not defaults from appointment type configuration
      const calcDuration = calculateDuration(selectedAppointment.timeSlot, selectedAppointment.slotEndTime);
      const actualMode = selectedAppointment.appointmentMode === 'onsite' ? 'visio' : 'Online';
      const actualPrice = selectedAppointment.price || '';
      const actualDuration = calcDuration;

      setMode(actualMode);
      setCommunication(selectedAppointment.description || '');
      setNotes(selectedAppointment.doctorDescription || '');

      // Find the speciality and appointment type names for display only (don't override actual values)
      let foundSpeciality = '';
      let foundAppointmentType = '';

      for (const spec of selectedDoctor?.appointmentSpeciality || []) {
        const appt = spec.appointmentTypes.find(
          (a) => a.typeId === selectedAppointment.appointmentType && a.specialtyId === selectedAppointment.SpecialtyId
        );
        if (appt) {
          foundSpeciality = spec.name;
          foundAppointmentType = appt.type;
          break;
        }
      }

      setSpeciality(foundSpeciality);
      setAppointmentType(foundAppointmentType);

      // Use actual appointment values, not defaults from appointment type
      setDuration(actualDuration);
      setPrice(actualPrice);
      setMode(actualMode);

      // Set original price ref for edit mode (to track if price is manually changed)
      originalPriceRef.current = actualPrice;
      isPriceManuallyChangedRef.current = false; // Reset flag, will be set if doctor changes price

      // Store original appointment type ID to detect when it changes
      originalAppointmentTypeIdRef.current = selectedAppointment.appointmentType;

      const startDate = new Date(selectedAppointment.localTime || selectedAppointment.scheduledDate || selectedAppointment.start);
      const durationMins = actualDuration && !isNaN(actualDuration) ? actualDuration : (selectedAppointment.duration || 15);
      if (!isNaN(startDate.getTime())) {
        const endDate = new Date(startDate.getTime() + durationMins * 60 * 1000);
        if (!isNaN(endDate.getTime())) {
          setSelectedSlot({
            start: startDate.toISOString(),
            end: endDate.toISOString(),
          });
        }
      }
    }
  }, [selectedAppointment, selectedDoctor, selectedAddress, setSelectedSlot]);

  // Set duration when appointmentType is set (for both manual changes and initialization)
  // This ensures duration is populated when coming from BookAppointmentModal
  useEffect(() => {
    if (appointmentType && speciality && !selectedAppointment && selectedDoctor && isModalOpen) {
      const selectedSpecialityData = selectedDoctor?.appointmentSpeciality?.find(
        (s) => s.name === speciality
      );
      const apptType = selectedSpecialityData?.appointmentTypes?.find(
        (appt) => appt.type === appointmentType
      );
      if (apptType) {
        const durationInMinutes = parseDuration(apptType.duration);
        // Set duration if it's empty or if originalDurationRef is not set (initialization case)
        if (!duration || originalDurationRef.current === null) {
          setDuration(durationInMinutes);
          originalDurationRef.current = durationInMinutes;
          // Update selectedSlot end time if slot is available (skip in custom selection —
          // the dedicated duration→slot.end useEffect handles it there).
          if (selectedSlot && !isCustomSelection) {
            const start = new Date(selectedSlot.start);
            const newEnd = new Date(start.getTime() + durationInMinutes * 60 * 1000);
            setSelectedSlot({ start: selectedSlot.start, end: newEnd.toISOString() });
          }
        }
      }
    } else if (speciality && !appointmentType && !selectedAppointment && isModalOpen) {
      // Clear duration when specialty changes and appointmentType is reset
      setDuration('');
      originalDurationRef.current = null;
    }
  }, [appointmentType, speciality, selectedAppointment, isCustomSelection, selectedDoctor, isModalOpen, duration, selectedSlot]);

  // Update duration, price, and mode when appointment type changes
  const handleAppointmentTypeChange = (e) => {
    const selectedType = e.target.value;
    setAppointmentType(selectedType);
    const apptType = appointmentTypes.find((appt) => appt.type === selectedType);
    if (apptType) {
      // Always store the original duration from appointment type
      originalDurationRef.current = apptType.duration;

      // Reset price refs when appointment type changes
      // The price will be set by the useEffect that handles custom price API or default price
      originalPriceRef.current = null;
      isPriceManuallyChangedRef.current = false;
      lastPriceUpdateRef.current = { customPrice: null, defaultPrice: null, appointmentTypeId: null };
      dispatch(clearSelectedCustomPrice());

      // Always populate duration with the appointment type's default — in
      // custom selection mode the doctor can still adjust it via the +/- buttons,
      // but they need a sensible starting value (not an empty field).
      setDuration(apptType.duration);

      // Mode and slot end are only auto-updated outside custom selection mode;
      // in custom mode the dedicated duration→slot.end useEffect handles the end time.
      if (!isCustomSelection) {
        // 30 Sep 2026: a virtual type on a plan (or hospital) without video
        // used to set video here while the toggle is disabled — the desk could
        // neither book it (the server refuses) nor switch it back.
        setMode(apptType.virtual && videoAllowed ? 'Online' : 'visio');
        if (selectedSlot) {
          const start = new Date(selectedSlot.start);
          const newEnd = new Date(start.getTime() + apptType.duration * 60 * 1000);
          setSelectedSlot({ start: selectedSlot.start, end: newEnd.toISOString() });
        }
      }
    }
  };

  // Restore original duration when custom selection is unchecked (for both new and edit mode)
  useEffect(() => {
    if (!isCustomSelection && originalDurationRef.current !== null && appointmentType) {
      const apptType = appointmentTypes.find((appt) => appt.type === appointmentType);
      if (apptType && apptType.duration === originalDurationRef.current) {
        setDuration(originalDurationRef.current);
        if (selectedSlot?.start) {
          const start = new Date(selectedSlot.start);
          const newEndISO = new Date(start.getTime() + originalDurationRef.current * 60 * 1000).toISOString();
          if (selectedSlot.end !== newEndISO) {
            setSelectedSlot({ start: selectedSlot.start, end: newEndISO });
          }
        }
      }
    }
  }, [isCustomSelection, appointmentType, selectedSlot?.start]);

  // Restore original price when custom selection is unchecked (for both new and edit mode)
  useEffect(() => {
    if (!isCustomSelection && originalPriceRef.current !== null && appointmentType) {
      // Restore to original price (either from appointment type or custom price API)
      setPrice(originalPriceRef.current);
      isPriceManuallyChangedRef.current = false; // Reset manual change flag
    }
  }, [isCustomSelection, appointmentType]);

  // Update appointment type when speciality changes
  const handleSpecialityChange = (e) => {
    const selectedSpec = e.target.value;
    setSpeciality(selectedSpec);
    setAppointmentType('');
    setDuration('');
    setPrice('');
    setMode('visio');
    originalDurationRef.current = null; // Reset original duration when speciality changes
    originalPriceRef.current = null; // Reset original price when speciality changes
    isPriceManuallyChangedRef.current = false; // Reset manual change flag
    lastFetchParamsRef.current = null; // Reset last fetch params when speciality changes
    originalAppointmentTypeIdRef.current = null; // Reset original appointment type ID when speciality changes
    dispatch(clearSelectedCustomPrice()); // Clear custom price when speciality changes
  };

  // Handle custom duration increase (15 minutes, max 6 hours)
  const handleDurationIncrease = () => {
    const currentDuration = typeof duration === 'number' ? duration : parseDuration(duration);
    const newDuration = Math.min(360, currentDuration + 15); // Max 6 hours (360 minutes)
    setDuration(newDuration);
    if (selectedSlot) {
      const start = new Date(selectedSlot.start);
      const newEnd = new Date(start.getTime() + newDuration * 60 * 1000);
      setSelectedSlot({ start: selectedSlot.start, end: newEnd.toISOString() });
    }
  };

  // Handle custom duration decrease (15 minutes, min 15 minutes)
  const handleDurationDecrease = () => {
    const currentDuration = typeof duration === 'number' ? duration : parseDuration(duration);
    const newDuration = Math.max(15, currentDuration - 15); // Min 15 minutes
    setDuration(newDuration);
    if (selectedSlot) {
      const start = new Date(selectedSlot.start);
      const newEnd = new Date(start.getTime() + newDuration * 60 * 1000);
      setSelectedSlot({ start: selectedSlot.start, end: newEnd.toISOString() });
    }
  };

  // Handle mode change in custom mode
  const handleModeChange = (newMode) => {
    setMode(newMode);
  };

  // 30 Sep 2026: when video is not offered (no `secured_video` on the doctor's
  // plan, or the hospital's video switch off) a NEW booking must not sit on
  // video with the toggle disabled — nothing could be selected and the save was
  // refused. Fall back to in-person ('visio' is the in-person value of `mode`).
  // An existing appointment keeps its stored mode: changing it silently on an
  // edit would rewrite a visit the desk did not touch.
  useEffect(() => {
    if (!videoAllowed && !selectedAppointment && mode === 'Online') setMode('visio');
  }, [videoAllowed, selectedAppointment, mode]);

  // Maximum length constants
  const MAX_PATIENT_SEARCH_LENGTH = 50;
  const MAX_COMMUNICATION_LENGTH = 500;
  const MAX_PRICE = 999999;
  const MIN_PRICE = 0;

  // Validate patient search input
  const validatePatientSearch = (value) => {
    // Remove any potentially dangerous characters
    const sanitized = value.replace(/[<>]/g, '');

    // Check length
    if (sanitized.length > MAX_PATIENT_SEARCH_LENGTH) {
      return t('appointmentmodal.search_max_length', { max: MAX_PATIENT_SEARCH_LENGTH });
    }

    // Check for only whitespace
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
      return t('appointmentmodal.search_only_spaces');
    }

    return '';
  };

  // Validate price input (only when custom selection is enabled and price is editable)
  const validatePrice = (value, isRequired = false) => {
    // If value is empty and not required, it's valid (will use default)
    if ((value === '' || value === null || value === undefined) && !isRequired) {
      return '';
    }

    if (value === '' || value === null || value === undefined) {
      return t('appointmentmodal.price_required');
    }

    const numValue = parseInt(value, 10);

    if (isNaN(numValue)) {
      return t('appointmentmodal.price_invalid');
    }

    if (numValue < MIN_PRICE) {
      return t('appointmentmodal.price_min', { min: MIN_PRICE });
    }

    if (numValue > MAX_PRICE) {
      return t('appointmentmodal.price_max', { max: MAX_PRICE });
    }

    return '';
  };

  // Validate communication text to prevent more than 2 consecutive special characters
  const validateCommunicationText = (text) => {
    // Remove any potentially dangerous characters
    const sanitized = text.replace(/[<>]/g, '');

    // Check length
    if (sanitized.length > MAX_COMMUNICATION_LENGTH) {
      return t('appointmentmodal.communication_max_length', { max: MAX_COMMUNICATION_LENGTH });
    }

    // Regular expression to match 3 or more consecutive special characters
    const consecutiveSpecialCharsRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{3,}/;

    // Check if the text contains 3 or more consecutive special characters
    if (consecutiveSpecialCharsRegex.test(sanitized)) {
      return t('appointmentmodal.communication_consecutive_special');
    }

    return '';
  };

  // Handle patient search change
  const handlePatientSearchChange = (e) => {
    const value = e.target.value;

    // Clear error if user is typing
    if (patientSearchError && value.length < patientSearch.length) {
      setPatientSearchError('');
    }

    // Validate input
    const error = validatePatientSearch(value);
    if (error) {
      setPatientSearchError(error);
    } else {
      // Sanitize input - remove dangerous characters
      const sanitized = value.replace(/[<>]/g, '');
      // Enforce max length
      const finalValue = sanitized.length > MAX_PATIENT_SEARCH_LENGTH
        ? sanitized.substring(0, MAX_PATIENT_SEARCH_LENGTH)
        : sanitized;
      setPatientSearch(finalValue);
      setPatientSearchError('');
    }
  };

  // Handle price change in custom mode
  const handlePriceChange = (e) => {
    const newPrice = e.target.value.replace(/[^0-9]/g, '');

    // Clear error if user is typing
    if (priceError && newPrice.length < price.length) {
      setPriceError('');
    }

    // Validate price (required when custom selection is enabled)
    const error = validatePrice(newPrice, true);
    setPrice(newPrice);
    if (error) {
      setPriceError(error);
    } else {
      setPriceError('');

      // Track if price was manually changed by comparing with original
      if (originalPriceRef.current !== null) {
        const originalPriceNum = typeof originalPriceRef.current === 'number'
          ? originalPriceRef.current
          : parseFloat(originalPriceRef.current) || 0;
        const newPriceNum = newPrice === '' ? 0 : parseFloat(newPrice) || 0;
        isPriceManuallyChangedRef.current = newPriceNum !== originalPriceNum;
      } else {
        // If no original price set yet, mark as manually changed
        isPriceManuallyChangedRef.current = true;
      }
    }
  };

  const handleCommunicationChange = (e) => {
    const newValue = e.target.value;

    // Clear error if user is typing
    if (communicationError && newValue.length < communication.length) {
      setCommunicationError('');
    }

    // Validate the new text
    const error = validateCommunicationText(newValue);
    if (error) {
      setCommunicationError(error);
    } else {
      // Sanitize input - remove dangerous characters
      const sanitized = newValue.replace(/[<>]/g, '');
      // Enforce max length
      const finalValue = sanitized.length > MAX_COMMUNICATION_LENGTH
        ? sanitized.substring(0, MAX_COMMUNICATION_LENGTH)
        : sanitized;
      setCommunication(finalValue);
      setCommunicationError('');
    }
  };

  const handleMissedOut = async () => {
    if (selectedAppointment) {
      const patientDisplayName = patientNameList.find((p) => p.patientId === patient)?.name ||
        `${selectedAppointment.patientName} ${selectedAppointment.patientSurname}`;
      const result = await Swal.fire({
        title: t('appointmentmodal.confirm_missed_title'),
        text: t('appointmentmodal.confirm_missed_text', { patient: patientDisplayName, time: formatTime(selectedAppointment.localTime) }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#9ca3af',
        confirmButtonText: t('appointmentmodal.missed_out_button', 'No Show'),
      });

      if (result.isConfirmed) {
        try {
          const actionResult = await dispatch(
            updateAppointment({
              appointmentId: selectedAppointment.appointmentId,
              method: '',
              paymentStatus: '',
              date: '',
              appointmentStatus: 'NoShow',
              start: dateRangeStart,
              end: dateRangeEnd,
            })
          );

          if (actionResult.meta.requestStatus === 'fulfilled') {
            handleCloseModal();
            toast.success(t('appointmentmodal.missed_success_text'));
          } else {
            throw new Error(extractErrorMessage(actionResult.payload, t('appointmentmodal.missed_error_text'), i18n.language));
          }
        } catch (err) {
          // Error toast handled centrally via Appointment.jsx appointmenterror useEffect
          console.error('Missed out error:', err);
        }
      }
    }
  };

  const handleSaveAppointment = async (e) => {
    e.preventDefault();
    // Set when a policy-mandated OTP is skipped at the desk (case C only).
    let bookAsPending = false;

    // Validate patient selection only if "My patient" or "Others" is selected
    if ((patientType === 'myPatient' || patientType === 'others') && !patient.trim()) {
      toast.warn(t('appointmentmodal.validation_patient_error'));
      return;
    }

    // Validate new patient fields if "New" is selected — but ONLY when a new
    // patient is actually being created. A FOUND record (typed identifier
    // matched here, or a consent link just completed) books by patientId with
    // isNewPatient:false — gender, DOB and the rest are never sent — so
    // demanding them here blocked every booking for a patient whose
    // self-registered profile is thin (home-page registration collects name +
    // contact + password, no gender or DOB). Reported from a live screen,
    // 2026-09-01: linked at the desk, then "Gender is required" forever.
    // The contact belongs to a household and nobody has been chosen yet:
    // booking "the" account is exactly the silent mis-booking this picker
    // exists to stop, so the answer is owed before anything else is checked.
    if (patientType === 'new' && household && !householdPick) {
      toast.warn(t('appointmentmodal.household.choose_required', 'Choose who this appointment is for.'));
      return;
    }
    if (patientType === 'new' && !(isPatientFound && foundPatientId)) {
      if (!validateNewPatientFields()) {
        // Don't show sweet alert, errors are shown below fields
        return;
      }
      // Check email existence (validation already includes this, but double-check for safety)
      if (newPatientData.email.trim() && emailStatus === false) {
        setNewPatientErrors(prev => ({ ...prev, email: blockMessage('email') }));
        return;
      }
      if (newPatientData.email.trim() && emailStatus === null && !emailBusy) {
        setNewPatientErrors(prev => ({ ...prev, email: t('appointmentmodal.verify_email_address') }));
        return;
      }
      // Policy-mandated OTPs may be SKIPPED at the desk (case C only — the
      // patient may not be reachable right now): confirm explicitly, then the
      // backend books and creates the account pending verification.
      const hasNewEmail = !!newPatientData.email.trim();
      const hasNewPhone = !!newPatientData.phone.trim();
      // Under 'either' ONE verified contact settles it — the second one may be
      // typed and left unverified without turning the booking pending. Every
      // other mode is unchanged (utils/loginPolicy#otpVerificationPending).
      // CONNECTED: nothing to confirm — an unverified contact is the normal
      // case, and the hint under the contact fields already says the patient
      // verifies at their first sign-in. It is still told to the server
      // (allowPending) whenever a given contact has no proof, so a backend
      // that reads the hospital's login policy books it pending, not refused.
      const otpStillOwed = !isPatientFound && !connected && otpVerificationPending(loginPolicy, {
        hasEmail: hasNewEmail,
        hasPhone: hasNewPhone,
        emailVerified: !!emailProof,
        phoneVerified: !!phoneProof,
      });
      if (connected && !isPatientFound
        && ((hasNewEmail && !emailProof) || (hasNewPhone && !phoneProof))) {
        bookAsPending = true;
      }
      if (otpStillOwed) {
        const pendingConfirm = await Swal.fire({
          title: t('appointmentmodal.pending_verification_title', 'Verify later?'),
          text: t('appointmentmodal.pending_verification_text', 'Not all required contact details were verified with an OTP. The appointment will be booked and the account created, but the patient must verify with a one-time code at their first sign-in.'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: t('appointmentmodal.pending_verification_confirm', 'Book as pending'),
          cancelButtonText: t('appointmentmodal.cancel_button', 'Cancel'),
          confirmButtonColor: '#4ABDBD',
          cancelButtonColor: '#e5e7eb',
        });
        if (!pendingConfirm.isConfirmed) return;
        bookAsPending = true;
      }
    }
    if (!speciality) {
      toast.warn(t('appointmentmodal.validation_speciality_error'));
      return;
    }
    if (!appointmentType) {
      toast.warn(t('appointmentmodal.validation_appointment_type_error'));
      return;
    }

    // Validate price if custom selection is enabled and price is editable
    if (isCustomSelection && (userRole === 'doctor' || userRole === 'admin') && !isRescheduling) {
      const priceValidationError = validatePrice(price, true); // Price is required when custom selection is enabled
      if (priceValidationError) {
        setPriceError(priceValidationError);
        toast.warn(priceValidationError);
        return;
      }
    }

    // Validate communication
    const communicationValidationError = validateCommunicationText(communication);
    if (communicationValidationError) {
      setCommunicationError(communicationValidationError);
      toast.warn(communicationValidationError);
      return;
    }

    // Validate payment (only for new bookings — matches when PaymentMethodSelect is shown)
    if (!isEditMode && !isRescheduling) {
      // `unavailable` means /payment/options ANSWERED with an empty list — the
      // hospital disabled the method, the doctor accepts none, a platform hold is
      // on, or the plan lacks it. Booking anyway would write a payment record the
      // doctor never agreed to, so refuse instead of inventing a method.
      if (payment.unavailable) {
        toast.warn(t('appointmentmodal.validation_no_payment_method_accepted', 'This doctor accepts no payment method yet — set one up before booking.'));
        return;
      }
      if (!payment.method) {
        toast.warn(t('appointmentmodal.validation_payment_method_error', 'Please choose a payment method'));
        return;
      }
      if (payment.timing === 'prepaid' && payment.method === 'cash') {
        toast.warn(t('appointmentmodal.validation_prepaid_not_cash_error', 'Cash is not available for advance (online) payment'));
        return;
      }
    }

    // Check if patient exists in patientNameList, allPatientsList, or selectedPatient from Redux
    let selectedPatientObj = null;
    if (patientType === 'myPatient' || patientType === 'others') {
      if (patientType === 'others') {
        // For "Others", check allPatientsList
        selectedPatientObj = allPatientsList.find((p) => p.patientId === patient);
        if (selectedPatientObj) {
          selectedPatientObj = {
            patientId: selectedPatientObj.patientId,
            name: `${selectedPatientObj.name || ''} ${selectedPatientObj.surname || ''}`.trim() || selectedPatientObj.name || t('appointmentmodal.patient', 'Patient')
          };
        }
      } else {
        // For "My patient", check patientNameList
        selectedPatientObj = patientNameList.find((p) => p.patientId === patient);

        // If not found in patientNameList, check selectedPatient from Redux (for BookAppointmentModal flow)
        if (!selectedPatientObj && selectedPatient?.patientId === patient) {
          selectedPatientObj = {
            patientId: selectedPatient.patientId,
            name: `${selectedPatient.name || ''} ${selectedPatient.surname || ''}`.trim() || selectedPatient.name || t('appointmentmodal.patient', 'Patient')
          };
        }
      }

      // Fallback to selectedAppointment data in edit/reschedule mode when patient isn't in any list
      if (!selectedPatientObj && selectedAppointment?.patientId === patient) {
        selectedPatientObj = {
          patientId: selectedAppointment.patientId,
          name: `${selectedAppointment.patientName || ''} ${selectedAppointment.patientSurname || ''}`.trim() || t('appointmentmodal.patient', 'Patient')
        };
      }

      if (!selectedPatientObj) {
        toast.warn(t('appointmentmodal.validation_invalid_patient_error'));
        return;
      }
    }

    const doctorId = selectedDoctor.doctorId;
    const patientId = (patientType === 'myPatient' || patientType === 'others') ? patient : null; // Set patientId for "My patient" and "Others"

    const selectedApptType = appointmentTypes.find((appt) => appt.type === appointmentType);
    if (!selectedApptType) {
      toast.warn(t('appointmentmodal.validation_invalid_appointment_type_error'));
      return;
    }

    // Extract date and time using browser local time (what user sees in UI)
    const date = formatDateForPayload(selectedSlot.start);
    const time = formatTimeForPayload(selectedSlot.start);
    const slotEndTime = formatTimeForPayload(selectedSlot.end);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const appointmentMode = mode === 'visio' ? 'onsite' : 'visio';

    // The programme question, when it was asked (a new booking for a patient
    // who already exists): answered, inside the stop's window for a programme
    // visit, a reason for an emergency.
    const programmeBody = !isEditMode && !isRescheduling ? programmeChoiceBody(programmeChoice) : null;
    if (!isEditMode && !isRescheduling) {
      const programmeProblem = programmeChoiceProblem(programmeChoice, date);
      if (programmeProblem) {
        setProgrammeShowErrors(true);
        toast.warn(t(programmeProblem));
        return;
      }
    }

    // Ensure duration is in minutes (number) before formatting
    // Duration can be a number (from custom selection) or already formatted string (from appointment type)
    let durationInMinutes;
    if (typeof duration === 'number') {
      durationInMinutes = duration;
    } else if (typeof duration === 'string' && duration.includes(':')) {
      durationInMinutes = parseDuration(duration);
    } else {
      durationInMinutes = parseInt(duration, 10) || 60;
    }
    const formattedDuration = formatDuration(durationInMinutes);

    // Determine the price to use in payload:
    // Priority:
    // 1. If doctor manually edited the price, always use the state price (overrides custom price API)
    // 2. If custom price from API exists and price wasn't manually edited, use it
    // 3. Otherwise, use the price from state
    // 4. If price is empty, use default from appointment type
    let finalPrice;
    const priceFromState = (() => {
      if (price === '' || price === null || price === undefined) {
        return selectedApptType?.price || 0;
      } else if (typeof price === 'string') {
        const parsed = parseFloat(price);
        return isNaN(parsed) ? (selectedApptType?.price || 0) : parsed;
      } else if (typeof price === 'number') {
        return price;
      } else {
        return selectedApptType?.price || 0;
      }
    })();

    if (isPriceManuallyChangedRef.current) {
      // Doctor manually edited the price — always honor that over the patient's custom price
      finalPrice = priceFromState;
    } else if (selectedCustomPrice?.customPrice !== undefined && selectedCustomPrice?.customPrice !== null) {
      // Use custom price if available and not manually overridden
      finalPrice = typeof selectedCustomPrice.customPrice === 'number'
        ? selectedCustomPrice.customPrice
        : parseFloat(selectedCustomPrice.customPrice) || 0;
    } else {
      finalPrice = priceFromState;
    }

    // Determine priceType based on the source:
    // Type 1: Price comes from appointmenttype (default, not from custom price API, not manually changed)
    // Type 2: Price comes from customprice API response (selectedCustomPrice exists and price matches it)
    // Type 3: Doctor manually edited the price (regardless of source)
    let priceType = 'Type 1'; // Default to appointment type price

    // First check if price was manually changed - if so, it's always Type 3
    if (isPriceManuallyChangedRef.current) {
      priceType = 'Type 3'; // Doctor manually edited the price
    } else if (selectedCustomPrice?.customPrice !== undefined && selectedCustomPrice?.customPrice !== null) {
      // If custom price exists and price wasn't manually changed, check if it matches custom price
      const customPriceValue = typeof selectedCustomPrice.customPrice === 'number'
        ? selectedCustomPrice.customPrice
        : parseFloat(selectedCustomPrice.customPrice) || 0;

      // If final price matches custom price from API, it's Type 2
      if (finalPrice === customPriceValue) {
        priceType = 'Type 2'; // Using custom price from API
      } else {
        // If custom price exists but final price doesn't match (shouldn't happen without manual change, but fallback)
        priceType = 'Type 1'; // Fallback to appointment type price
      }
    } else {
      // No custom price and no manual change - using appointment type price
      priceType = 'Type 1'; // Using appointment type price (default)
    }

    // Build payload based on patient type
    let payload;

    if (patientType === 'new') {
      if (isPatientFound) {
        // Found an existing patient via email - book as existing
        payload = {
          doctorId,
          patientId: foundPatientId,
          SpecialtyId: selectedApptType.specialtyId,
          appointmentTypeId: selectedApptType.appointmentTypeId,
          date,
          time,
          slotEndTime,
          duration: formattedDuration,
          appointmentMode,
          // 30 Sep 2026: this payload goes to the EXISTING-patient route
          // (bookAppointment), which reads the site from `addressType` only —
          // `addressHostId` is the new-patient route's name. Without it the
          // booked visit had no address. Both are sent; each route reads its own.
          addressType: addressTypeId,
          addressHostId: addressTypeId,
          timeZone,
          description: communication || '',
          doctorDescription: notes || null,
          price: finalPrice,
          pricetype: priceType,
          paymentMethod: payment.method || undefined,
          paymentTiming: payment.timing || undefined,
          isNewPatient: false,
          ...(programmeBody ? { programme: programmeBody } : {}),
          start: dateRangeStart,
          end: dateRangeEnd,
        };
      } else {
        // New patient payload structure - NO patientId, includes newPatientData
        payload = {
          doctorId,
          SpecialtyId: selectedApptType.specialtyId,
          appointmentTypeId: selectedApptType.appointmentTypeId,
          date,
          time,
          slotEndTime,
          duration: formattedDuration,
          appointmentMode,
          addressHostId: addressTypeId,
          timeZone,
          description: communication || '',
          doctorDescription: notes || null,
          price: finalPrice,
          pricetype: priceType,
          paymentMethod: payment.method || undefined,
          paymentTiming: payment.timing || undefined,
          isNewPatient: true,
          newPatientData: {
            name: newPatientData.name.trim(),
            surname: newPatientData.surname?.trim() || '',
            email: newPatientData.email.trim(),
            phone: newPatientData.phone.trim(),
            dob: newPatientData.dob,
            gender: newPatientData.gender,
            countryId: newPatientData.countryId,
            timeZone: newPatientData.timeZone,
            // Server-side proof only — the old verified booleans are ignored
            // by the backend and no longer sent.
            ...(emailProof && { emailProof }),
            ...(phoneProof && { phoneProof }),
            ...(bookAsPending && { allowPending: true }),
          },
          // Contract-level copies: bookNew may read proofs/allowPending from
          // the request root rather than newPatientData.
          ...(emailProof && { emailProof }),
          ...(phoneProof && { phoneProof }),
          ...(bookAsPending && { allowPending: true }),
          start: dateRangeStart,
          end: dateRangeEnd,
        };
      }
    } else {
      // Existing patient payload structure
      payload = {
        appointmentId: selectedAppointment?.appointmentId,
        patientId,
        doctorId,
        ...(isEditMode || isRescheduling
          ? {
            newDate: date,
            newTime: time,
            newSlotEndTime: slotEndTime,
          }
          : {
            date,
            time,
            slotEndTime,
          }),
        timeZone,
        appointmentTypeId: selectedApptType.appointmentTypeId,
        description: communication || '',
        doctorDescription: notes || null,
        appointmentMode,
        SpecialtyId: selectedApptType.specialtyId,
        addressType: addressTypeId,
        duration: formattedDuration,
        price: finalPrice,
        pricetype: priceType,
        paymentMethod: payment.method || undefined,
        paymentTiming: payment.timing || undefined,
        ...(programmeBody ? { programme: programmeBody } : {}),
        start: dateRangeStart,
        end: dateRangeEnd,
      };
    }

    // Debug: Log payload to verify custom price is included
    if (isCustomSelection) {
      console.log('Payload with custom price:', JSON.stringify(payload, null, 2));
    }

    // Debug: Log payload for new patient to verify all data is included
    if (patientType === 'new') {
      console.log('New Patient Payload:', JSON.stringify(payload, null, 2));
    }

    const patientDisplayName = patientType === 'new'
      ? newPatientData.name.trim()
      : selectedPatientObj?.name || t('appointmentmodal.patient_fallback');

    if (!isEditMode && !isRescheduling) {
      const result = await Swal.fire({
        title: t('appointmentmodal.confirm_create_title'),
        text: t('appointmentmodal.confirm_create_text', { patient: patientDisplayName, time: formatTime(selectedSlot.start) }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4ABDBD',
        cancelButtonColor: '#e5e7eb',
        confirmButtonText: t('appointmentmodal.create_button', 'Create'),
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    if (isEditMode) {
      const result = await Swal.fire({
        title: t('appointmentmodal.confirm_update_title'),
        text: t('appointmentmodal.confirm_update_text', { patient: patientDisplayName, time: formatTime(selectedSlot.start) }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4ABDBD',
        cancelButtonColor: '#e5e7eb',
        confirmButtonText: t('appointmentmodal.save_button', 'Save'),
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    try {
      let result;
      setSaveRefusal('');
      setProgrammeRefusal('');
      if (isEditMode || isRescheduling) {
        result = await dispatch(saveAppointmentUpdate({
          appointmentId: selectedAppointment?.appointmentId,
          payload,
          isNewPatient: false,
        }));
      } else if (patientType === 'new' && !isPatientFound) {
        result = await dispatch(bookWitthNewPatient(payload));
      } else {
        result = await dispatch(bookAppointment(payload));
      }

      if (result.meta.requestStatus === 'fulfilled') {
        handleCloseModal();

        if (!isEditMode && !isRescheduling) {
          toast.success(t('appointmentmodal.create_success_text'));
          // 30 Sep 2026: both booking routes (book / bookNew) answer
          // `meetingLinkPending: true` when Mitra One Care Meet was down and no
          // room was made. The visit IS booked, so it stays a success — but the
          // desk is told, instead of finding no Join button on the day. The
          // room is NOT made automatically later (the server only mints it on
          // the "Video room not ready — create it" button), so say that.
          if (result.payload?.meetingLinkPending) {
            toast.warn(t('appointmentmodal.meeting_link_pending_booked', 'Booked. The video room could not be created yet — open the appointment and create it once the video service is back.'));
          }
        } else if (isEditMode) {
          toast.success(t('appointmentmodal.update_success_text'));
        } else if (isRescheduling) {
          toast.success(t('appointmentmodal.reschedule_success_text'));
        }
        // Refresh the "My Patients" selector list so a just-booked patient — a
        // newly created one, or an existing/"all" patient now linked to this
        // doctor — appears the next time the modal opens. bookAppointment
        // (existing/all-patient path) doesn't refresh this list on its own.
        if (selectedDoctor?.doctorId) {
          dispatch(getPatienttsNameList(selectedDoctor.doctorId));
        }

        // Call the callback to close parent modal and refresh library
        if (onAppointmentBooked) {
          onAppointmentBooked();
        }
      } else {
        // Login-policy 400s (FIELD_REQUIRED / OTP_PROOF_REQUIRED) land on the
        // field itself, translated; anything else keeps the central toast.
        const rp = result.payload;
        const errData = rp && typeof rp.data === 'object' && rp.data ? rp.data : rp;

        // The server refused the inline create: the contact belongs to a
        // Mitra One Care profile, and a connected hospital never mints a
        // second person for a human who already exists. Open the same consent
        // popup the debounced lookup opens; booking continues from its
        // onLinked with the patientId this hospital was just allocated.
        if (errData?.code === 'MOC_CONSENT_REQUIRED') {
          const source = errData.matchedOn === 'phone' ? 'phone' : 'email';
          const lookupAction = await dispatch(lookupMocLink(
            source === 'phone'
              ? { phone: newPatientData.phone.trim(), doctorId: selectedDoctor?.doctorId }
              : { email: newPatientData.email.trim(), doctorId: selectedDoctor?.doctorId }
          ));
          if (lookupMocLink.fulfilled.match(lookupAction)) {
            openMocPopup(source, lookupAction.payload);
            return;
          }
          toast.info(typeof errData.message === 'string' ? errData.message : t('appointmentmodal.moc_consent_required', 'This contact already belongs to a Mitra One Care profile — ask the patient to confirm before adding them here.'));
          return;
        }

        const policyMsg = policyErrorMessage(errData, t, i18n.language, loginPolicy);
        if (policyMsg) {
          setNewPatientErrors(prev => ({ ...prev, [policyErrorField(errData.field)]: policyMsg }));
          toast.warn(policyMsg);
          return;
        }
        const refusal = extractErrorMessage(result.payload, t('appointmentmodal.error_text'), i18n.language);
        setSaveRefusal(refusal);
        // A programme refusal (PROGRAMME_NOT_ACTIVE / _OTHER_DOCTOR /
        // _STOP_TAKEN / _OUTSIDE_WINDOW / _REASON_REQUIRED) is also said on the
        // programme question itself, where it can be answered.
        if (typeof errData?.code === 'string' && errData.code.startsWith('PROGRAMME_')) {
          setProgrammeRefusal(extractErrorMessage(errData, refusal, i18n.language));
        }
        throw new Error(refusal);
      }
    } catch (err) {
      // Error toast handled centrally via Appointment.jsx appointmenterror useEffect
      console.error('Save appointment error:', err);
    }
  };

  const handleOpenAppointments = async () => {
    if (isOpenAppointmentLoading) return;
    const patientId = selectedAppointment?.patientId;
    if (!patientId) {
      console.error("Cannot open appointment: patientId missing on selectedAppointment");
      return;
    }
    setIsOpenAppointmentLoading(true);
    try {
      const result = await dispatch(getPatientById(patientId));

      if (getPatientById.fulfilled.match(result)) {
        setisModalOpenAppointment(true);
        setAppoId(selectedAppointment);
        handleCloseModal();
      } else {
        console.error("Failed to fetch patient data");
      }
    } catch (error) {
      console.error("Error fetching patient:", error);
    } finally {
      setIsOpenAppointmentLoading(false);
    }
  };

  const handleManualReschedule = () => {
    if (!patient.trim()) {
      toast.warn(t('appointmentmodal.validation_patient_error'));
      return;
    }

    const selectedApptType = appointmentTypes.find((appt) => appt.type === appointmentType);

    let finalPrice;
    const priceFromState = (() => {
      if (price === '' || price === null || price === undefined) {
        return selectedApptType?.price || 0;
      } else if (typeof price === 'string') {
        const parsed = parseFloat(price);
        return isNaN(parsed) ? (selectedApptType?.price || 0) : parsed;
      } else if (typeof price === 'number') {
        return price;
      } else {
        return selectedApptType?.price || 0;
      }
    })();

    if (isPriceManuallyChangedRef.current) {
      // Doctor manually edited the price — always honor that over the patient's custom price
      finalPrice = priceFromState;
    } else if (selectedCustomPrice?.customPrice !== undefined && selectedCustomPrice?.customPrice !== null) {
      finalPrice = typeof selectedCustomPrice.customPrice === 'number'
        ? selectedCustomPrice.customPrice
        : parseFloat(selectedCustomPrice.customPrice) || 0;
    } else {
      finalPrice = priceFromState;
    }

    const payload = {
      appointmentId: selectedAppointment?.appointmentId,
      patientId: patient,
      doctorId: selectedDoctor.doctorId,
      appointmentTypeId: selectedApptType?.appointmentTypeId || selectedAppointment?.appointmentType,
      SpecialtyId: selectedApptType?.specialtyId || selectedAppointment?.SpecialtyId,
      addressType: addressTypeId || selectedAppointment?.addressType,
      duration: formatDuration(typeof duration === 'number' ? duration : parseDuration(duration)),
      price: finalPrice,
      pricetype: currentPriceType,
      appointmentMode: mode === 'visio' ? 'onsite' : 'visio',
      description: communication || '',
      doctorDescription: notes || null,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    setManualReschedulePayload(payload);
    setIsRescheduleDropdownOpen(false);
    setIsManualRescheduleMode(true);
    setSelectedRecommendationSlots([]);
    setWeekOffset(0);
    setOpenDay(null);

    // Get addressTypeId - prioritize selectedAppointment addressType
    let currentAddressTypeId = selectedAppointment?.addressType || addressTypeId;

    // Try to get from first available day if no addressType found
    if (!currentAddressTypeId) {
      const firstAvailability = selectedDoctor?.availability?.find(avail => avail.addressType);
      if (firstAvailability?.addressType) {
        currentAddressTypeId = firstAvailability.addressType;
      } else {
        toast.warn(t('appointmentmodal.unable_to_determine_address'));
        return;
      }
    }

    // Immediately fetch available slots when button is clicked
    const currentWeekMonday = getCurrentWeekMonday(today);
    const startDate = format(addDays(currentWeekMonday, 0), 'yyyy-MM-dd');
    const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');
    const resolvedAppointmentTypeId = selectedApptType?.appointmentTypeId || selectedAppointment?.appointmentType;

    // Create fetch key to track this call (same format as useEffect)
    const fetchKey = `${selectedDoctor.doctorId}-${startDate}-${endDate}-${currentAddressTypeId}-${resolvedAppointmentTypeId}`;
    lastFetchParamsRef.current = fetchKey; // Set the ref to prevent duplicate call in useEffect

    dispatch(getDoctorAvailableSlots({
      doctorId: selectedDoctor.doctorId,
      startDate,
      endDate,
      timeZone: systemTimeZone,
      addressType: currentAddressTypeId,
      appointmentTypeId: resolvedAppointmentTypeId,
      duration: typeof duration === 'number' ? duration : parseDuration(duration),
      isReschedulingProcess: true,
      appointmentId: selectedAppointment?.appointmentId,
    }));

    scrollToBottom();
  };

  const handleReschedule = async () => {
    if (!patient.trim()) {
      toast.warn(t('appointmentmodal.validation_patient_error'));
      return;
    }
    if (!speciality) {
      toast.warn(t('appointmentmodal.validation_speciality_error'));
      return;
    }
    if (!appointmentType) {
      toast.warn(t('appointmentmodal.validation_appointment_type_error'));
      return;
    }

    // Check if patient exists in patientNameList or selectedPatient from Redux
    let selectedPatientObj = patientNameList.find((p) => p.patientId === patient);

    // If not found in patientNameList, check selectedPatient from Redux (for BookAppointmentModal flow)
    if (!selectedPatientObj && selectedPatient?.patientId === patient) {
      selectedPatientObj = {
        patientId: selectedPatient.patientId,
        name: `${selectedPatient.name || ''} ${selectedPatient.surname || ''}`.trim() || selectedPatient.name || t('appointmentmodal.patient', 'Patient')
      };
    }

    // Fallback to selectedAppointment data in edit/reschedule mode when patient isn't in any list
    if (!selectedPatientObj && selectedAppointment?.patientId === patient) {
      selectedPatientObj = {
        patientId: selectedAppointment.patientId,
        name: `${selectedAppointment.patientName || ''} ${selectedAppointment.patientSurname || ''}`.trim() || t('appointmentmodal.patient', 'Patient')
      };
    }

    if (!selectedPatientObj) {
      toast.warn(t('appointmentmodal.validation_invalid_patient_error'));
      return;
    }

    const selectedApptType = appointmentTypes.find((appt) => appt.type === appointmentType);
    if (!selectedApptType) {
      toast.warn(t('appointmentmodal.validation_invalid_appointment_type_error'));
      return;
    }

    // Get addressTypeId - prioritize selectedAppointment addressType
    let currentAddressTypeId = selectedAppointment?.addressType || addressTypeId;

    // Try to get from first available day if no addressType found
    if (!currentAddressTypeId) {
      const firstAvailability = selectedDoctor?.availability?.find(avail => avail.addressType);
      if (firstAvailability?.addressType) {
        currentAddressTypeId = firstAvailability.addressType;
      } else {
        toast.warn(t('appointmentmodal.unable_to_determine_address'));
        return;
      }
    }

    // Enable reschedule recommendation mode
    setIsRescheduleRecommendationMode(true);
    setSelectedRecommendationSlots([]);
    setWeekOffset(0);
    setOpenDay(null);

    // Immediately fetch available slots when button is clicked
    const currentWeekMonday = getCurrentWeekMonday(today);
    const startDate = format(addDays(currentWeekMonday, 0), 'yyyy-MM-dd');
    const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

    // Create fetch key to track this call (same format as useEffect)
    const fetchKey = `${selectedDoctor.doctorId}-${startDate}-${endDate}-${currentAddressTypeId}-${selectedApptType.appointmentTypeId}`;
    lastFetchParamsRef.current = fetchKey; // Set the ref to prevent duplicate call in useEffect

    console.log('AppointmentModal: Fetching available slots on button click', {
      doctorId: selectedDoctor.doctorId,
      startDate,
      endDate,
      timeZone: systemTimeZone,
      addressType: currentAddressTypeId,
      appointmentTypeId: selectedApptType.appointmentTypeId,
    });

    dispatch(getDoctorAvailableSlots({
      doctorId: selectedDoctor.doctorId,
      startDate,
      endDate,
      timeZone: systemTimeZone,
      addressType: currentAddressTypeId,
      appointmentTypeId: selectedApptType.appointmentTypeId,
      duration: typeof duration === 'number' ? duration : parseDuration(duration),
      isReschedulingProcess: true,
      appointmentId: selectedAppointment?.appointmentId,
    }));

    // Scroll to bottom to show the recommendation section
    scrollToBottom();
  };

  // Fetch available slots when in reschedule recommendation mode
  useEffect(() => {
    console.log('AppointmentModal: useEffect triggered', {
      isRescheduleRecommendationMode,
      speciality,
      appointmentType,
      doctorId: selectedDoctor?.doctorId,
      weekOffset,
      lastFetchParamsRef: lastFetchParamsRef.current
    });

    if ((isRescheduleRecommendationMode || isManualRescheduleMode) && speciality && appointmentType && selectedDoctor?.doctorId) {
      // Use the already computed appointmentTypes or find from selectedDoctor
      let selectedApptType = appointmentTypes.find((appt) => appt.type === appointmentType);

      // If not found in appointmentTypes, try to find from selectedDoctor directly
      if (!selectedApptType) {
        const selectedSpecialityData = selectedDoctor?.appointmentSpeciality?.find(
          (s) => s.name === speciality
        );
        const rawApptType = selectedSpecialityData?.appointmentTypes?.find(
          (appt) => appt.type === appointmentType
        );

        if (rawApptType) {
          // Map the raw data to match the structure
          selectedApptType = {
            type: rawApptType.type,
            appointmentTypeId: rawApptType.typeId, // Note: raw data uses typeId
            specialtyId: rawApptType.specialtyId,
            duration: parseDuration(rawApptType.duration),
            price: parseInt(rawApptType.price, 10),
            virtual: rawApptType.virtual,
          };
        }
      }

      if (!selectedApptType?.appointmentTypeId) {
        console.warn('AppointmentModal: Cannot find appointmentTypeId for', {
          speciality,
          appointmentType,
          appointmentTypes: appointmentTypes.length,
          selectedDoctorSpecialities: selectedDoctor?.appointmentSpeciality?.length
        });
        return;
      }

      // Get addressTypeId - prioritize selectedAppointment addressType in reschedule recommendation mode
      let currentAddressTypeId = selectedAppointment?.addressType || addressTypeId;

      // Try to get from first available day if no addressType found
      if (!currentAddressTypeId) {
        console.warn('AppointmentModal: addressTypeId is missing, trying to find from availability', {
          selectedAppointment: selectedAppointment?.addressType,
          addressTypeId,
          clickedDay,
          dayAvailability: dayAvailability?.addressType
        });
        const firstAvailability = selectedDoctor?.availability?.find(avail => avail.addressType);
        if (firstAvailability?.addressType) {
          currentAddressTypeId = firstAvailability.addressType;
          console.log('AppointmentModal: Using first available addressType', currentAddressTypeId);
        } else {
          console.error('AppointmentModal: No addressType found, cannot fetch slots');
          return;
        }
      }

      // Get the Monday of the current week
      const currentWeekMonday = getCurrentWeekMonday(today);

      // Calculate start date (Monday of the target week)
      const startDate = format(addDays(currentWeekMonday, weekOffset * 7), 'yyyy-MM-dd');

      // Calculate end date (Sunday of the target week - 6 days after Monday)
      const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

      // Use the determined addressTypeId
      const finalAddressTypeId = currentAddressTypeId;

      // Create a unique key for this fetch request
      const fetchKey = `${selectedDoctor.doctorId}-${startDate}-${endDate}-${finalAddressTypeId}-${selectedApptType.appointmentTypeId}`;

      // Only make the API call if parameters have changed
      // If lastFetchParamsRef is null, it means we explicitly want a new fetch (e.g., week navigation)
      if (lastFetchParamsRef.current !== null && lastFetchParamsRef.current === fetchKey) {
        console.log('AppointmentModal: Skipping duplicate API call', fetchKey);
        return;
      }

      // Update the ref to track this call
      lastFetchParamsRef.current = fetchKey;

      console.log('AppointmentModal: Fetching available slots', {
        doctorId: selectedDoctor.doctorId,
        startDate,
        endDate,
        timeZone: systemTimeZone,
        addressType: finalAddressTypeId,
        appointmentTypeId: selectedApptType.appointmentTypeId,
      });

      dispatch(getDoctorAvailableSlots({
        doctorId: selectedDoctor.doctorId,
        startDate,
        endDate,
        timeZone: systemTimeZone,
        addressType: finalAddressTypeId,
        appointmentTypeId: selectedApptType.appointmentTypeId,
        duration: typeof duration === 'number' ? duration : parseDuration(duration),
        isReschedulingProcess: true,
        appointmentId: selectedAppointment?.appointmentId,
      }));
    } else if (isRescheduleRecommendationMode || isManualRescheduleMode) {
      // Log why the API call is not being made
      console.warn('AppointmentModal: Cannot fetch slots - missing required data', {
        isRescheduleRecommendationMode,
        speciality,
        appointmentType,
        doctorId: selectedDoctor?.doctorId,
        addressTypeId,
        selectedAppointmentAddressType: selectedAppointment?.addressType
      });
    }
  }, [isRescheduleRecommendationMode, isManualRescheduleMode, speciality, appointmentType, selectedDoctor?.doctorId, selectedDoctor?.availability, weekOffset, addressTypeId, selectedAppointment?.addressType, dispatch]);

  const handleNextWeek = () => {
    // Reset fetch params BEFORE updating state to ensure useEffect triggers
    console.log('AppointmentModal: handleNextWeek - resetting fetch params and updating weekOffset');
    lastFetchParamsRef.current = null;
    setWeekOffset(prev => {
      const newOffset = Math.min(2, prev + 1);
      console.log('AppointmentModal: handleNextWeek - new weekOffset:', newOffset);
      return newOffset;
    });
    setOpenDay(null);
  };

  const handlePrevWeek = () => {
    // Reset fetch params BEFORE updating state to ensure useEffect triggers
    console.log('AppointmentModal: handlePrevWeek - resetting fetch params and updating weekOffset');
    lastFetchParamsRef.current = null;
    setWeekOffset(prev => {
      const newOffset = Math.max(0, prev - 1);
      console.log('AppointmentModal: handlePrevWeek - new weekOffset:', newOffset);
      return newOffset;
    });
    setOpenDay(null);
  };

  const toggleDayDrawer = (day) => {
    setOpenDay(openDay === day ? null : day);
  };

  const handleCancelAppointment = async () => {
    if (selectedAppointment) {
      const patientDisplayName = patientNameList.find((p) => p.patientId === patient)?.name ||
        `${selectedAppointment.patientName} ${selectedAppointment.patientSurname}`;
      const result = await Swal.fire({
        title: t('appointmentmodal.confirm_cancel_title'),
        text: t('appointmentmodal.confirm_cancel_text', { patient: patientDisplayName, time: formatTime(selectedAppointment.localTime) }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#9ca3af',
        confirmButtonText: t('appointmentmodal.cancel_appointment_button', 'Cancel Appointment'),
      });

      if (result.isConfirmed) {
        try {
          const doctorId = selectedDoctor.doctorId;
          const actionResult = await dispatch(cancelAppointment({
            appointmentId: selectedAppointment.appointmentId,
            doctorId,
            start: dateRangeStart,
            end: dateRangeEnd
          }));

          if (actionResult.meta.requestStatus === 'fulfilled') {
            handleCloseModal();
            toast.success(t('appointmentmodal.cancel_success_text'));
          } else {
            throw new Error(extractErrorMessage(actionResult.payload, t('appointmentmodal.cancel_error_text'), i18n.language));
          }
        } catch (err) {
          // Error toast handled centrally via Appointment.jsx appointmenterror useEffect
          console.error('Cancel appointment error:', err);
        }
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPatient('');
    setLocation(locations[0]);
    setSpeciality('');
    setAppointmentType('');
    setDuration('');
    originalDurationRef.current = null;
    setPrice('');
    setMode('visio');
    setCommunication('');
    setNotes('');
    setSelectedSlot(null);
    setSelectedAppointment(null);
    setIsRescheduling(false);
    setIsPatientOpen(false);
    setPatientSearch('');
    setPatientSearchError('');
    setPriceError('');
    setCommunicationError('');
    setIsRescheduleRecommendationMode(false);
    setIsManualRescheduleMode(false);
    setManualReschedulePayload(null);
    setIsViewingMoreSlots(false);
    setSelectedRecommendationSlots([]);
    setWeekOffset(0);
    setOpenDay(null);
    setIsCustomSelection(false);
    setPatientType('myPatient'); // Reset to "My patient"
    setNewPatientData({
      name: '',
      email: '',
      phone: '',
      dob: '',
      gender: '',
      countryId: '',
      timeZone: ''
    });
    setNewPatientErrors({
      name: '',
      email: '',
      gender: '',
      dob: '',
      phone: ''
    });
    setCharLimitReached({});
    setEmailStatus(null);
    setPhoneStatus(null);
    resetMocState();
    resetOtpEmailState();
    resetOtpPhoneState();
    lastCheckedPhoneRef.current = '';
    isPhoneCheckInProgressRef.current = false;
    setOthersSearch('');
    setIsViewMode(false);
    originalDurationRef.current = null;
    originalPriceRef.current = null; // Reset original price when modal closes
    isPriceManuallyChangedRef.current = false; // Reset manual change flag when modal closes
    lastFetchParamsRef.current = null; // Reset last fetch params when modal closes
    lastPriceUpdateRef.current = { customPrice: null, defaultPrice: null, appointmentTypeId: null }; // Reset price update tracking when modal closes
    // Clear custom price when modal closes
    dispatch(clearSelectedCustomPrice());
  };

  // Check if a slot overlaps with any selected slot based on duration
  const isSlotOverlapping = (slot) => {
    // Use the original appointment's duration if it exists, otherwise use current duration
    let slotDuration;
    if (selectedAppointment) {
      slotDuration = calculateDuration(selectedAppointment.timeSlot, selectedAppointment.slotEndTime);
    } else {
      const selectedApptType = appointmentTypes.find((appt) => appt.type === appointmentType);
      slotDuration = selectedApptType?.duration || duration;
    }

    if (!slotDuration || selectedRecommendationSlots.length === 0) return false;

    const slotStart = new Date(slot.start).getTime();
    const slotEnd = slotStart + (slotDuration * 60 * 1000); // duration is in minutes, convert to milliseconds

    return selectedRecommendationSlots.some(selectedSlot => {
      const selectedStart = new Date(selectedSlot.start).getTime();
      const selectedEnd = selectedStart + (slotDuration * 60 * 1000);

      // Check if slots overlap: one starts before the other ends
      return (slotStart < selectedEnd && slotEnd > selectedStart);
    });
  };

  const handleSlotSelection = (slot) => {
    const slotString = slot.start;
    const isSelected = selectedRecommendationSlots.some(s => s.start === slotString);

    if (isSelected) {
      // Remove slot if already selected
      setSelectedRecommendationSlots(selectedRecommendationSlots.filter(s => s.start !== slotString));
    } else {
      // Check if slot would overlap with already selected slots
      if (isSlotOverlapping(slot)) {
        toast.warn(t('appointmentmodal.slot_overlap_text') || 'This slot overlaps with a selected slot. Please select a different time.');
        return;
      }

      // Add slot if less than 3 selected
      if (selectedRecommendationSlots.length < 3) {
        setSelectedRecommendationSlots([...selectedRecommendationSlots, slot]);
      } else {
        toast.warn(t('appointmentmodal.max_slots_selected_text'));
      }
    }
  };

  const handleSendRescheduleRecommendation = async () => {
    if (selectedRecommendationSlots.length !== 3) {
      toast.warn(t('appointmentmodal.select_three_slots_error'));
      return;
    }

    // Get patient display name
    const patientDisplayName = patientNameList.find((p) => p.patientId === patient)?.name ||
      `${selectedAppointment?.patientName || ''} ${selectedAppointment?.patientSurname || ''}`.trim() ||
      t('appointmentmodal.patient_label');

    // Format selected slots for display with styled boxes
    const slotsHtml = selectedRecommendationSlots
      .map((slot) => {
        const slotDate = new Date(slot.start);
        const dateStr = format(slotDate, 'MM/dd/yyyy');
        const timeStr = formatClockTime(slotDate);
        return `
          <div style="
            background: white;
            color: #3AA8A8;
            padding: 12px 16px;
            border-radius: 8px;
            border: 1px solid #3AA8A8;
            margin: 8px;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            font-weight: 500;
            min-width: 160px;
          ">
            <span style="font-size: 14px;">${dateStr}, ${timeStr}</span>
          </div>
        `;
      })
      .join('');

    // Show confirmation dialog
    const confirmationResult = await Swal.fire({
      title: t('appointmentmodal.confirm_send_reschedule_recommendation_title'),
      html: `
        <div style="text-align: center; padding: 10px 0;">
          <p style="color: #374151; font-size: 15px; margin-bottom: 16px;">
            ${t('appointmentmodal.confirm_send_reschedule_recommendation_text_prefix')} <strong>${patientDisplayName}</strong>?
          </p>
          <div style="margin-top: 20px;">
            <p style="color: #6B7280; font-size: 14px; font-weight: 600; margin-bottom: 12px;">
              ${t('appointmentmodal.selected_slots')}:
            </p>
            <div style="display: flex; flex-wrap: wrap; justify-content: center; max-width: 500px; margin: 0 auto;">
              ${slotsHtml}
            </div>
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4ABDBD',
      cancelButtonColor: '#e5e7eb',
      confirmButtonText: t('appointmentmodal.send_button', 'Send'),
      cancelButtonText: t('appointmentmodal.cancel_button', 'Cancel'),
      width: '600px'
    });

    if (!confirmationResult.isConfirmed) {
      return;
    }

    try {
      const payload = {
        date_1: formatDateForPayload(selectedRecommendationSlots[0].start),
        time_1: formatTimeForPayload(selectedRecommendationSlots[0].start),
        date_2: formatDateForPayload(selectedRecommendationSlots[1].start),
        time_2: formatTimeForPayload(selectedRecommendationSlots[1].start),
        date_3: formatDateForPayload(selectedRecommendationSlots[2].start),
        time_3: formatTimeForPayload(selectedRecommendationSlots[2].start),
        status: 'Rescheduling',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const result = await dispatch(sendRescheduleRecommendation({
        appointmentId: selectedAppointment?.appointmentId,
        payload
      }));

      if (result.meta.requestStatus === 'fulfilled') {
        handleCloseModal();
        toast.success(t('appointmentmodal.reschedule_recommendation_success_text'));

        // Refresh appointments after successful reschedule recommendation
        if (selectedDoctor?.doctorId && dateRangeStart && dateRangeEnd) {
          dispatch(getAllAppointments({
            doctorId: selectedDoctor.doctorId,
            start: dateRangeStart,
            end: dateRangeEnd
          }));
        }

        // Refresh rescheduling-recommendation slots so the calendar greys them out
        onReschedulingSlotsRefetch?.();
      } else {
        throw new Error(extractErrorMessage(result.payload, t('appointmentmodal.reschedule_recommendation_error_text'), i18n.language));
      }
    } catch (err) {
      // Error toast handled centrally via Appointment.jsx appointmenterror useEffect
      console.error('Reschedule recommendation error:', err);
    }
  };

  // Handle opening view more slots for rescheduling
  const handleViewMoreSlots = () => {
    if (!isReschedulingStatus || !selectedAppointment) return;

    // Enable view more slots mode (direct selection without "select 3 slots")
    setIsViewingMoreSlots(true);
    setIsRescheduleRecommendationMode(true);
    setSelectedRecommendationSlots([]);
    setWeekOffset(0);
    setOpenDay(null);

    // Fetch available slots with the appointment's speciality, address type, and appointment type
    const currentWeekMonday = getCurrentWeekMonday(today);
    const startDate = format(addDays(currentWeekMonday, 0), 'yyyy-MM-dd');
    const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

    // Get the appointment type ID from selectedAppointment
    const appointmentTypeId = selectedAppointment.appointmentType;
    const addressTypeId = selectedAppointment.addressType;

    console.log('AppointmentModal: Fetching available slots for view more', {
      doctorId: selectedDoctor?.doctorId,
      startDate,
      endDate,
      timeZone: systemTimeZone,
      addressType: addressTypeId,
      appointmentTypeId,
    });

    dispatch(getDoctorAvailableSlots({
      doctorId: selectedDoctor?.doctorId,
      startDate,
      endDate,
      timeZone: systemTimeZone,
      addressType: addressTypeId,
      appointmentTypeId,
      duration: typeof duration === 'number' ? duration : parseDuration(duration),
      isReschedulingProcess: true,
      appointmentId: selectedAppointment?.appointmentId,
    }));

    // Scroll to bottom to show the available slots
    scrollToBottom();
  };

  // Handle reschedule slot selection with confirmation
  const handleManualRescheduleConfirm = async (slotDate) => {
    if (!selectedAppointment || !manualReschedulePayload) {
      toast.warn(t('appointmentmodal.missing_manual_reschedule_params'));
      return;
    }

    const formattedDate = `${format(new Date(slotDate), 'MMM d, yyyy')} ${formatClockTime(slotDate)}`;

    const result = await Swal.fire({
      title: t('appointmentmodal.confirm_reschedule_title'),
      text: t('appointmentmodal.confirm_reschedule_slot_text', { date: formattedDate, defaultValue: `Are you sure you want to reschedule to ${formattedDate}?` }),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4ABDBD',
      cancelButtonColor: '#e5e7eb',
      confirmButtonText: t('appointmentmodal.yes_reschedule_button', 'Yes, Reschedule'),
      cancelButtonText: t('appointmentmodal.cancel_button', 'Cancel')
    });

    if (result.isConfirmed) {
      try {
        const date = formatDateForPayload(slotDate);
        const time = formatTimeForPayload(slotDate);

        // Calculate end time
        let durationMinutes = parseDuration(manualReschedulePayload.duration);
        const [hours, minutes] = time.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes + durationMinutes;
        const endHours = Math.floor(totalMinutes / 60) % 24;
        const endMinutes = totalMinutes % 60;
        const slotEndTimeStr = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

        const finalPayload = {
          ...manualReschedulePayload,
          newDate: date,
          newTime: time,
          newSlotEndTime: slotEndTimeStr,
          start: dateRangeStart,
          end: dateRangeEnd
        };

        const res = await dispatch(rescheduleAppointment(finalPayload));
        if (res.meta.requestStatus === 'fulfilled') {
          handleCloseModal();
          toast.success(t('appointmentmodal.reschedule_success_text', 'Your appointment has been successfully rescheduled.'));
          onReschedulingSlotsRefetch?.();
        } else {
          throw new Error(extractErrorMessage(res.payload, t('appointmentmodal.reschedule_error_text', 'Failed to reschedule'), i18n.language));
        }
      } catch (err) {
        // Error toast handled centrally via Appointment.jsx appointmenterror useEffect
        console.error('Manual reschedule error:', err);
      }
    }
  };

  // Handle reschedule slot selection with confirmation
  const handleRescheduleSlotClick = async (slotDate) => {
    if (!selectedAppointment) return;

    // Check if slot is before the allowed past boundary (doctor/admin opt-out
    // widens this; default is "now" — unchanged).
    if (new Date(slotDate) < getDoctorPastBoundary(selectedDoctor?.doctorSettings)) {
      toast.warn(t('appointmentmodal.slot_in_past_message', 'The selected slot is in the past. Please choose another slot from View More Slots.'));
      return;
    }

    const formattedDate = `${format(new Date(slotDate), 'MMM d, yyyy')} ${formatClockTime(slotDate)}`;

    const result = await Swal.fire({
      title: t('appointmentmodal.confirm_reschedule_title'),
      text: t('appointmentmodal.confirm_reschedule_slot_text', { date: formattedDate, defaultValue: `Are you sure you want to reschedule to ${formattedDate}?` }),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4ABDBD',
      cancelButtonColor: '#e5e7eb',
      confirmButtonText: t('appointmentmodal.yes_reschedule_button', 'Yes, Reschedule'),
      cancelButtonText: t('appointmentmodal.cancel_button', 'Cancel')
    });

    if (result.isConfirmed) {
      try {
        // Extract date and time using browser local time (what user sees in UI)
        const date = formatDateForPayload(slotDate);
        const time = formatTimeForPayload(slotDate);

        // Use the original appointment's duration
        const durationMinutes = calculateDuration(selectedAppointment.timeSlot, selectedAppointment.slotEndTime);

        // Calculate end time by adding duration to start time
        const [hours, minutes] = time.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes + durationMinutes;
        const endHours = Math.floor(totalMinutes / 60) % 24;
        const endMinutes = totalMinutes % 60;
        const slotEndTimeStr = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

        const payload = {
          appointmentId: selectedAppointment.appointmentId,
          patientId: selectedAppointment.patientId,
          doctorId: selectedAppointment.doctorId,
          newDate: date,
          newTime: time,
          newSlotEndTime: slotEndTimeStr,
          timeZone: systemTimeZone,
          appointmentTypeId: selectedAppointment.appointmentType,
          description: selectedAppointment.description || '',
          doctorDescription: selectedAppointment.doctorDescription || null,
          appointmentMode: selectedAppointment.appointmentMode || 'onsite',
          SpecialtyId: selectedAppointment.SpecialtyId,
          addressType: selectedAppointment.addressType,
          duration: `${Math.floor(durationMinutes / 60)}:${(durationMinutes % 60).toString().padStart(2, '0')}`,
          price: selectedAppointment.price || 0,
          pricetype: 'Type 1',
          start: dateRangeStart,
          end: dateRangeEnd,
        };

        const actionResult = await dispatch(rescheduleAppointment(payload));

        if (actionResult.meta.requestStatus === 'fulfilled') {
          handleCloseModal();
          toast.success(t('appointmentmodal.reschedule_success_text') || 'Appointment rescheduled successfully!');
          onReschedulingSlotsRefetch?.();
        } else {
          throw new Error(extractErrorMessage(actionResult.payload, t('appointmentmodal.reschedule_error_text'), i18n.language));
        }
      } catch (err) {
        // Error toast handled centrally via Appointment.jsx appointmenterror useEffect
        console.error('Reschedule slot click error:', err);
      }
    }
  };

  // Handle patient error
  // useEffect(() => {
  //   if (patientError) {
  //     Swal.fire({
  //       icon: 'warning',
  //       title: 'Warning',
  //       text: patientError || t('appointmentmodal.patient_error_text'),
  //       confirmButtonColor: '#4ABDBD',
  //     });
  //   }
  // }, [patientError, t]);

  if (!isModalOpen) return null;

  const isEditMode = selectedAppointment && !isRescheduling;
  const isCompletedAppointment = selectedAppointment?.status === 'Completed';
  const isReschedulingStatus = selectedAppointment?.status === 'Rescheduling';

  return (
    <div data-testid="appointment-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div data-testid="appointment-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
        {/* Header Section - Sticky */}
        <div data-testid="appointment-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h3 data-testid="appointment-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
              {isCompletedAppointment
                ? t('appointmentmodal.view_completed_appointment')
                : isEditMode
                  ? (isViewMode
                    ? t('appointmentmodal.view_appointment', 'View Appointment')
                    : t('appointmentmodal.edit_appointment'))
                  : isRescheduling
                    ? t('appointmentmodal.reschedule_appointment')
                    : t('appointmentmodal.add_appointment')}
            </h3>
            {isManualRescheduleMode && (
              <span data-testid="appointment-modal-manual-mode-badge" className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs sm:text-sm font-semibold rounded-lg shadow-sm border border-blue-200">
                {t('appointmentmodal.manual_mode', 'Manual Reschedule Mode')}
              </span>
            )}
            {isRescheduleRecommendationMode && !isManualRescheduleMode && !isViewingMoreSlots && (
              <span data-testid="appointment-modal-recommend-mode-badge" className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs sm:text-sm font-semibold rounded-lg shadow-sm border border-amber-200">
                {t('appointmentmodal.recommend_mode', 'Recommend Reschedule Mode')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isReschedulingStatus && (
              <div data-testid="appointment-modal-waiting-status-badge" className="px-3 py-1.5 bg-red-500 text-white rounded-lg shadow-md text-sm font-semibold flex items-center gap-2">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                {t('appointmentmodal.waiting_for_patient_response')}
              </div>
            )}
            <button
              data-testid="close-appointment-modal"
              id="btn-close-modal"
              onClick={handleCloseModal}
              className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
            >
              <X data-testid="close-modal-icon" className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div ref={scrollableContentRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
          {saveRefusal && (
            <div data-testid="appointment-save-refusal" role="alert" className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <CalendarX size={18} className="mt-0.5 shrink-0" />
              <span>{saveRefusal}</span>
            </div>
          )}
          <form data-testid="appointment-form" onSubmit={handleSaveAppointment} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${appointmentLoading || patientLoading || isBookNewPatientLoading ? "opacity-50 pointer-events-none" : ""} ${isEditMode && isViewMode ? "view-mode-form" : ""}`}>
            {/* Patient Type Radio Buttons */}
            {!isEditMode && (
              <div data-testid="appointment-patient-type-selector" className="sm:col-span-2 lg:col-span-3">
                <div className="flex items-center gap-6">
                  <label data-testid="appointment-patient-type-mypatient-label" className="flex items-center gap-2 cursor-pointer">
                    <input
                      data-testid="appointment-patient-type-radio-myPatient"
                      type="radio"
                      name="patientType"
                      value="myPatient"
                      checked={patientType === 'myPatient'}
                      onChange={(e) => {
                        setPatientType(e.target.value);
                        // Reset all fields when switching to "My patient"
                        setPatient('');
                        setNewPatientData({
                          name: '',
                          email: '',
                          phone: '',
                          dob: '',
                          gender: '',
                          countryId: '',
                          timeZone: ''
                        });
                        setNewPatientErrors({
                          name: '',
                          email: '',
                          gender: '',
                          dob: '',
                          phone: ''
                        });
                        setCharLimitReached({});
                        setEmailStatus(null);
                        setPhoneStatus(null);
                        resetMocState();
                        resetOtpEmailState();
                        resetOtpPhoneState();
                        setPatientSearch('');
                        setOthersSearch('');
                        setIsPatientOpen(false);
                        // Reset appointment fields
                        setSpeciality('');
                        setAppointmentType('');
                        setDuration('');
                        originalDurationRef.current = null;
                        setPrice('');
                        setPriceError('');
                      }}
                      className="w-4 h-4 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer"
                      disabled={appointmentLoading || patientLoading || isRescheduling || isEditMode}
                    />
                    <span data-testid="appointment-patient-type-mypatient-text" className="text-sm font-semibold text-gray-700">{t('appointmentmodal.my_patients', 'My Patients')}</span>
                  </label>
                  <label data-testid="appointment-patient-type-new-label" title={patientLimitHit ? patientLimitTip : ''} className={`flex items-center gap-2 ${patientLimitHit ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      data-testid="appointment-patient-type-radio-new"
                      type="radio"
                      name="patientType"
                      value="new"
                      checked={patientType === 'new'}
                      onChange={(e) => {
                        setPatientType(e.target.value);
                        // Reset all fields when switching to "New"
                        setPatient('');
                        setPatientSearch('');
                        setOthersSearch('');
                        setIsPatientOpen(false);
                        setNewPatientData({
                          name: '',
                          email: '',
                          phone: '',
                          dob: '',
                          gender: '',
                          countryId: '',
                          timeZone: ''
                        });
                        setNewPatientErrors({
                          name: '',
                          email: '',
                          gender: '',
                          dob: '',
                          phone: ''
                        });
                        setCharLimitReached({});
                        setEmailStatus(null);
                        setPhoneStatus(null);
                        resetMocState();
                        resetOtpEmailState();
                        resetOtpPhoneState();
                        // Reset appointment fields
                        setSpeciality('');
                        setAppointmentType('');
                        setDuration('');
                        originalDurationRef.current = null;
                        setPrice('');
                        setPriceError('');
                      }}
                      className="w-4 h-4 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer"
                      disabled={appointmentLoading || patientLoading || isRescheduling || isEditMode || patientLimitHit}
                    />
                    <span data-testid="appointment-patient-type-new-text" className="text-sm font-semibold text-gray-700">{t('appointmentmodal.new_patient', 'New Patient')}</span>
                  </label>
                  {/* Gated by admin Settings → Doctor → All Patient Mode. */}
                  {allPatientModeEnabled && (
                    <label data-testid="appointment-patient-type-others-label" title={patientLimitHit ? patientLimitTip : ''} className={`flex items-center gap-2 ${patientLimitHit ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <input
                        data-testid="appointment-patient-type-radio-others"
                        type="radio"
                        name="patientType"
                        value="others"
                        checked={patientType === 'others'}
                        onChange={(e) => {
                          setPatientType(e.target.value);
                          // Reset all fields when switching to "Others"
                          setNewPatientData({
                            name: '',
                            email: '',
                            phone: '',
                            dob: '',
                            gender: '',
                            countryId: '',
                            timeZone: ''
                          });
                          setNewPatientErrors({
                            name: '',
                            email: '',
                            gender: '',
                            dob: '',
                            phone: ''
                          });
                          setCharLimitReached({});
                          setEmailStatus(null);
                          setPhoneStatus(null);
                          resetMocState();
                          resetOtpEmailState();
                          resetOtpPhoneState();
                          setPatient('');
                          setPatientSearch('');
                          setOthersSearch('');
                          setIsPatientOpen(false);
                          // Reset appointment fields
                          setSpeciality('');
                          setAppointmentType('');
                          setDuration('');
                          originalDurationRef.current = null;
                          setPrice('');
                          setPriceError('');
                        }}
                        className="w-4 h-4 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer"
                        disabled={appointmentLoading || patientLoading || isRescheduling || isEditMode || patientLimitHit}
                      />
                      <span data-testid="appointment-patient-type-others-text" className="text-sm font-semibold text-gray-700">{t('appointmentmodal.all_patients', 'All Patients')}</span>
                    </label>
                  )}
                </div>
              </div>
            )}
            {/* New Patient Creation Fields */}
            {patientType === 'new' && (
              <div data-testid="appointment-new-patient-section" className="sm:col-span-2 lg:col-span-3">
                <h4 data-testid="appointment-new-patient-heading" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-4">
                  {t('appointmentmodal.new_patient_creation', 'New Patient Creation')}
                </h4>
                {isAnyOneContact(loginPolicy) && (
                  <p data-testid="appointment-new-patient-any-one-hint" className="text-xs text-gray-500 -mt-2 mb-4">
                    {connected
                      ? t('appointmentmodal.moc_contact_hint', 'One contact is enough: an e-mail or a mobile number. Both are welcome, and no code is needed here.')
                      : t('appointmentmodal.one_contact_hint', 'One contact is enough: fill in the e-mail or the mobile number and verify that one. The other is optional and may stay unverified.')}
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div data-testid="appointment-new-patient-email-container">
                    <label data-testid="appointment-new-patient-email-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('appointmentmodal.email', 'Email')} {loginPolicy.email.required && <span className="text-red-500">*</span>}
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${emailOtpVerified
                      ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                      : emailStatus === false
                        ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                        : emailStatus === true
                          ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                          : newPatientErrors.email
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <Mail className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                      <input
                        data-testid="appointment-new-patient-email-input"
                        type="email"
                        value={newPatientData.email}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value && !/^[a-zA-Z0-9._+-@]*$/.test(value)) return;
                          setEmailStatus(null);
                          setEmailExistsRole(null);
                          setEmailBlock(null);
                          setInTenantApplied(null); setInTenantOwned(null);
                          resetHousehold();
                          setIsPatientFound(false);
                          setFoundPatientId(null);
                          lastCheckedEmailRef.current = '';
                          isEmailCheckInProgressRef.current = false;
                          setNewPatientData(prev => ({
                            ...prev,
                            email: value,
                            name: '',
                            surname: '',
                            phone: '',
                            dob: '',
                            gender: ''
                          }));
                          resetOtpEmailState();
                          resetOtpPhoneState();
                          setNewPatientErrors({ name: '', surname: '', email: '', gender: '', dob: '', phone: '' });
                        }}
                        placeholder={t('appointmentmodal.enter_email', 'Enter email')}
                        maxLength={254}
                        required={patientType === 'new' && loginPolicy.email.required}
                        disabled={appointmentLoading || patientLoading || emailOtpVerified}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                      {emailBusy && (
                        <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                      )}
                      {!emailBusy && emailStatus === false && (
                        <X
                          data-testid="appointment-new-patient-email-clear-icon"
                          className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                          onClick={() => {
                            setNewPatientData(prev => ({ ...prev, email: '' }));
                            setEmailStatus(null);
                            setEmailExistsRole(null);
                            setEmailBlock(null);
                            setInTenantApplied(null); setInTenantOwned(null);
                            resetHousehold();
                            setIsPatientFound(false);
                            setFoundPatientId(null);
                            setNewPatientErrors(prev => ({ ...prev, email: '' }));
                            lastCheckedEmailRef.current = '';
                            resetOtpEmailState();
                          }}
                        />
                      )}
                      {loginPolicy.email.otp && !emailBusy && emailStatus === true && !isPatientFound && !emailOtpVerified && (
                        <button
                          data-testid="appointment-new-patient-email-send-otp-button"
                          title={connected ? t('appointmentmodal.verify_optional_hint', 'Optional — only if the patient is with you now. Otherwise they verify at their first sign-in.') : undefined}
                          type="button"
                          onClick={handleSendEmailOtp}
                          disabled={sendOtpLoading || emailOtpSent}
                          className={connected ? OPTIONAL_OTP_CLASS : "ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"}
                        >
                          {sendOtpLoading ? '...' : emailOtpSent ? t('appointmentmodal.sent', 'Sent') : (connected ? t('appointmentmodal.verify_optional', 'Verify (optional)') : t('appointmentmodal.send_otp', 'Send OTP'))}
                        </button>
                      )}
                      {emailOtpVerified && (
                        <ShieldCheck className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                      )}
                      {!emailBusy && emailStatus === true && isPatientFound && (
                        <Check className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                      )}
                    </div>
                    {!emailBusy && emailStatus === false && (
                      <p data-testid="appointment-new-patient-email-exists-message" className="text-xs text-red-500 mt-1 flex flex-wrap items-center gap-x-2">
                        <span>{blockMessage('email')}</span>
                        {['moc', 'pending'].includes(emailBlock) && (
                          <button type="button" onClick={() => reopenMocPopup('email')} className="text-[#2D8A8A] font-semibold hover:underline cursor-pointer" data-testid="appointment-new-patient-email-moc-button">
                            {emailBlock === 'moc' ? t('appointmentmodal.link_account', 'Add to this hospital') : t('appointmentmodal.view_details', 'View')}
                          </button>
                        )}
                      </p>
                    )}
                    {/* Case A applied automatically: a green confirmation, never a red block. */}
                    {isPatientFound && inTenantApplied !== 'phone' && (
                      <p data-testid="appointment-new-patient-existing-found-message" className="text-xs text-green-600 mt-1 font-medium flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="flex items-center gap-1">
                          <Check size={12} />
                          {inTenantApplied === 'email'
                            ? inTenantMessage()
                            : t('appointmentmodal.existing_patient_found')}
                        </span>
                        {inTenantApplied === 'email' && (
                          <button type="button" onClick={clearInTenantMatch} className="text-[#2D8A8A] font-semibold hover:underline cursor-pointer" data-testid="appointment-new-patient-email-change-button">
                            {t('appointmentmodal.change_identifier', 'Change')}
                          </button>
                        )}
                      </p>
                    )}
                    {emailOtpVerified && !isPatientFound && (
                      <p data-testid="appointment-new-patient-email-otp-verified-message" className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('appointmentmodal.email_otp_verified')}</p>
                    )}
                    {emailOtpSent && !emailOtpVerified && !isPatientFound && (
                      <div data-testid="appointment-new-patient-email-otp-container" className="mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <input
                            data-testid="appointment-new-patient-email-otp-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={emailOtpCode}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setEmailOtpCode(val);
                              setEmailOtpError('');
                              if (val.length === 6) handleVerifyEmailOtp(val);
                            }}
                            placeholder={t('appointmentmodal.enter_otp', 'Enter OTP')}
                            className="flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                          />
                          <button
                            data-testid="appointment-new-patient-email-verify-otp-button"
                            type="button"
                            onClick={() => handleVerifyEmailOtp()}
                            disabled={verifyOtpLoading || emailOtpCode.length !== 6}
                            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {verifyOtpLoading ? '...' : t('appointmentmodal.verify', 'Verify')}
                          </button>
                        </div>
                        <div>
                          {emailOtpTimer > 0 ? (
                            <span data-testid="appointment-new-patient-email-otp-timer" className="text-xs text-gray-500">{t('appointmentmodal.resend_in', { seconds: emailOtpTimer })}</span>
                          ) : (
                            <button data-testid="appointment-new-patient-email-resend-otp-button" type="button" onClick={handleSendEmailOtp} disabled={sendOtpLoading} className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50">{t('appointmentmodal.resend_otp')}</button>
                          )}
                        </div>
                        {emailOtpError && <p data-testid="appointment-new-patient-email-otp-error" className="text-red-500 text-xs">{emailOtpError}</p>}
                      </div>
                    )}
                    {newPatientErrors.email && emailStatus !== false && (
                      <p data-testid="appointment-new-patient-email-error" className="text-xs text-red-500 mt-1">{newPatientErrors.email}</p>
                    )}
                  </div>
                  <div data-testid="appointment-new-patient-country-container">
                    <label data-testid="appointment-new-patient-country-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('appointmentmodal.country_timezone', 'Country & Timezone')} <span className="text-red-500">*</span>
                    </label>
                    <CountryTimeZoneSelect
                      countryTimezoneList={countryTimezoneList}
                      countryId={newPatientData.countryId}
                      timeZone={newPatientData.timeZone}
                      onChange={({ countryId, timeZone }) => {
                        setNewPatientData(prev => ({ ...prev, countryId, timeZone, phone: '' }));
                        setPhoneStatus(null);
                        if (lastCheckedPhoneRef) lastCheckedPhoneRef.current = '';
                        if (resetOtpPhoneState) resetOtpPhoneState();
                      }}
                      placeholder={t('appointmentmodal.select_country_timezone', 'Select Country & Timezone')}
                      disabled={appointmentLoading || patientLoading || isPatientFound}
                    />
                  </div>
                  <div data-testid="appointment-new-patient-phone-container">
                    <label data-testid="appointment-new-patient-phone-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('appointmentmodal.mobile_no')} {loginPolicy.mobile.required && <span className="text-red-500">*</span>}
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 ${!newPatientData.countryId ? 'bg-gray-100' : 'bg-white'} ${phoneOtpVerified && !isPatientFound
                      ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                      : phoneStatus === false
                        ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500 bg-red-50'
                        : phoneStatus === true
                          ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                          : newPatientErrors.phone ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      {newPatientData.countryId && selectedCountry?.countryCode && (
                        <span className="text-sm font-medium text-gray-600 mr-2 flex-shrink-0">{selectedCountry.countryCode}</span>
                      )}
                      <input
                        data-testid="appointment-new-patient-phone-input"
                        type="tel"
                        value={newPatientData.phone}
                        minLength={selectedCountry?.mobileMinLength || 9}
                        maxLength={selectedCountry?.mobileMaxLength || 15}
                        pattern="[0-9]*"
                        inputMode="numeric"
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ''); // Only allow numbers
                          const phoneMax = selectedCountry?.mobileMaxLength || 15;
                          if (value.length <= phoneMax) {
                            setNewPatientData({ ...newPatientData, phone: value });
                            setPhoneStatus(null);
                            setPhoneBlock(null);
                            setInTenantApplied(null); setInTenantOwned(null);
                            resetHousehold();
                            lastCheckedPhoneRef.current = '';
                            isPhoneCheckInProgressRef.current = false;
                            resetOtpPhoneState();
                            if (newPatientErrors.phone) {
                              setNewPatientErrors({ ...newPatientErrors, phone: '' });
                            }
                          }
                        }}
                        className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm ${!newPatientData.countryId ? 'cursor-not-allowed' : ''}`}
                        placeholder={!newPatientData.countryId ? t('appointmentmodal.select_country_first', 'Select country first') : t('appointmentmodal.enter_mobile_number', 'Enter mobile number')}
                        required={patientType === 'new' && loginPolicy.mobile.required}
                        disabled={!newPatientData.countryId || appointmentLoading || patientLoading || isPatientFound || phoneOtpVerified}
                      />
                      {phoneBusy && !isPatientFound && (
                        <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                      )}
                      {!phoneBusy && phoneStatus === false && !isPatientFound && (
                        <svg className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24" onClick={() => { setNewPatientData(prev => ({ ...prev, phone: '' })); setPhoneStatus(null); setPhoneBlock(null); setInTenantApplied(null); setInTenantOwned(null); resetHousehold(); setNewPatientErrors(prev => ({ ...prev, phone: '' })); lastCheckedPhoneRef.current = ''; resetOtpPhoneState(); }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      )}
                      {loginPolicy.mobile.otp && !phoneBusy && phoneStatus === true && !isPatientFound && !phoneOtpVerified && (
                        <button
                          data-testid="appointment-new-patient-phone-send-otp-button"
                          title={connected ? t('appointmentmodal.verify_optional_hint', 'Optional — only if the patient is with you now. Otherwise they verify at their first sign-in.') : undefined}
                          type="button"
                          onClick={handleSendPhoneOtp}
                          disabled={sendOtpLoading || phoneOtpSent}
                          className={connected ? OPTIONAL_OTP_CLASS : "ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"}
                        >
                          {sendOtpLoading ? '...' : phoneOtpSent ? t('appointmentmodal.sent', 'Sent') : (connected ? t('appointmentmodal.verify_optional', 'Verify (optional)') : t('appointmentmodal.send_otp', 'Send OTP'))}
                        </button>
                      )}
                      {phoneOtpVerified && !isPatientFound && (
                        <ShieldCheck className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                      )}
                    </div>
                    {newPatientErrors.phone && (
                      <p data-testid="appointment-new-patient-phone-error" className="text-xs text-red-500 mt-1">{newPatientErrors.phone}</p>
                    )}
                    {!phoneBusy && phoneStatus === false && !newPatientErrors.phone && !isPatientFound && (
                      <p data-testid="appointment-new-patient-phone-exists-error" className="text-red-500 text-xs mt-1 flex flex-wrap items-center gap-x-2">
                        <span>{blockMessage('phone')}</span>
                        {['moc', 'pending'].includes(phoneBlock) && (
                          <button type="button" onClick={() => reopenMocPopup('phone')} className="text-[#2D8A8A] font-semibold hover:underline cursor-pointer" data-testid="appointment-new-patient-phone-moc-button">
                            {phoneBlock === 'moc' ? t('appointmentmodal.link_account', 'Add to this hospital') : t('appointmentmodal.view_details', 'View')}
                          </button>
                        )}
                      </p>
                    )}
                    {inTenantApplied === 'phone' && (
                      <p data-testid="appointment-new-patient-phone-in-tenant-message" className="text-xs text-green-600 mt-1 font-medium flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="flex items-center gap-1">
                          <Check size={12} /> {inTenantMessage()}
                        </span>
                        <button type="button" onClick={clearInTenantMatch} className="text-[#2D8A8A] font-semibold hover:underline cursor-pointer" data-testid="appointment-new-patient-phone-change-button">
                          {t('appointmentmodal.change_identifier', 'Change')}
                        </button>
                      </p>
                    )}
                    {/* Phone OTP input row */}
                    {phoneOtpSent && !phoneOtpVerified && !isPatientFound && (
                      <div data-testid="appointment-new-patient-phone-otp-container" className="mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <input
                            data-testid="appointment-new-patient-phone-otp-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={phoneOtpCode}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setPhoneOtpCode(val);
                              setPhoneOtpError('');
                              if (val.length === 6) handleVerifyPhoneOtp(val);
                            }}
                            placeholder={t('appointmentmodal.enter_otp', 'Enter OTP')}
                            className="flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                          />
                          <button
                            data-testid="appointment-new-patient-phone-verify-otp-button"
                            type="button"
                            onClick={() => handleVerifyPhoneOtp()}
                            disabled={verifyOtpLoading || phoneOtpCode.length !== 6}
                            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {verifyOtpLoading ? '...' : t('appointmentmodal.verify', 'Verify')}
                          </button>
                        </div>
                        <div>
                          {phoneOtpTimer > 0 ? (
                            <span data-testid="appointment-new-patient-phone-otp-timer" className="text-xs text-gray-500">{t('appointmentmodal.resend_in', { seconds: phoneOtpTimer })}</span>
                          ) : (
                            <button data-testid="appointment-new-patient-phone-resend-otp-button" type="button" onClick={handleSendPhoneOtp} disabled={sendOtpLoading} className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50">
                              {t('appointmentmodal.resend_otp', 'Resend OTP')}
                            </button>
                          )}
                        </div>
                        {phoneOtpError && <p data-testid="appointment-new-patient-phone-otp-error" className="text-red-500 text-xs">{phoneOtpError}</p>}
                      </div>
                    )}
                    {phoneOtpVerified && !isPatientFound && (
                      <p data-testid="appointment-new-patient-phone-otp-verified-message" className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('appointmentmodal.mobile_otp_verified')}</p>
                    )}
                  </div>
                  {/* CONNECTED: what replaced the "book as pending?"
                      question — said before Book, for a brand-new person. */}
                  {connected && !isPatientFound && !household && (
                    <p
                      data-testid="appointment-new-patient-first-signin-hint"
                      className="sm:col-span-2 lg:col-span-3 -mt-1 flex items-start gap-2 text-xs text-gray-500"
                    >
                      <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#2D8A8A]" />
                      <span>{t('appointmentmodal.first_signin_hint', 'The patient verifies their contact and sets a password at their first sign-in.')}</span>
                    </p>
                  )}
                  {/* ONE CONTACT, SEVERAL PATIENTS — who is this visit for?
                      Raised only when the contact turned out to be a household
                      with something to choose; otherwise the old silent
                      hand-off still applies and nothing renders here. */}
                  {household && (
                    <HouseholdPicker
                      household={household}
                      tPrefix="appointmentmodal.household"
                      testId="appointment-household"
                      selectedId={householdPick?.patientId || null}
                      onSelect={pickHouseholdMember}
                      onAddMember={submitHouseholdMember}
                      adding={householdAdding}
                      addError={householdAddError}
                      disabled={appointmentLoading || patientLoading}
                    />
                  )}
                  {/* A chosen member replaces the person fields: they belong to
                      an existing record now, so editing them here would promise
                      an update the booking never makes. */}
                  {householdPick && (
                    <div data-testid="appointment-household-summary" className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-green-200 bg-green-50 px-3 py-2">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-green-700">
                        <Check size={14} />
                        {t('appointmentmodal.household.booking_for', {
                          name: [householdPick.name, householdPick.surname].filter(Boolean).join(' ').trim(),
                          defaultValue: 'Booking for {{name}}',
                        })}
                      </span>
                      <span className="text-xs text-gray-500">
                        {[hospitalPatientCode(householdPick), householdPick.relation].filter(Boolean).join(' · ')}
                      </span>
                      <button
                        type="button"
                        data-testid="appointment-household-change-button"
                        onClick={() => pickHouseholdMember(null)}
                        className="text-[#2D8A8A] text-xs font-semibold hover:underline cursor-pointer"
                      >
                        {t('appointmentmodal.household.change', 'Change')}
                      </button>
                    </div>
                  )}
                  {!householdPick && (<>
                  <div data-testid="appointment-new-patient-name-container">
                    <label data-testid="appointment-new-patient-name-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('appointmentmodal.name', 'Name')} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${newPatientErrors.name ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="appointment-new-patient-name-input"
                        type="text"
                        value={newPatientData.name}
                        maxLength={20}
                        autoComplete="off"
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value.length > 0) {
                            value = value.charAt(0).toUpperCase() + value.slice(1);
                          }
                          if (value && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(value)) return;

                          setNewPatientData({ ...newPatientData, name: value });

                          if (value.length >= 20) {
                            setCharLimitReached({ ...charLimitReached, name: true });
                          } else {
                            setCharLimitReached({ ...charLimitReached, name: false });
                          }

                          if (newPatientErrors.name) {
                            setNewPatientErrors({ ...newPatientErrors, name: '' });
                          }
                        }}
                        placeholder={t('appointmentmodal.enter_name', 'Enter name')}
                        required={patientType === 'new'}
                        disabled={appointmentLoading || patientLoading || isPatientFound}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                    </div>
                    {charLimitReached.name && (
                      <p data-testid="appointment-new-patient-name-char-limit-message" className="text-xs text-amber-600 mt-1">{t('appointmentmodal.character_limit_reached', 'Character limit reached (20/20)')}</p>
                    )}
                    {newPatientErrors.name && (
                      <p data-testid="appointment-new-patient-name-error" className="text-xs text-red-500 mt-1">{newPatientErrors.name}</p>
                    )}
                  </div>
                  <div data-testid="appointment-new-patient-surname-container">
                    <label data-testid="appointment-new-patient-surname-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('appointmentmodal.surname', 'Surname')} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${newPatientErrors.surname ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="appointment-new-patient-surname-input"
                        type="text"
                        value={newPatientData.surname}
                        maxLength={20}
                        autoComplete="off"
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value.length > 0) {
                            value = value.charAt(0).toUpperCase() + value.slice(1);
                          }
                          if (value && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(value)) return;
                          setNewPatientData({ ...newPatientData, surname: value });
                          if (newPatientErrors.surname) {
                            setNewPatientErrors({ ...newPatientErrors, surname: '' });
                          }
                        }}
                        placeholder={t('appointmentmodal.enter_surname', 'Enter surname')}
                        required={patientType === 'new'}
                        disabled={appointmentLoading || patientLoading || isPatientFound}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                    </div>
                    {newPatientErrors.surname && (
                      <p data-testid="appointment-new-patient-surname-error" className="text-xs text-red-500 mt-1">{newPatientErrors.surname}</p>
                    )}
                  </div>
                  <div data-testid="appointment-new-patient-gender-container">
                    <label data-testid="appointment-new-patient-gender-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('appointmentmodal.gender', 'Gender')} <span className="text-red-500">*</span>
                    </label>
                    <GenderSelect
                      value={newPatientData.gender}
                      onChange={(val) => {
                        setNewPatientData({ ...newPatientData, gender: val });
                        if (newPatientErrors.gender) {
                          setNewPatientErrors({ ...newPatientErrors, gender: '' });
                        }
                      }}
                      options={[
                        { value: 'male', label: t('appointmentmodal.male', 'Male') },
                        { value: 'female', label: t('appointmentmodal.female', 'Female') },
                        { value: 'Not_Communicated', label: t('common.gender.notCommunicated', 'Not Communicated') },
                        { value: 'other', label: t('appointmentmodal.other', 'Other') },
                      ]}
                      placeholder={t('appointmentmodal.select_gender', 'Select gender')}
                      disabled={appointmentLoading || patientLoading || isPatientFound}
                      error={!!newPatientErrors.gender}
                    />
                    {newPatientErrors.gender && (
                      <p data-testid="appointment-new-patient-gender-error" className="text-xs text-red-500 mt-1">{newPatientErrors.gender}</p>
                    )}
                  </div>
                  <div data-testid="appointment-new-patient-dob-container">
                    <label data-testid="appointment-new-patient-dob-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('appointmentmodal.dob', 'Date of Birth')} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${newPatientErrors.dob ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <Calendar className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="appointment-new-patient-dob-input"
                        type="date"
                        value={newPatientData.dob}
                        onChange={(e) => {
                          setNewPatientData({ ...newPatientData, dob: e.target.value });
                          if (newPatientErrors.dob) {
                            setNewPatientErrors({ ...newPatientErrors, dob: '' });
                          }
                        }}
                        min="1900-01-01"
                        max={new Date().toISOString().split('T')[0]}
                        required={patientType === 'new'}
                        disabled={appointmentLoading || patientLoading || isPatientFound}
                        className="w-full bg-transparent focus:outline-none text-gray-800 text-sm"
                      />
                    </div>
                    {newPatientErrors.dob && (
                      <p data-testid="appointment-new-patient-dob-error" className="text-xs text-red-500 mt-1">{newPatientErrors.dob}</p>
                    )}
                  </div>
                  </>)}
                </div>
              </div>
            )}
            {/* Appointment Info Section */}
            <div data-testid="appointment-info-section-header" className="sm:col-span-2 lg:col-span-3">
              <h4 data-testid="appointment-info-section-heading" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mt-4">
                {t('appointmentmodal.appointment_info_title', 'Appointment Info')}
              </h4>
            </div>
            {/* CUSTOM PATIENT DROPDOWN - hidden in new patient mode */}
            {patientType !== 'new' && <div data-testid="appointment-patient-dropdown-container" ref={dropdownRef} className="relative">
              <label data-testid="appointment-patient-dropdown-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('appointmentmodal.patient_label')} <span className="text-red-500">*</span>
              </label>

              <button
                data-testid="appointment-patient-dropdown-button"
                type="button"
                onClick={() => {
                  const patientInList = selectedPatient?.patientId ? patientNameList.find((p) => p.patientId === selectedPatient.patientId) : null;
                  // Disable if: loading states, edit/reschedule mode, OR patient from BookAppointmentModal (not in list) is set, OR new patient type is selected
                  const shouldDisable = appointmentLoading || patientLoading || isRescheduling || isEditMode || patientType === 'new' ||
                    (selectedPatient?.patientId && !selectedAppointment && !patientInList && patient === selectedPatient.patientId);
                  if (!shouldDisable) {
                    setIsPatientOpen(!isPatientOpen);
                  }
                }}
                disabled={(() => {
                  const patientInList = selectedPatient?.patientId ? patientNameList.find((p) => p.patientId === selectedPatient.patientId) : null;
                  // Only disable if patient from BookAppointmentModal (not in patientNameList) is currently set, OR new patient type is selected
                  return appointmentLoading || patientLoading || isRescheduling || isEditMode || patientType === 'new' ||
                    (selectedPatient?.patientId && !selectedAppointment && !patientInList && patient === selectedPatient.patientId);
                })()}
                className={`
                  w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] rounded-xl flex justify-between items-center font-medium transition
                  ${(() => {
                    const patientInList = selectedPatient?.patientId ? patientNameList.find((p) => p.patientId === selectedPatient.patientId) : null;
                    return (appointmentLoading || patientLoading || isRescheduling || isEditMode || patientType === 'new' ||
                      (selectedPatient?.patientId && !selectedAppointment && !patientInList && patient === selectedPatient.patientId))
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'text-gray-500 text-sm cursor-pointer';
                  })()}
                `}
              >
                <span className="truncate w-[85%] text-left">
                  {patient
                    ? (() => {
                      if (patientType === 'others') {
                        const patientFromList = allPatientsList.find(p => p.patientId === patient);
                        if (patientFromList) {
                          const name = patientFromList.name || '';
                          const surname = patientFromList.surname || '';
                          const fullName = `${name} ${surname}`.trim() || name || t('appointmentmodal.patient_fallback');
                          return `${fullName} (${patient})`;
                        }
                      } else {
                        const patientFromList = patientNameList.find(p => p.patientId === patient);
                        if (patientFromList) {
                          return `${patientFromList.name || ''} (${patient})`;
                        }
                        // Fallback to selectedPatient from Redux if not in patientNameList
                        if (selectedPatient?.patientId === patient) {
                          const patientName = `${selectedPatient.name || ''} ${selectedPatient.surname || ''}`.trim() || selectedPatient.name || t('appointmentmodal.patient_fallback');
                          return `${patientName} (${patient})`;
                        }
                      }
                      // Fallback to selectedAppointment data in edit/reschedule mode
                      if (selectedAppointment?.patientId === patient) {
                        const apptPatientName = `${selectedAppointment.patientName || ''} ${selectedAppointment.patientSurname || ''}`.trim();
                        if (apptPatientName) {
                          return `${apptPatientName} (${patient})`;
                        }
                      }
                      return `(${patient})`;
                    })()
                    : t('appointmentmodal.select_patient')}
                </span>
                <span className="ml-2 flex items-center">
                  {(appointmentLoading || patientLoading) ? (
                    <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : isPatientOpen ? (
                    <ChevronUp />
                  ) : (
                    <ChevronDown />
                  )}
                </span>
              </button>

              {isPatientOpen && (
                <div data-testid="appointment-patient-dropdown-menu" className="absolute left-0 mt-2 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                  {/* Search */}
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        data-testid="appointment-patient-dropdown-search-input"
                        type="text"
                        value={patientType === 'others' ? othersSearch : patientSearch}
                        onChange={(e) => {
                          if (patientType === 'others') {
                            setOthersSearch(e.target.value);
                            setPatientSearchError('');
                          } else {
                            handlePatientSearchChange(e);
                          }
                        }}
                        maxLength={50}
                        placeholder={t('appointmentmodal.search_patient', 'Search patient...')}
                        className={`w-full pl-10 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${patientSearchError
                          ? 'border-red-300 bg-red-50 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-[#4ABDBD]'
                          }`}
                        autoFocus
                      />
                    </div>
                    {patientSearchError && (
                      <p data-testid="appointment-patient-dropdown-search-error" className="text-xs text-red-600 mt-1 px-1">{patientSearchError}</p>
                    )}
                  </div>

                  {/* Loading */}
                  {(appointmentLoading || patientLoading) ? (
                    <div data-testid="appointment-patient-dropdown-loading" className="p-4 flex justify-center">
                      <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                    </div>
                  ) : (
                    <ul data-testid="appointment-patient-dropdown-list" className="max-h-60 overflow-y-auto custom-scrollbar">
                      {filteredPatients.length === 0 ? (
                        <li data-testid="appointment-patient-dropdown-empty" className="px-4 py-3 text-gray-500 text-center">
                          {patientSearch ? t('appointmentmodal.no_patients_found') : t('appointmentmodal.no_patients_available')}
                        </li>
                      ) : (
                        filteredPatients.map((pat, idx) => {
                          const displayName = patientType === 'others'
                            ? `${pat.name || ''} ${pat.surname || ''}`.trim() || pat.name || t('appointmentmodal.patient_fallback')
                            : pat.name || t('appointmentmodal.patient_fallback');
                          return (
                            <li
                              data-testid={`appointment-patient-option-${pat.patientId}`}
                              key={pat.patientId}
                              onClick={() => handleSelectPatient(pat)}
                              className={`
                                px-4 py-2.5 transition flex justify-between items-center cursor-pointer
                                ${idx !== filteredPatients.length - 1 ? 'border-b border-gray-100' : ''}
                                hover:bg-[#4ABDBD] hover:text-white
                              `}
                            >
                              <span data-testid={`appointment-patient-option-name-${pat.patientId}`}>{displayName}</span>
                              {hospitalPatientCode(pat) && (
                                <span data-testid={`appointment-patient-option-code-${pat.patientId}`} className="text-[10px] font-mono font-semibold opacity-70 ml-2">
                                  {hospitalPatientCode(pat)}
                                </span>
                              )}
                            </li>
                          );
                        })
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>}
            <div data-testid="speciality-field-container">
              <label htmlFor="speciality" data-testid="speciality-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('appointmentmodal.speciality_label')} <span className="text-red-500">*</span></label>
              {/* Translated labels resolved from ONE tree read rather than a
                  hook per row — see resolveMasterLabel. That is what makes a
                  searchable list possible here without losing the translation. */}
              <SearchableSelect
                testId="speciality-select"
                variant="form"
                value={speciality}
                onChange={(v) => handleSpecialityChange({ target: { value: v } })}
                options={(specialities || []).map((sp) => {
                  // The option value is the specialty NAME (what the booking
                  // payload sends); the master id lives on the doctor's row.
                  const master = selectedDoctor?.appointmentSpeciality?.find((x) => x.name === sp);
                  return {
                    value: sp,
                    label: resolveMasterLabel(masterTree, 'masterSpecialty', master?.specialtyId, 'name', sp),
                    icon: () => (
                      <SpecialtyIcon specialtyId={master?.specialtyId} categoryId={master?.categoryId} size={16} />
                    ),
                  };
                })}
                placeholder={t('appointmentmodal.select_speciality', 'Select a speciality')}
                searchPlaceholder={t('common.search', 'Search…')}
                disabled={appointmentLoading}
              />
            </div>
            <div data-testid="appointment-type-field-container">
              <label htmlFor="appointmentType" data-testid="appointment-type-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('appointmentmodal.appointment_type_label')} <span className="text-red-500">*</span></label>
              <select
                id="appointmentType"
                data-testid="appointment-type-select"
                value={appointmentType}
                onChange={handleAppointmentTypeChange}
                className={`tt-select w-full p-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300 ${appointmentLoading || !speciality ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                disabled={appointmentLoading || !speciality}
                required
              >
                <option data-testid="appointment-type-option-empty" value="">{t('appointmentmodal.select_appointment_type', 'Select an appointment type')}</option>
                {appointmentTypes.map((appt) => (
                  <option key={appt.type} data-testid={`appointment-type-option-${appt.type}`} value={appt.type}>
                    {appt.type}
                  </option>
                ))}
              </select>
            </div>
            {/* CUSTOM SELECTION CHECKBOX - Only for doctors and admins */}
            {(userRole === 'doctor' || userRole === 'admin') && (
              <div data-testid="appointment-custom-selection-container" className="sm:col-span-2 lg:col-span-3 mb-2">
                <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3">
                    <input
                      data-testid="appointment-custom-selection-checkbox"
                      type="checkbox"
                      id="customSelection"
                      checked={isCustomSelection}
                      onChange={(e) => setIsCustomSelection(e.target.checked)}
                      className="w-4 h-4 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer rounded"
                      disabled={appointmentLoading || patientLoading}
                    />
                    <label data-testid="appointment-custom-selection-label" htmlFor="customSelection" className="text-sm font-semibold text-gray-700 cursor-pointer">
                      {t('appointmentmodal.custom_selection', 'Custom Selection')}
                    </label>
                  </div>
                  <p className="text-xs text-gray-600 ml-7">
                    {t('appointmentmodal.custom_selection_description', 'Customize appointment mode and duration as needed')}
                  </p>
                </div>
              </div>
            )}
            <div data-testid="mode-field-container">
              <label htmlFor="mode" data-testid="mode-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('appointmentmodal.appointment_mode_label')}</label>
              <div className="flex items-center justify-between sm:justify-start gap-2">
                <span className="text-sm text-gray-700">{t('appointmentmodal.on_site')}</span>
                <label title={!videoAllowed ? SECURED_VIDEO_MSG : ''} className={`relative inline-flex items-center ${!videoAllowed ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    id="mode"
                    data-testid="mode-checkbox"
                    checked={mode === "Online"}
                    onChange={() => handleModeChange(mode === "visio" ? "Online" : "visio")}
                    className="sr-only peer"
                    disabled={appointmentLoading || (!isCustomSelection && !isVirtualAllowed) || !videoAllowed}
                  />
                  <div
                    className={`w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-[#4ABDBD] transition-all ${(!isCustomSelection && !isVirtualAllowed) || appointmentLoading || !videoAllowed ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <div
                      className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${mode === "Online" ? "translate-x-5" : "translate-x-0"}`}
                    ></div>
                  </div>
                </label>
                <span className="text-sm text-gray-700">{t('appointmentmodal.visio')}</span>

                {/* Join opens the doctor's HOST link, so it stays the doctor's —
                    neither the admin nor a staff login joins as the host. */}
                {selectedAppointment && mode === "Online" && !selectedAppointment.meetingCompleted && !meetingDoneLive && isWithinTwoHoursBeforeAppointment() && userRole !== 'admin' && userRole !== 'user' && selectedAppointment.doctorMeetingLink && (
                  <button
                    data-testid="appointment-connect-meet-button"
                    type="button"
                    className="ml-4 px-4 py-2 font-semibold rounded-lg shadow-sm text-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 flex items-center gap-2 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white hover:from-[#3aa8a8] hover:to-[#2a7b7b] cursor-pointer"
                    onClick={() => {
                      window.open(selectedAppointment.doctorMeetingLink, '_blank');
                    }}
                  >
                    {t('appointmentmodal.connect_meet', 'Connect Meet')}
                  </button>
                )}

                {/* Create-room: offered to whoever the backend lets mint it (P39),
                    and withdrawn once it has said 409 "this visit can never have a room". */}
                {selectedAppointment && mode === "Online" && !selectedAppointment.meetingCompleted && !meetingDoneLive && canCreateMeetingRoom && !meetingRoomRefused && !selectedAppointment.doctorMeetingLink && (
                  <button
                    data-testid="appointment-retry-meeting-link-button"
                    type="button"
                    disabled={meetingLinkRetrying}
                    className="ml-4 px-4 py-2 font-semibold rounded-lg shadow-sm text-sm transition-all focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-opacity-50 flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={retryMeetingLinks}
                    title={t('appointmentmodal.meeting_link_missing_hint', 'This video appointment has no room yet — the video service was unreachable when it was booked.')}
                  >
                    {meetingLinkRetrying
                      ? t('appointmentmodal.meeting_link_creating', 'Creating video room…')
                      : t('appointmentmodal.meeting_link_missing', 'Video room not ready — create it')}
                  </button>
                )}
              </div>
            </div>
            <div data-testid="duration-field-container">
              <label htmlFor="duration" data-testid="duration-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('appointmentmodal.duration_label')}</label>
              {isCustomSelection && (userRole === 'doctor' || userRole === 'admin') ? (
                <div data-testid="appointment-duration-custom-container" className="flex items-center gap-2 w-full min-w-0">
                  <button
                    data-testid="appointment-duration-decrease-button"
                    type="button"
                    onClick={handleDurationDecrease}
                    disabled={appointmentLoading || (typeof duration === 'number' ? duration <= 15 : parseDuration(duration) <= 15)}
                    className="shrink-0 p-2 bg-[#4ABDBD] text-white rounded-lg hover:bg-[#3AA8A8] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <ChevronDown size={20} />
                  </button>
                  <input
                    id="duration"
                    data-testid="duration-input"
                    type="text"
                    size={1}
                    value={duration ? formatDuration(typeof duration === 'number' ? duration : parseDuration(duration)) : ''}
                    readOnly
                    className="flex-1 min-w-0 p-3 rounded-xl border border-gray-300 bg-white text-sm text-center font-medium"
                  />
                  <button
                    data-testid="appointment-duration-increase-button"
                    type="button"
                    onClick={handleDurationIncrease}
                    disabled={appointmentLoading || (typeof duration === 'number' ? duration >= 360 : parseDuration(duration) >= 360)}
                    className="shrink-0 p-2 bg-[#4ABDBD] text-white rounded-lg hover:bg-[#3AA8A8] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <ChevronUp size={20} />
                  </button>
                </div>
              ) : (
                <input
                  id="duration"
                  data-testid="duration-input-readonly"
                  type="text"
                  value={duration ? formatDuration(typeof duration === 'number' ? duration : parseDuration(duration)) : ''}
                  readOnly
                  className="w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
                />
              )}
            </div>
            <div data-testid="price-field-container">
              <label htmlFor="price" data-testid="price-label" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('appointmentmodal.price_label')} {currencySymbol(selectedDoctor?.country) ? `(${currencySymbol(selectedDoctor?.country)})` : ''}
                {(userRole === 'doctor' || userRole === 'admin') && appointmentType && (
                  <span className="ml-2 text-xs font-normal text-green-600">
                    {currentPriceType === 'Type 1' && t('appointmentmodal.price_type_appointment')}
                    {currentPriceType === 'Type 2' && t('appointmentmodal.price_type_custom')}
                    {currentPriceType === 'Type 3' && t('appointmentmodal.price_type_manual')}
                  </span>
                )}
              </label>
              {isCustomSelection && (userRole === 'doctor' || userRole === 'admin') ? (
                <div>
                  <input
                    id="price"
                    data-testid="price-input"
                    type="number"
                    value={price}
                    onChange={handlePriceChange}
                    onKeyDown={(e) => { if (e.key === '.' || e.key === ',') e.preventDefault(); }}
                    className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 hover:border-[#4ABDBD] bg-white text-sm transition-all duration-300 ${priceError
                      ? 'border-red-300 bg-red-50 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-[#4ABDBD]'
                      }`}
                    disabled={appointmentLoading}
                    min="0"
                    max="999999"
                    step="1"
                  />
                  {priceError && (
                    <p data-testid="appointment-price-error" className="text-xs text-red-600 mt-1">{priceError}</p>
                  )}
                </div>
              ) : (
                <input
                  id="price"
                  data-testid="price-input-readonly"
                  type="number"
                  value={price}
                  readOnly
                  className="w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
                />
              )}
            </div>
            <div data-testid="location-field-container">
              <label htmlFor="location" data-testid="location-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('appointmentmodal.location_label')}</label>
              {/* Locations come from the API. The placeholder MUST use the t(key, fallback)
                  form: appointmentmodal.select_location was missing from every locale file,
                  so i18next returned the key and the dropdown showed the raw
                  "appointmentmodal.select_location" to users. */}
              <SearchableSelect
                testId="location-select"
                variant="form"
                value={location}
                onChange={(v) => setLocation(v)}
                options={(locations || []).map((loc) => ({ value: loc, label: loc }))}
                placeholder={t('appointmentmodal.select_location', 'Select a location')}
                searchPlaceholder={t('common.search', 'Search…')}
              />
            </div>
            <div data-testid="start-time-field-container">
              <label htmlFor="startTime" data-testid="start-time-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('appointmentmodal.start_time_label')}</label>
              <input
                id="startTime"
                data-testid="start-time-input"
                type="text"
                value={formatTime(selectedSlot?.start)}
                readOnly
                className="w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
              />
            </div>
            <div data-testid="end-time-field-container">
              <label htmlFor="endTime" data-testid="end-time-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('appointmentmodal.end_time_label')}</label>
              <input
                id="endTime"
                data-testid="end-time-input"
                type="text"
                value={duration ? formatTime(selectedSlot?.end) : ''}
                readOnly
                className="w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-sm"
              />
            </div>
            {/* THE PROGRAMME QUESTION — a new booking for a patient who already
                exists, once the doctor is known; nothing when the doctor runs no
                programme for them (doc/programme-visits.md §2). */}
            {!isEditMode && !isRescheduling && (
              <ProgrammeBookingQuestion
                patientId={(patientType === 'myPatient' || patientType === 'others') ? (patient || null) : (patientType === 'new' && isPatientFound ? foundPatientId : null)}
                doctorId={selectedDoctor?.doctorId || null}
                date={formatDateForPayload(selectedSlot?.start)}
                value={programmeChoice}
                onChange={(next) => { setProgrammeChoice(next); setProgrammeRefusal(''); }}
                refusal={programmeRefusal}
                showErrors={programmeShowErrors}
                className="sm:col-span-2 lg:col-span-3"
              />
            )}
            <div data-testid="communication-field-container" className="sm:col-span-2 lg:col-span-3">
              <label htmlFor="communication" data-testid="communication-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('appointmentmodal.communication_label')}</label>
              <textarea
                id="communication"
                data-testid="communication-textarea"
                value={communication}
                onChange={handleCommunicationChange}
                maxLength={500}
                className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 hover:border-[#4ABDBD] text-sm transition-all duration-300 ${communicationError
                  ? 'border-red-300 bg-red-50 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-[#4ABDBD]'
                  } ${appointmentLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                rows="4"
                disabled={appointmentLoading}
                placeholder={t('appointmentmodal.communication_placeholder', 'Reason / message for the doctor (optional)')}
              />
              {communicationError && (
                <p data-testid="appointment-communication-error" className="text-xs text-red-600 mt-1">{communicationError}</p>
              )}
            </div>

            {/* Payment step — placed last (after Communication), full width. In-modal, no stacked popup. */}
            {!isEditMode && !isRescheduling && (
              <div className="sm:col-span-2 lg:col-span-3">
                <PaymentMethodSelect
                  doctorId={selectedDoctor?.doctorId}
                  value={payment}
                  onChange={setPayment}
                  summary={[
                    { label: t('appointmentmodal.price_label', 'Price'), value: formatPrice(price, selectedDoctor?.country) },
                    { label: t('appointmentmodal.duration_label', 'Duration'), value: duration || '—' },
                    // 30 Sep 2026: the `mode` state is named backwards — 'visio' is
                    // the IN-PERSON value, 'Online' the video one (see the payload's
                    // `mode === 'visio' ? 'onsite' : 'visio'`). The summary read it
                    // literally and showed the opposite of what was being booked.
                    { label: t('appointmentmodal.mode_label', 'Mode'), value: mode === 'Online' ? t('appointmentmodal.visio', 'Visio') : t('appointmentmodal.onsite', 'On-Site') },
                    { label: t('appointmentmodal.start_time_label', 'Start time'), value: formatTime(selectedSlot?.start) || '—' },
                    { label: t('appointmentmodal.location_label', 'Location'), value: location || '—' },
                  ]}
                />
              </div>
            )}

            {/* Payment info — shown when VIEWING a booked/completed appointment. The
                booking step above only renders for new bookings, which is why this
                section was previously missing from "View Appointment". Doctors/admins
                get inline controls to collect payment or change the method. */}
            {isEditMode && isViewMode && selectedAppointment?.payment && (
              <div className="sm:col-span-2 lg:col-span-3">
                <CompletedPaymentCard
                  appointment={selectedAppointment}
                  canEdit={userRole === 'doctor' || userRole === 'admin'}
                />
              </div>
            )}

            {/* Reschedule Recommendation Slots - Show when status is Rescheduling */}
            {isReschedulingStatus && selectedAppointment && (
              <div data-testid="appointment-rescheduling-status-section" className="mt-6 p-4 sm:p-6 bg-white border border-gray-200 rounded-xl sm:col-span-2 lg:col-span-3">
                <div className="space-y-4">
                  {/* Heading */}
                  <div className='flex justify-between'>
                    <div>
                      <h3 data-testid="appointment-rescheduling-status-title" className="text-lg font-bold text-gray-800 mb-2">
                        {t('appointmentmodal.select_recommended_slots_title') || 'Select One of the Recommended Slots'}
                      </h3>
                      <p data-testid="appointment-rescheduling-status-reason" className="text-sm text-gray-600">
                        {t('appointmentmodal.reschedule_recommendation_reason') || 'Due to the doctor\'s unavailability, your appointment has been sent for reschedule recommendation.'}
                      </p>
                    </div>
                    {canCancelVisit && (
                    <button
                      data-testid="appointment-rescheduling-status-cancel-button"
                      type="button"
                      onClick={handleCancelAppointment}
                      className="py-2 px-4 h-fit bg-red-500 hover:bg-red-600 text-white rounded-full text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 cursor-pointer ml-auto"
                      disabled={appointmentLoading || patientLoading || isBookNewPatientLoading}
                    >
                      {appointmentLoading && (
                        <span data-testid="appointment-rescheduling-status-cancel-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                      )}
                      {appointmentLoading
                        ? t('appointmentmodal.loading_button')
                        : t('appointmentmodal.cancel_appointment_button') || 'Cancel Appointment'}
                    </button>
                    )}
                  </div>

                  {/* Recommended Slots Buttons — taking one moves the visit (appointments.reschedule) */}
                  {canRescheduleVisit && (
                  <div data-testid="appointment-rescheduling-recommended-slots" className="flex flex-wrap items-center gap-3">
                    {selectedAppointment.date_1 && (
                      <button
                        data-testid="appointment-rescheduling-recommended-slot-1"
                        type="button"
                        onClick={() => handleRescheduleSlotClick(selectedAppointment.date_1)}
                        className="px-6 py-3 bg-white border-2 border-[#4ABDBD] rounded-full text-sm font-medium text-gray-800 hover:bg-[#4ABDBD] hover:text-white transition-all duration-300 cursor-pointer"
                      >
                        {format(new Date(selectedAppointment.date_1), 'MMM d, yyyy')} {formatClockTime(selectedAppointment.date_1)}
                      </button>
                    )}
                    {selectedAppointment.date_2 && (
                      <button
                        data-testid="appointment-rescheduling-recommended-slot-2"
                        type="button"
                        onClick={() => handleRescheduleSlotClick(selectedAppointment.date_2)}
                        className="px-6 py-3 bg-white border-2 border-[#4ABDBD] rounded-full text-sm font-medium text-gray-800 hover:bg-[#4ABDBD] hover:text-white transition-all duration-300 cursor-pointer"
                      >
                        {format(new Date(selectedAppointment.date_2), 'MMM d, yyyy')} {formatClockTime(selectedAppointment.date_2)}
                      </button>
                    )}
                    {selectedAppointment.date_3 && (
                      <button
                        data-testid="appointment-rescheduling-recommended-slot-3"
                        type="button"
                        onClick={() => handleRescheduleSlotClick(selectedAppointment.date_3)}
                        className="px-6 py-3 bg-white border-2 border-[#4ABDBD] rounded-full text-sm font-medium text-gray-800 hover:bg-[#4ABDBD] hover:text-white transition-all duration-300 cursor-pointer"
                      >
                        {format(new Date(selectedAppointment.date_3), 'MMM d, yyyy')} {formatClockTime(selectedAppointment.date_3)}
                      </button>
                    )}
                    <button
                      data-testid="appointment-rescheduling-view-more-slots-button"
                      type="button"
                      onClick={handleViewMoreSlots}
                      className="px-6 py-3 bg-white border-2 border-gray-400 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-100 transition-all duration-300 cursor-pointer"
                    >
                      {t('appointmentmodal.view_more_slots_button', 'View More Slots')}
                    </button>
                  </div>
                  )}
                </div>
              </div>
            )}
          </form>

          {/* Reschedule Recommendation Section */}
          {(isRescheduleRecommendationMode || isManualRescheduleMode) && (
            <div data-testid="appointment-reschedule-recommendation-section" className="mt-6 p-4 sm:p-6 bg-gradient-to-br from-yellow-50/50 to-orange-50/30 border border-yellow-200 rounded-xl">
              <h4 data-testid="appointment-reschedule-recommendation-heading" className="text-lg font-semibold text-gray-800 mb-2">
                {isManualRescheduleMode
                  ? t('appointmentmodal.manual_reschedule_heading', 'Select a Slot to Reschedule')
                  : isViewingMoreSlots
                    ? (t('appointmentmodal.view_more_slots_title') || 'Select an Available Slot')
                    : t('appointmentmodal.reschedule_recommendation_heading')}
              </h4>
              <p data-testid="appointment-reschedule-recommendation-description" className="text-sm text-gray-600 mb-4">
                {isManualRescheduleMode
                  ? t('appointmentmodal.manual_reschedule_description', 'Click on any available slot to reschedule your appointment')
                  : isViewingMoreSlots
                    ? (t('appointmentmodal.view_more_slots_description') || 'Click on any available slot to reschedule your appointment')
                    : t('appointmentmodal.reschedule_recommendation_description')}
              </p>

              {appointmentLoading ? (
                <div data-testid="appointment-reschedule-recommendation-loading" className="flex justify-center items-center py-8">
                  <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                </div>
              ) : availableDoctorSlots && Object.keys(availableDoctorSlots).length > 0 ? (
                <div data-testid="appointment-reschedule-recommendation-slots" className="space-y-6">
                  {/* Week Navigation */}
                  {(() => {
                    const currentWeekMonday = getCurrentWeekMonday(today);
                    const startDate = addDays(currentWeekMonday, weekOffset * 7);
                    const endDate = addDays(startDate, 6);
                    return (
                      <div data-testid="appointment-reschedule-week-nav" className="flex items-center justify-center gap-4 sm:gap-6 mb-4">
                        <button
                          data-testid="appointment-reschedule-prev-week-button"
                          onClick={handlePrevWeek}
                          disabled={weekOffset === 0}
                          className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                          aria-label={t('appointmentmodal.previous_week', 'Previous week')}
                        >
                          <ArrowLeft size={20} />
                        </button>
                        <h3 data-testid="appointment-reschedule-week-range" className="text-lg font-semibold text-gray-800">
                          {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
                        </h3>
                        <button
                          data-testid="appointment-reschedule-next-week-button"
                          onClick={handleNextWeek}
                          disabled={weekOffset === 2}
                          className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                          aria-label={t('appointmentmodal.next_week', 'Next week')}
                        >
                          <ArrowRight size={20} />
                        </button>
                      </div>
                    );
                  })()}

                  {/* Selected Slots Display - Below Week Navigation */}
                  {!isViewingMoreSlots && !isManualRescheduleMode && selectedRecommendationSlots.length > 0 && (
                    <div data-testid="appointment-selected-slots-container" className="mb-4 p-4 bg-white border border-[#4ABDBD] rounded-xl">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        {/* Selected Slots */}
                        <div data-testid="appointment-selected-slots-list" className="flex flex-wrap items-center gap-2 flex-1">
                          {selectedRecommendationSlots.map((slot, idx) => (
                            <div
                              data-testid={`appointment-selected-slot-${idx}`}
                              key={idx}
                              className="flex items-center gap-2 px-3 py-2 bg-[#4ABDBD] text-white rounded-lg shadow-md"
                            >
                              <span data-testid={`appointment-selected-slot-text-${idx}`} className="text-sm font-medium">
                                {format(new Date(slot.start), 'MMM d, yyyy')} {formatClockTime(slot.start)}
                              </span>
                              <button
                                data-testid={`appointment-selected-slot-remove-${idx}`}
                                type="button"
                                onClick={() => setSelectedRecommendationSlots(selectedRecommendationSlots.filter((_, i) => i !== idx))}
                                className="hover:bg-white/20 rounded-full p-1 transition-all duration-200"
                                aria-label={t('appointmentmodal.remove_slot', 'Remove slot')}
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Action Buttons - Fixed on Right */}
                        <div className="flex items-center gap-2">
                          <button
                            data-testid="appointment-selected-slots-cancel-button"
                            type="button"
                            onClick={() => {
                              setIsRescheduleRecommendationMode(false);
                              setSelectedRecommendationSlots([]);
                              setWeekOffset(0);
                              setOpenDay(null);
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-300 font-semibold text-sm"
                          >
                            {t('appointmentmodal.cancel_button')}
                          </button>
                          <button
                            data-testid="appointment-selected-slots-send-button"
                            type="button"
                            onClick={handleSendRescheduleRecommendation}
                            disabled={selectedRecommendationSlots.length !== 3 || appointmentLoading || patientLoading || isBookNewPatientLoading}
                            className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {(appointmentLoading || patientLoading || isBookNewPatientLoading) && (
                              <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                            )}
                            {appointmentLoading || patientLoading || isBookNewPatientLoading
                              ? t('appointmentmodal.loading_button')
                              : t('appointmentmodal.send_button')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Available Days */}
                  {(() => {
                    const availableDays = Object.entries(availableDoctorSlots || {})
                      .map(([day, data]) => ({ day, ...data }))
                      .sort((a, b) => new Date(a.date) - new Date(b.date));

                    return (
                      <div className="grid grid-cols-1 gap-4">
                        {availableDays.map(({ day, date, slots }) => {
                          const hasAvailableSlots = slots.filter(slot => slot.status === 'available').length > 0;
                          if (!hasAvailableSlots) return null;

                          return (
                            <div
                              data-testid={`appointment-reschedule-day-card-${day}`}
                              key={day}
                              className="group overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm hover:shadow-[0_0px_8px_0_#4ABDBD] transition-all duration-300"
                            >
                              <div
                                data-testid={`appointment-reschedule-day-toggle-${day}`}
                                className={`p-4 bg-gray-50 flex items-center gap-3 cursor-pointer hover:bg-gray-100 transition-all duration-200`}
                                onClick={() => toggleDayDrawer(day)}
                              >
                                <CalendarCheck size={20} className="text-[#4ABDBD]" />
                                <h4 data-testid={`appointment-reschedule-day-label-${day}`} className="font-semibold text-gray-500 group-hover:text-[#4ABDBD] transition-colors truncate">
                                  {format(new Date(date), 'EEEE')}, {format(new Date(date), 'MMM d, yyyy')}
                                </h4>
                              </div>
                              {openDay === day && (
                                <div data-testid={`appointment-reschedule-day-slots-${day}`} className="p-4 bg-white border-t border-gray-200">
                                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                    {slots
                                      .filter(slot => slot.status === 'available')
                                      .map((slot, idx) => {
                                        // In manual reschedule or view more slots mode: select 1 slot for direct reschedule
                                        // In normal recommendation mode: select up to 3 slots for recommendation
                                        const isSelected = (isViewingMoreSlots || isManualRescheduleMode)
                                          ? false // Don't show selection state in direct mode
                                          : selectedRecommendationSlots.some(s => s.start === slot.start);
                                        const isOverlapping = (isViewingMoreSlots || isManualRescheduleMode) ? false : isSlotOverlapping(slot);
                                        const isDisabled = (isViewingMoreSlots || isManualRescheduleMode) ? false : (isOverlapping && !isSelected);

                                        return (
                                          <button
                                            data-testid={`appointment-reschedule-slot-${day}-${idx}`}
                                            key={idx}
                                            type="button"
                                            onClick={() => {
                                              if (isManualRescheduleMode) {
                                                handleManualRescheduleConfirm(slot.start);
                                              } else if (isViewingMoreSlots) {
                                                handleRescheduleSlotClick(slot.start);
                                              } else {
                                                !isDisabled && handleSlotSelection(slot);
                                              }
                                            }}
                                            disabled={isDisabled}
                                            className={`p-2 rounded-xl text-center text-sm transition-all duration-200 ${(isViewingMoreSlots || isManualRescheduleMode)
                                              ? 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-[#4ABDBD] hover:text-white cursor-pointer'
                                              : isSelected
                                                ? 'bg-[#4ABDBD] text-white shadow-md cursor-pointer'
                                                : isDisabled
                                                  ? 'bg-gray-200 border border-gray-300 text-gray-400 cursor-not-allowed opacity-50'
                                                  : 'bg-gray-50 border border-gray-200 text-gray-700 hover:bg-[#4ABDBD] hover:text-white cursor-pointer'
                                              }`}
                                            title={isDisabled ? t('appointmentmodal.slot_overlap') : ''}
                                          >
                                            {formatClockTime(slot.start)}
                                          </button>
                                        );
                                      })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {(isViewingMoreSlots || isManualRescheduleMode) && (
                    <div data-testid="appointment-reschedule-mode-actions" className="flex justify-end gap-3 mt-4">
                      <button
                        data-testid="appointment-reschedule-mode-close-button"
                        type="button"
                        onClick={() => {
                          setIsViewingMoreSlots(false);
                          setIsRescheduleRecommendationMode(false);
                          setIsManualRescheduleMode(false);
                          setManualReschedulePayload(null);
                          setSelectedRecommendationSlots([]);
                          setWeekOffset(0);
                          setOpenDay(null);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-all duration-300 font-semibold text-sm cursor-pointer"
                      >
                        {t('appointmentmodal.close_button') || 'Close'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div data-testid="appointment-reschedule-no-slots" className="text-center py-8 text-gray-500">
                  {t('appointmentmodal.no_slots_available')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Section - Sticky */}
        {
          isCompletedAppointment ? (
            <div data-testid="form-actions-container-completed" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
              <button
                data-testid="close-button"
                id="btn-close"
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
              >
                {t('appointmentmodal.close_button', 'Close')}
              </button>
            </div>
          ) : (
            <div data-testid="form-actions-container" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
              {/* <button
                data-testid="cancel-button"
                id="btn-cancel"
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                disabled={appointmentLoading || patientLoading}
              >
                {t('appointmentmodal.cancel_button', 'Cancel')}
              </button> */}
              {isEditMode && isViewMode && (
                <>
                  {isPastAppointment && canWriteVisit && (
                    <button
                      data-testid="missed-out-button"
                      id="btn-missed-out"
                      type="button"
                      onClick={handleMissedOut}
                      className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 flex items-center gap-2 cursor-pointer"
                      disabled={appointmentLoading || isOpenAppointmentLoading || isBookNewPatientLoading}
                    >
                      {appointmentLoading && (
                        <span data-testid="loading-spinner-missed-out" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                      )}
                      {appointmentLoading
                        ? t('appointmentmodal.loading_button')
                        : t('appointmentmodal.missed_out_button')}
                    </button>
                  )}
                  {(!isModalOpenAppointment && userRole === 'doctor' && isWithinTwoHoursBeforeAppointment() && !isReschedulingStatus && !isRescheduleRecommendationMode && !isManualRescheduleMode) && (
                    <button
                      data-testid="open-appointment-button"
                      id="btn-open-appointment"
                      type="button"
                      onClick={handleOpenAppointments}
                      className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 flex items-center gap-2 cursor-pointer"
                      disabled={isOpenAppointmentLoading || appointmentLoading || isBookNewPatientLoading}
                    >
                      {isOpenAppointmentLoading && (
                        <span data-testid="loading-spinner-open-appointment" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                      )}
                      {isOpenAppointmentLoading
                        ? t('appointmentmodal.loading_button')
                        : t('appointmentmodal.open_appointment_button')}
                    </button>
                  )}
                  {canRescheduleVisit && !isReschedulingStatus && !isRescheduleRecommendationMode && !isManualRescheduleMode && (
                    <div className="relative" ref={dropdownRescheduleRef}>
                      <button
                        data-testid="reschedule-button"
                        id="btn-reschedule"
                        type="button"
                        onClick={() => setIsRescheduleDropdownOpen(!isRescheduleDropdownOpen)}
                        className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 flex items-center gap-2 cursor-pointer"
                        disabled={appointmentLoading || isOpenAppointmentLoading || isBookNewPatientLoading}
                      >
                        {appointmentLoading && (
                          <span data-testid="loading-spinner-reschedule" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                        )}
                        {appointmentLoading
                          ? t('appointmentmodal.loading_button')
                          : t('appointmentmodal.reschedule', 'Reschedule')}
                      </button>
                      {isRescheduleDropdownOpen && (
                        <div data-testid="appointment-reschedule-dropdown-menu" className="absolute bottom-full mb-2 right-0 w-48 bg-white rounded-lg shadow-[0_0_15px_rgba(0,0,0,0.1)] border border-gray-100 z-50 overflow-hidden py-1 divide-y divide-gray-50">
                          <button
                            data-testid="appointment-manual-reschedule-button"
                            type="button"
                            onClick={handleManualReschedule}
                            className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-black/10 transition-colors"
                          >
                            {t('appointmentmodal.manual_reschedule', 'Manual Reschedule')}
                          </button>
                          <button
                            data-testid="appointment-reschedule-recommend-button"
                            type="button"
                            onClick={() => {
                              setIsRescheduleDropdownOpen(false);
                              handleReschedule();
                            }}
                            className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-black/10 transition-colors"
                          >
                            {t('appointmentmodal.reschedule_recommendation_button', 'Reschedule Recommend')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {canCancelVisit && !isPastAppointment && !isReschedulingStatus && !isRescheduleRecommendationMode && (
                    <button
                      data-testid="cancel-appointment-button"
                      id="btn-cancel-appointment"
                      type="button"
                      onClick={handleCancelAppointment}
                      className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 flex items-center gap-2 cursor-pointer"
                      disabled={appointmentLoading || isOpenAppointmentLoading || isBookNewPatientLoading}
                    >
                      {appointmentLoading && (
                        <span data-testid="loading-spinner-cancel-appointment" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                      )}
                      {appointmentLoading
                        ? t('appointmentmodal.loading_button')
                        : t('appointmentmodal.cancel_appointment_button')}
                    </button>
                  )}
                  {canWriteVisit && !isPastAppointment && !isReschedulingStatus && !isRescheduleRecommendationMode && !isAppointmentInRestrictedSlot && (
                    <button
                      data-testid="edit-button"
                      id="btn-edit"
                      type="button"
                      onClick={() => setIsViewMode(false)}
                      className={`px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 ${appointmentLoading || isOpenAppointmentLoading || isBookNewPatientLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                      disabled={appointmentLoading || isOpenAppointmentLoading || isBookNewPatientLoading}
                    >
                      {appointmentLoading && (
                        <span data-testid="loading-spinner-edit" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                      )}
                      {appointmentLoading
                        ? t('appointmentmodal.loading_button')
                        : t('appointmentmodal.edit_button', 'Edit')}
                    </button>
                  )}
                </>
              )}
              {isEditMode && !isViewMode && (
                <>
                  <button
                    data-testid="cancel-edit-button"
                    id="btn-cancel-edit"
                    type="button"
                    onClick={() => setIsViewMode(true)}
                    className={`px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer ${appointmentLoading || patientLoading || isBookNewPatientLoading ? 'opacity-50 pointer-events-none' : ''}`}
                    disabled={appointmentLoading || patientLoading || isBookNewPatientLoading}
                  >
                    {t('appointmentmodal.cancel_edit_button', 'Cancel')}
                  </button>
                  <button
                    data-testid="save-button"
                    id="btn-save"
                    type="button"
                    onClick={handleSaveAppointment}
                    className={`px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 ${appointmentLoading || patientLoading || isBookNewPatientLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    disabled={appointmentLoading || patientLoading || isBookNewPatientLoading}
                  >
                    {(appointmentLoading || patientLoading || isBookNewPatientLoading) && (
                      <span data-testid="loading-spinner-save" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                    )}
                    {appointmentLoading || patientLoading || isBookNewPatientLoading
                      ? t('appointmentmodal.loading_button')
                      : t('appointmentmodal.save_button')}
                  </button>
                </>
              )}
              {!isEditMode && (isRescheduling ? canRescheduleVisit : canCreateVisit) && (
                <button
                  data-testid="create-button"
                  id="btn-submit"
                  type="button"
                  onClick={handleSaveAppointment}
                  className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2"
                  disabled={appointmentLoading || patientLoading || isBookNewPatientLoading}
                >
                  {(appointmentLoading || patientLoading || isBookNewPatientLoading) && (
                    <span data-testid="loading-spinner-create" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                  )}
                  {appointmentLoading || patientLoading || isBookNewPatientLoading
                    ? t('appointmentmodal.loading_button')
                    : isRescheduling
                      ? t('appointmentmodal.confirm_reschedule_button')
                      : t('appointmentmodal.create_button')}
                </button>
              )}
            </div>
          )
        }
      </div >
      {/* MOC linking popup — only for a Mitra One Care account / a pending link
          request. A record that already exists here never opens it. */}
      <MocLinkModal
        open={Boolean(mocPopup)}
        lookup={mocPopup?.lookup || null}
        // OPEN PLATFORM: the popup asks "may THIS DOCTOR take you on?" instead
        // of "link this record to the account" — the record and the account
        // link already exist there. The doctor is the one the appointment is
        // FOR, which is not the person at the keyboard when an admin books.
        consentKind={classifyMocLookup(mocPopup?.lookup) === 'doctorConsent' ? 'doctor' : 'link'}
        consentDoctorId={selectedDoctor?.doctorId || null}
        consentDoctorName={[selectedDoctor?.name, selectedDoctor?.surname].filter(Boolean).join(' ')}
        draftPatient={buildDraftPatient()}
        onUsePatient={handleUseExistingById}
        usePatientLabel={t('appointmentmodal.use_this_patient', 'Use this patient')}
        onLinked={handleMocLinked}
        onApprovalSent={handleMocApprovalSent}
        onClose={handleMocPopupClose}
      />
    </div >
  );
};

export default AppointmentModal;